import { TS, WW, WH, PW, PH, GRAV, JFORCE, MSPD, T, TC, SOLID, LIQUID, NONSOLID_PASS, WEAPON_TIERS, ARMOR_TIERS, PICKAXE_TIERS, LOOT_TABLE, ACHIEVEMENTS, BIOME_ATM, MOB_TYPES, GOD_MODE, EVENTS, EVENT_REWARDS, TRADER_ITEMS, WORLD_BOSSES, VILLAGE_QUESTS, VILLAGER_PROFESSIONS, ACCESSORIES, SPECIAL_WEAPONS, BESTIARY_CATEGORIES } from './constants';
import { WorldGen, SRng } from './WorldGen';
import { LiquidSim, GoreSystem, DmgNumbers, XpOrb, ItemDrop } from './Systems';
import { SoundEngine, sfx } from './Sound';
import { TileTextures, ParallaxBG, AmbientSystem } from './Rendering';
import { MobRenderer } from './MobRenderer';
import { Mob, EyeOfCthulhu, MoonLord } from './Entities';
import { BiomeDecorator } from './BiomeDetails';

// === MAIN GAME ===
export class Game {
    constructor() {
        this.stopped = false;
        this.cv = document.getElementById('game'); this.cx = this.cv.getContext('2d');
        this.mc = document.getElementById('minimap'); this.mx = this.mc.getContext('2d');
        this.cv.width = this.cv.parentElement.clientWidth; this.cv.height = this.cv.parentElement.clientHeight;
        this.lightCv = document.createElement('canvas'); this.lightCx = this.lightCv.getContext('2d');
        this.keys = {}; this.mouse = { x: 0, y: 0, l: false, r: false };
        this.cam = { x: 0, y: 0, shakeX: 0, shakeY: 0, shakeT: 0, shakeStr: 0 }; this.zoom = 2; this.targetZoom = 2;
        this.w = new WorldGen(); this.w.generate();
        window._worldRef = this.w;
        this.tex = new TileTextures(this.w.seed);
        this.liq = new LiquidSim(this.w);
        this.ambient = new AmbientSystem();
        this.parallax = new ParallaxBG(this.w.seed);
        this.biomeDecorator = new BiomeDecorator(this.w.seed);
        this.dmgNums = new DmgNumbers();
        this.gore = new GoreSystem();
        this._cachedDecorations = [];
        this._lastDecoCamX = -9999;
        this._ambientSoundTimer = 0;
        this.hitstop = 0; this.maxParts = 350;
        // Juice state
        this.screenFlash = 0; this.screenFlashColor = '#fff';
        this.slowMo = 0; this.slowMoFactor = 1;
        this.p = {
            x: this.w.spawnX * TS, y: this.w.spawnY * TS, vx: 0, vy: 0, ground: false, right: true,
            hp: 100, maxHp: 100, slot: 0, onLadder: false, af: 0, at: 0, mt: 0, mtgt: null,
            inv: Array.from({ length: 32 }, (_, i) => {
                const defaults = [{ t: T.DIRT, c: 99 }, { t: T.STONE, c: 99 }, { t: T.WOOD, c: 50 }, { t: T.TORCH, c: 50 },
                { t: T.LADDER, c: 50 }, { t: T.DBRICK, c: 30 }, { t: T.LANTERN, c: 20 }, { t: T.SAND, c: 50 }];
                return i < defaults.length ? defaults[i] : null;
            }),
            iframes: 0, dmgFlash: 0, squashX: 1, squashY: 1, swingAngle: 0, swingDir: 0, swingTimer: 0,
            coyoteTime: 0, jumpBuffer: 0, stepTimer: 0, wasGround: false, prevHp: 100, blinkTimer: 0, breathTimer: 0,
            inLiquid: 0, liquidType: 0, wasInLiquid: false, swimTimer: 0, bubbleTimer: 0, liquidDepth: 0
        };
        this._invOpen = false; // inventory panel toggle
        this.mobs = []; this.parts = []; this.droppedItems = [];
        ItemDrop._TC = TC; ItemDrop._SOLID = SOLID; // give ItemDrop access to tile data
        // Death/respawn state
        this._dead = false;
        this._deathTimer = 0;
        this._regenTimer = 0;
        this._noDmgTimer = 0;
        this.boss = null;
        this.dayTime = 0.25; this.daySpeed = 0.000005; this.timeEnabled = true;
        this.lastT = performance.now(); this.mobSpawnTimer = 0;
        // === XP / STATS SYSTEM ===
        this.xp = 0;
        this.stats = { blocksMined: 0, mobsKilled: 0, bossesKilled: 0, maxDepth: 0, distWalked: 0 };
        this.bestXp = 0;
        try { this.bestXp = parseInt(localStorage.getItem('terraria_best') || '0', 10); } catch { this.bestXp = 0; }
        this.onScoreUpdate = null; // callback from React component
        this._lastPx = this.p.x; this._lastPy = this.p.y;
        this._xpSaveTimer = 0;
        this._xpSubmitted = false;
        // XP juice visual state
        this.xpOrbs = [];
        this._xpFlash = 0; // HUD flash timer
        this._xpPulse = 1; // HUD scale pulse
        this._xpLastMilestone = 0; // last 100-xp milestone
        // === EQUIPMENT STATE ===
        this.equipWeapon = 0; // index into WEAPON_TIERS
        this.equipArmor = 0;  // index into ARMOR_TIERS
        this.equipPick = 0;   // index into PICKAXE_TIERS
        this._craftNotify = ''; this._craftNotifyTimer = 0;
        // === ACHIEVEMENTS ===
        this._achievements = {}; // id -> true
        this._achQueue = []; // popup queue
        this._achDisplayTimer = 0;
        this._visitedSky = false;
        // === EVENT SYSTEM ===
        this._currentEvent = null;
        this._eventTimer = 0;
        this._eventCooldown = 180000 + Math.random() * 300000; // 3-8 min before first event
        this._eventNotifyTimer = 0;
        this._eventNotify = '';
        this._eventWave = 0; // current wave index
        this._eventWaveTimer = 0; // timer within current wave
        this._eventMobsKilled = 0; // mobs killed during event
        this._eventCompleted = false; // did player survive all waves?
        // === TRADER STATE ===
        this._traderShopOpen = false;
        this._traderTarget = null;
        this._traderCurrency = 0; // earned from killing mobs
        // === QUEST STATE ===
        this._activeQuest = null; // { type, desc, target, progress, reward, villager }
        this._questCooldown = 0;
        this._questNotify = '';
        this._questNotifyTimer = 0;
        this._villagerTarget = null; // villager near player for interaction
        // === BUFFS ===
        this._buffs = {}; // { speed: {timer, value}, shield: {timer}, etc }
        // === ACCESSORIES ===
        this._accessories = []; // array of accessory ids (max 3 equipped)
        this._accessorySlots = 3;
        this._accessoryNotify = '';
        this._accessoryNotifyTimer = 0;
        // === SPECIAL WEAPONS ===
        this._specialWeapon = null; // currently equipped special weapon {id, ...}
        this._specialWeaponNotify = '';
        this._specialWeaponNotifyTimer = 0;
        this._projectiles = []; // active projectiles [{x,y,vx,vy,type,dmg,life,color}]
        this._specialAttackCd = 0; // cooldown for special weapon attack
        // === BESTIARY ===
        this._bestiary = {}; // mobType -> {seen: bool, killed: int}
        this._bestiaryOpen = false;
        this._bestiaryNotify = '';
        this._bestiaryNotifyTimer = 0;
        // === DASH ===
        this._dashTimer = 0;
        this._dashCd = 0;
        this._dashDir = 1;
        // === DOUBLE JUMP ===
        this._hasDoubleJumped = false;
        // === WORLD BOSS ===
        this._worldBossTimer = 0;
        this._worldBossSpawned = {};
        // === TIDAL SYSTEM ===
        this._tidalTimer = 0;
        this._tidalPhase = 0; // 0..1, controls tide level
        this._lastTidalUpdate = 0;
        // === STORM SYSTEM ===
        this._stormTimer = 0;
        this._lightningFlash = 0;
        this._lightningBolts = []; // { x, y, timer }
        // === WEATHER SYSTEM ===
        this._weather = {
            state: 'clear',       // clear, cloudy, rain, storm, clearing
            timer: 30000 + Math.random() * 60000, // time until next transition
            intensity: 0,         // 0..1 current rain/cloud intensity (smoothed)
            targetIntensity: 0,   // target intensity for current state
            cloudDensity: 0,      // 0..1 smoothed cloud cover
            targetCloudDensity: 0,
            windSpeed: 0,         // affects rain angle and cloud speed
            targetWind: 0,
            rainDrops: [],        // screen-space rain drops [{x,y,l}]
            maxRainDrops: 80,
        };
        // === DAY COUNTER & MOON PHASES ===
        this._dayCount = 0;
        this._lastDayTime = this.dayTime;
        // === SHOOTING STARS ===
        this._shootingStar = null; // {x,y,vx,vy,life,maxLife,color}
        this._shootingStarTimer = 20000 + Math.random() * 40000;
        // === WORLD MUTATION SYSTEM ===
        this._mutation = {
            phase: 0, // 0=dormant, 1=cracks, 2=roots, 3=tree_growing, 4=tree_complete, 5=bloomed
            timer: 0, // ms elapsed total
            phaseTimer: 0, // ms in current phase
            cracks: [], // [{x, y, intensity, growTimer}]
            roots: [], // [{x, y, dir, length, maxLen}]
            treeX: Math.floor(WW / 2), // center of world tree
            treeY: 0, // will be set to surface Y
            treeBuilt: false,
            treeHeight: 0,
            maxTreeHeight: 65,
            trunkWidth: 3,
            branchCount: 0,
            glowPulse: 0,
            crackSpawnTimer: 0,
            rootGrowTimer: 0,
            treeGrowTimer: 0,
            treeGrowStep: 0,
            announced: {},
            treeHeartFound: false,
            particleTimer: 0,
        };
        // Spawn initial villagers in villages
        if (this.w.villageLocations) {
            for (let vi2 = 0; vi2 < this.w.villageLocations.length; vi2++) {
                const vcx = this.w.villageLocations[vi2];
                const numV = 4 + Math.floor(Math.random() * 4); // 4-7 villagers
                let profIdx = 0;
                for (let vi = 0; vi < numV; vi++) {
                    const vx = vcx + Math.floor((Math.random() - 0.5) * 35);
                    if (vx < 5 || vx >= WW - 5) continue;
                    let vy = Math.floor(this.w.heightMap[vx]);
                    for (let dy = -5; dy < 10; dy++) {
                        if (!SOLID.has(this.w.gt(vx, vy + dy)) && SOLID.has(this.w.gt(vx, vy + dy + 1))) { vy = vy + dy; break; }
                    }
                    const mob = new Mob('VILLAGER', vx * TS, vy * TS - 22);
                    // Assign professions in order, then random
                    mob.ai.profession = profIdx < 7 ? profIdx : Math.floor(Math.random() * 7);
                    profIdx++;
                    this.mobs.push(mob);
                }
                // Spawn 1-2 guards
                for (let gi = 0; gi < 1 + Math.floor(Math.random() * 2); gi++) {
                    const gx = vcx + Math.floor((Math.random() - 0.5) * 20);
                    if (gx < 5 || gx >= WW - 5) continue;
                    let gy = Math.floor(this.w.heightMap[gx]);
                    for (let dy = -5; dy < 10; dy++) {
                        if (!SOLID.has(this.w.gt(gx, gy + dy)) && SOLID.has(this.w.gt(gx, gy + dy + 1))) { gy = gy + dy; break; }
                    }
                    this.mobs.push(new Mob('GUARD', gx * TS, gy * TS - 24));
                }
                // Spawn one trader per village
                let ty = Math.floor(this.w.heightMap[Math.min(vcx, WW - 1)]);
                for (let dy = -5; dy < 10; dy++) {
                    if (!SOLID.has(this.w.gt(vcx, ty + dy)) && SOLID.has(this.w.gt(vcx, ty + dy + 1))) { ty = ty + dy; break; }
                }
                this.mobs.push(new Mob('TRADER', vcx * TS, ty * TS - 22));
            }
        }
        this.curAtm = { fog: 'rgba(200,220,200,', fs: 0.08, tint: null, dm: 1, fogR: 200, fogG: 220, fogB: 200, tintR: 0, tintG: 0, tintB: 0, tintAlpha: 0 };
        this.smoothBiomeWeights = null;
        this.setupInput(); this.setupHotbar(); this.renderMinimap(); this.loop();
    }
    setupInput() {
        this.handlers = {};
        // Keys that the game uses — prevent their default browser behavior (e.g. space = scroll)
        const GAME_KEYS = new Set([' ', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright']);
        this.handlers.keydown = e => {
            if (this.stopped) return;
            const k = e.key.toLowerCase();
            if (GAME_KEYS.has(k)) e.preventDefault();
            this.keys[k] = true;
            if (e.key >= '1' && e.key <= '8') { this.p.slot = parseInt(e.key) - 1; this.updateHotbar(); }
            if (k === 't') this.timeEnabled = !this.timeEnabled;
            if (k === 'b') { this._bestiaryOpen = !this._bestiaryOpen; this._renderBestiary(); }
            if (k === 'q' && this._specialWeapon && this._specialAttackCd <= 0) { this._fireSpecialWeapon(); }
            if (k === 'shift' && this._hasAccessory('dash') && this._dashCd <= 0) { this._startDash(); }
            if (k === 'e') {
                if (this._traderTarget && !this._traderTarget.dead) {
                    if (this._traderShopOpen) this._closeTraderShop(); else this._openTraderShop();
                } else if (this._villagerTarget && !this._villagerTarget.dead && !this._villagerTarget.def.isTrader) {
                    this._interactVillager(this._villagerTarget);
                } else {
                    this._invOpen = !this._invOpen; this._renderInventory();
                    if (this._traderShopOpen) this._closeTraderShop();
                }
            }
            sfx.resume();
        };
        this.handlers.keyup = e => { if (!this.stopped) this.keys[e.key.toLowerCase()] = false; };
        this.handlers.wheel = e => {
            if (!this.stopped) {
                e.preventDefault();
                this.targetZoom = Math.max(1, Math.min(4, this.targetZoom - e.deltaY * 0.001));
            }
        };
        this.handlers.resize = () => {
            if (!this.stopped && this.cv && this.cv.parentElement) {
                this.cv.width = this.cv.parentElement.clientWidth;
                this.cv.height = this.cv.parentElement.clientHeight;
                // Re-render immediately after resize to avoid flicker
                this.render();
            }
        };

        window.addEventListener('keydown', this.handlers.keydown);
        window.addEventListener('keyup', this.handlers.keyup);
        // Wheel and resize handled differently below
        window.addEventListener('resize', this.handlers.resize);

        // Correct mouse handling relative to canvas
        this.handlers.mousemove = e => {
            if (!this.stopped) {
                const rect = this.cv.getBoundingClientRect();
                this.mouse.x = e.clientX - rect.left;
                this.mouse.y = e.clientY - rect.top;
            }
        };
        this.handlers.mousedown = e => { if (!this.stopped) { sfx.resume(); if (e.button === 0) this.mouse.l = true; if (e.button === 2) this.mouse.r = true; } };
        this.handlers.mouseup = e => { if (!this.stopped) { if (e.button === 0) this.mouse.l = false; if (e.button === 2) this.mouse.r = false; this.p.mt = 0; this.p.mtgt = null; } };
        this.handlers.contextmenu = e => e.preventDefault();

        // Attach wheel to canvas to prevent page scroll
        this.cv.addEventListener('wheel', this.handlers.wheel, { passive: false });
        this.cv.addEventListener('mousemove', this.handlers.mousemove);
        this.cv.addEventListener('mousedown', this.handlers.mousedown);
        this.cv.addEventListener('mouseup', this.handlers.mouseup);
        this.cv.addEventListener('contextmenu', this.handlers.contextmenu);

        // === MOBILE TOUCH CONTROLS ===
        this.setupTouchControls();

        // Initial resize
        this.handlers.resize();
    }

    setupTouchControls() {
        // Detect touch device and add CSS class
        const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
        if (isTouchDevice) {
            const container = document.getElementById('terraria-container');
            if (container) container.classList.add('is-touch-device');
        }

        const joystick = document.getElementById('touch-joystick');
        const knob = document.getElementById('touch-knob');
        const atkJoystick = document.getElementById('touch-attack-joystick');
        const atkKnob = document.getElementById('touch-attack-knob');
        if (!joystick || !knob) return;

        this._touchCleanup = [];
        this._touchAttackDir = { x: 0, y: 0, active: false };

        // --- LEFT JOYSTICK (movement + jump) ---
        let joystickId = null;
        let joyCenter = { x: 0, y: 0 };
        const JOY_RADIUS = 45;
        const DEAD_ZONE = 0.2;
        const JUMP_THRESHOLD = -0.55; // push up past 55% to jump

        const onJoyStart = (e) => {
            if (this.stopped) return;
            e.preventDefault();
            sfx.resume();
            const t = e.changedTouches[0];
            joystickId = t.identifier;
            const rect = joystick.getBoundingClientRect();
            joyCenter = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
            knob.classList.add('active');
            this._updateMoveJoystick(t.clientX, t.clientY, joyCenter, knob, JOY_RADIUS, DEAD_ZONE, JUMP_THRESHOLD);
        };
        const onJoyMove = (e) => {
            if (this.stopped || joystickId === null) return;
            e.preventDefault();
            for (const t of e.changedTouches) {
                if (t.identifier === joystickId) {
                    this._updateMoveJoystick(t.clientX, t.clientY, joyCenter, knob, JOY_RADIUS, DEAD_ZONE, JUMP_THRESHOLD);
                }
            }
        };
        const onJoyEnd = (e) => {
            for (const t of e.changedTouches) {
                if (t.identifier === joystickId) {
                    joystickId = null;
                    knob.classList.remove('active');
                    knob.style.transform = 'translate(-50%, -50%)';
                    this.keys['a'] = false; this.keys['d'] = false;
                    this.keys['w'] = false; this.keys['s'] = false;
                    this.keys[' '] = false;
                }
            }
        };

        joystick.addEventListener('touchstart', onJoyStart, { passive: false });
        window.addEventListener('touchmove', onJoyMove, { passive: false });
        window.addEventListener('touchend', onJoyEnd);
        window.addEventListener('touchcancel', onJoyEnd);
        this._touchCleanup.push(
            () => joystick.removeEventListener('touchstart', onJoyStart),
            () => window.removeEventListener('touchmove', onJoyMove),
            () => window.removeEventListener('touchend', onJoyEnd),
            () => window.removeEventListener('touchcancel', onJoyEnd)
        );

        // --- RIGHT JOYSTICK (attack direction) ---
        if (atkJoystick && atkKnob) {
            let atkJoystickId = null;
            let atkCenter = { x: 0, y: 0 };
            const ATK_RADIUS = 45;
            const ATK_DEAD_ZONE = 0.3;

            const onAtkStart = (e) => {
                if (this.stopped) return;
                e.preventDefault();
                sfx.resume();
                const t = e.changedTouches[0];
                atkJoystickId = t.identifier;
                const rect = atkJoystick.getBoundingClientRect();
                atkCenter = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
                atkKnob.classList.add('active');
                this._updateAttackJoystick(t.clientX, t.clientY, atkCenter, atkKnob, ATK_RADIUS, ATK_DEAD_ZONE);
            };
            const onAtkMove = (e) => {
                if (this.stopped || atkJoystickId === null) return;
                e.preventDefault();
                for (const t of e.changedTouches) {
                    if (t.identifier === atkJoystickId) {
                        this._updateAttackJoystick(t.clientX, t.clientY, atkCenter, atkKnob, ATK_RADIUS, ATK_DEAD_ZONE);
                    }
                }
            };
            const onAtkEnd = (e) => {
                for (const t of e.changedTouches) {
                    if (t.identifier === atkJoystickId) {
                        atkJoystickId = null;
                        atkKnob.classList.remove('active');
                        atkKnob.style.transform = 'translate(-50%, -50%)';
                        this._touchAttackDir.active = false;
                        this.mouse.l = false;
                        this.p.mt = 0;
                        this.p.mtgt = null;
                    }
                }
            };

            atkJoystick.addEventListener('touchstart', onAtkStart, { passive: false });
            window.addEventListener('touchmove', onAtkMove, { passive: false });
            window.addEventListener('touchend', onAtkEnd);
            window.addEventListener('touchcancel', onAtkEnd);
            this._touchCleanup.push(
                () => atkJoystick.removeEventListener('touchstart', onAtkStart),
                () => window.removeEventListener('touchmove', onAtkMove),
                () => window.removeEventListener('touchend', onAtkEnd),
                () => window.removeEventListener('touchcancel', onAtkEnd)
            );
        }

        // --- CANVAS TOUCH (tap = place block, hold = mine block) ---
        // Nothing happens on touch start. After hold threshold, start mining (left-click).
        // If released early (tap), place a block (brief right-click pulse).
        let canvasTouchId = null;
        let canvasTouchTimer = null;
        let canvasTouchMining = false;
        const HOLD_THRESHOLD = 150; // ms before switching to mine mode

        const onCanvasTouchStart = (e) => {
            if (this.stopped) return;
            if (e.target !== this.cv) return;
            e.preventDefault();
            sfx.resume();
            const t = e.changedTouches[0];
            canvasTouchId = t.identifier;
            canvasTouchMining = false;
            const rect = this.cv.getBoundingClientRect();
            this.mouse.x = t.clientX - rect.left;
            this.mouse.y = t.clientY - rect.top;
            // Don't activate anything yet — wait to see if tap or hold
            canvasTouchTimer = setTimeout(() => {
                // Held long enough — start mining (left-click)
                this.mouse.l = true;
                canvasTouchMining = true;
            }, HOLD_THRESHOLD);
        };
        const onCanvasTouchMove = (e) => {
            if (this.stopped || canvasTouchId === null) return;
            for (const t of e.changedTouches) {
                if (t.identifier === canvasTouchId) {
                    e.preventDefault();
                    const rect = this.cv.getBoundingClientRect();
                    this.mouse.x = t.clientX - rect.left;
                    this.mouse.y = t.clientY - rect.top;
                }
            }
        };
        const onCanvasTouchEnd = (e) => {
            for (const t of e.changedTouches) {
                if (t.identifier === canvasTouchId) {
                    canvasTouchId = null;
                    if (canvasTouchTimer) { clearTimeout(canvasTouchTimer); canvasTouchTimer = null; }
                    if (canvasTouchMining) {
                        // Was mining — release left-click
                        this.mouse.l = false;
                        this.p.mt = 0;
                        this.p.mtgt = null;
                    } else {
                        // Quick tap — place block (pulse right-click for one frame)
                        this.mouse.r = true;
                        setTimeout(() => { this.mouse.r = false; }, 50);
                    }
                    canvasTouchMining = false;
                }
            }
        };

        this.cv.addEventListener('touchstart', onCanvasTouchStart, { passive: false });
        window.addEventListener('touchmove', onCanvasTouchMove, { passive: false });
        window.addEventListener('touchend', onCanvasTouchEnd);
        window.addEventListener('touchcancel', onCanvasTouchEnd);
        this._touchCleanup.push(
            () => this.cv.removeEventListener('touchstart', onCanvasTouchStart),
            () => window.removeEventListener('touchmove', onCanvasTouchMove),
            () => window.removeEventListener('touchend', onCanvasTouchEnd),
            () => window.removeEventListener('touchcancel', onCanvasTouchEnd)
        );

        // --- PINCH-TO-ZOOM on canvas ---
        let pinchStartDist = null;
        let pinchStartZoom = null;

        const getPinchDist = (touches) => {
            const dx = touches[0].clientX - touches[1].clientX;
            const dy = touches[0].clientY - touches[1].clientY;
            return Math.hypot(dx, dy);
        };

        const onPinchStart = (e) => {
            if (this.stopped || e.touches.length < 2) return;
            e.preventDefault();
            pinchStartDist = getPinchDist(e.touches);
            pinchStartZoom = this.targetZoom;
        };

        const onPinchMove = (e) => {
            if (this.stopped || e.touches.length < 2 || pinchStartDist === null) return;
            e.preventDefault();
            const currentDist = getPinchDist(e.touches);
            const scale = currentDist / pinchStartDist;
            this.targetZoom = Math.max(1, Math.min(4, pinchStartZoom * scale));
        };

        const onPinchEnd = (e) => {
            if (e.touches.length < 2) {
                pinchStartDist = null;
                pinchStartZoom = null;
            }
        };

        this.cv.addEventListener('touchstart', onPinchStart, { passive: false });
        this.cv.addEventListener('touchmove', onPinchMove, { passive: false });
        this.cv.addEventListener('touchend', onPinchEnd);
        this.cv.addEventListener('touchcancel', onPinchEnd);
        this._touchCleanup.push(
            () => this.cv.removeEventListener('touchstart', onPinchStart),
            () => this.cv.removeEventListener('touchmove', onPinchMove),
            () => this.cv.removeEventListener('touchend', onPinchEnd),
            () => this.cv.removeEventListener('touchcancel', onPinchEnd)
        );
    }

    // Left joystick: movement + jump (push up to jump)
    _updateMoveJoystick(touchX, touchY, center, knob, maxR, deadZone, jumpThreshold) {
        let dx = touchX - center.x;
        let dy = touchY - center.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > maxR) { dx = (dx / dist) * maxR; dy = (dy / dist) * maxR; }
        knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
        const nx = dx / maxR, ny = dy / maxR;
        this.keys['a'] = nx < -deadZone;
        this.keys['d'] = nx > deadZone;
        this.keys['w'] = ny < -deadZone;
        this.keys['s'] = ny > deadZone;
        // Jump when pushing joystick up past threshold
        this.keys[' '] = ny < jumpThreshold;
    }

    // Right joystick: attack in direction
    _updateAttackJoystick(touchX, touchY, center, knob, maxR, deadZone) {
        let dx = touchX - center.x;
        let dy = touchY - center.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > maxR) { dx = (dx / dist) * maxR; dy = (dy / dist) * maxR; }
        knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
        const nx = dx / maxR, ny = dy / maxR;
        const magnitude = Math.sqrt(nx * nx + ny * ny);
        if (magnitude > deadZone) {
            this._touchAttackDir.x = nx;
            this._touchAttackDir.y = ny;
            this._touchAttackDir.active = true;
            // Set player facing direction based on attack joystick
            if (Math.abs(nx) > 0.2) this.p.right = nx > 0;
            // Find nearest mob/block in attack direction and simulate mouse click there
            this._performTouchAttack();
        } else {
            this._touchAttackDir.active = false;
            this.mouse.l = false;
            this.p.mt = 0;
            this.p.mtgt = null;
        }
    }

    // Attack toward the direction the right joystick is pointing
    _performTouchAttack() {
        const p = this.p;
        const atkDir = this._touchAttackDir;
        if (!atkDir.active) return;
        const pcx = p.x + PW / 2, pcy = p.y + PH / 2;
        const reach = 5; // tiles
        // Find the best target (mob or block) in attack direction
        let bestMob = null, bestMobDist = Infinity;
        for (const mob of this.mobs) {
            if (mob.dead) continue;
            const mx = mob.x + mob.w / 2, my = mob.y + mob.h / 2;
            const mdx = mx - pcx, mdy = my - pcy;
            const mdist = Math.sqrt(mdx * mdx + mdy * mdy) / TS;
            if (mdist > reach) continue;
            // Check if mob is roughly in the attack direction
            const dot = (mdx * atkDir.x + mdy * atkDir.y) / (Math.sqrt(mdx * mdx + mdy * mdy) || 1);
            if (dot > 0.4 && mdist < bestMobDist) {
                bestMobDist = mdist;
                bestMob = mob;
            }
        }
        // Also check boss
        if (this.boss && this.boss.alive) {
            const b = this.boss;
            const bx = b instanceof MoonLord ? b.cx : b.x + b.w / 2;
            const by = b instanceof MoonLord ? b.cy : b.y + b.h / 2;
            const bdx = bx - pcx, bdy = by - pcy;
            const bdist = Math.sqrt(bdx * bdx + bdy * bdy) / TS;
            const dot = (bdx * atkDir.x + bdy * atkDir.y) / (Math.sqrt(bdx * bdx + bdy * bdy) || 1);
            if (dot > 0.3 && bdist < bestMobDist && bdist < reach * 2) {
                bestMob = { x: bx - 4, y: by - 4, w: 8, h: 8, _isBoss: true };
            }
        }
        if (bestMob) {
            // Point mouse at the mob
            const vw = this.cv.width / this.zoom, vh = this.cv.height / this.zoom;
            const screenX = (bestMob.x + (bestMob.w || 0) / 2 - this.cam.x + vw / 2) * this.zoom;
            const screenY = (bestMob.y + (bestMob.h || 0) / 2 - this.cam.y + vh / 2) * this.zoom;
            this.mouse.x = screenX;
            this.mouse.y = screenY;
        } else {
            // No mob found — attack/mine in the joystick direction
            const aimDist = 2.5 * TS;
            const worldX = pcx + atkDir.x * aimDist;
            const worldY = pcy + atkDir.y * aimDist;
            const vw = this.cv.width / this.zoom, vh = this.cv.height / this.zoom;
            const screenX = (worldX - this.cam.x + vw / 2) * this.zoom;
            const screenY = (worldY - this.cam.y + vh / 2) * this.zoom;
            this.mouse.x = screenX;
            this.mouse.y = screenY;
        }
        this.mouse.l = true;
    }

    stop() {
        this.stopped = true;
        if (this.handlers) {
            window.removeEventListener('keydown', this.handlers.keydown);
            window.removeEventListener('keyup', this.handlers.keyup);
            window.removeEventListener('resize', this.handlers.resize);

            if (this.cv) {
                this.cv.removeEventListener('wheel', this.handlers.wheel);
                this.cv.removeEventListener('mousemove', this.handlers.mousemove);
                this.cv.removeEventListener('mousedown', this.handlers.mousedown);
                this.cv.removeEventListener('mouseup', this.handlers.mouseup);
                this.cv.removeEventListener('contextmenu', this.handlers.contextmenu);
            }
        }
        // Clean up touch controls
        if (this._touchCleanup) {
            for (const fn of this._touchCleanup) fn();
            this._touchCleanup = null;
        }
    }
    setupHotbar() {
        const hb = document.getElementById('hotbar'); hb.innerHTML = '';
        for (let i = 0; i < 8; i++) {
            const s = document.createElement('div'); s.className = 'slot' + (i === this.p.slot ? ' active' : ''); s.id = 'sl-' + i;
            const it = this.p.inv[i];
            if (it && it.c > 0) {
                const cvs = document.createElement('canvas'); cvs.width = 24; cvs.height = 24; cvs.style.cssText = 'width:24px;height:24px;image-rendering:pixelated;';
                const icx = cvs.getContext('2d'); icx.imageSmoothingEnabled = false;
                ItemDrop.renderIcon(icx, it.t, 0, 0, 24, 24);
                s.appendChild(cvs);
                const c = document.createElement('div'); c.className = 'slot-count'; c.textContent = it.c; s.appendChild(c);
            }
            s.addEventListener('click', () => { this.p.slot = i; this.updateHotbar(); }); hb.appendChild(s);
        }
    }
    updateHotbar() {
        for (let i = 0; i < 8; i++) {
            const s = document.getElementById('sl-' + i); if (!s) continue;
            s.className = 'slot' + (i === this.p.slot ? ' active' : ''); s.innerHTML = '';
            const it = this.p.inv[i]; if (it && it.c > 0) {
                const cvs = document.createElement('canvas'); cvs.width = 24; cvs.height = 24; cvs.style.cssText = 'width:24px;height:24px;image-rendering:pixelated;';
                const icx = cvs.getContext('2d'); icx.imageSmoothingEnabled = false;
                ItemDrop.renderIcon(icx, it.t, 0, 0, 24, 24);
                s.appendChild(cvs);
                const c = document.createElement('div'); c.className = 'slot-count'; c.textContent = it.c; s.appendChild(c);
            }
        }
        if (this._invOpen) this._renderInventory();
    }
    _renderInventory() {
        let panel = document.getElementById('inv-panel');
        if (!this._invOpen) { if (panel) panel.style.display = 'none'; return; }
        if (!panel) {
            panel = document.createElement('div'); panel.id = 'inv-panel';
            panel.style.cssText = `position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
                background:rgba(10,10,20,0.92);border:2px solid rgba(255,255,255,0.25);border-radius:12px;
                padding:16px;z-index:50;backdrop-filter:blur(8px);box-shadow:0 8px 32px rgba(0,0,0,0.6);
                font-family:monospace;color:#fff;`;
            this.cv.parentElement.appendChild(panel);
        }
        panel.style.display = 'block';
        panel.innerHTML = '';
        // Title
        const title = document.createElement('div');
        title.style.cssText = 'font-size:14px;font-weight:bold;margin-bottom:8px;text-align:center;color:#ffcc44;text-shadow:0 0 8px rgba(255,200,50,0.4);';
        title.textContent = '📦 Inventory [E]';
        panel.appendChild(title);
        // Equipment info
        const eqDiv = document.createElement('div');
        eqDiv.style.cssText = 'font-size:11px;margin-bottom:8px;text-align:center;padding:4px;background:rgba(255,255,255,0.05);border-radius:6px;';
        const wt = WEAPON_TIERS[this.equipWeapon], at = ARMOR_TIERS[this.equipArmor], pt = PICKAXE_TIERS[this.equipPick];
        let eqHtml = `<span style="color:${wt.color}">🗡${wt.name} (${wt.dmg}dmg)</span> · <span style="color:${at.color || '#888'}">🛡${at.name} (${Math.round(at.def * 100)}%def)</span> · <span style="color:${pt.color}">⛏${pt.name} (${pt.speed}x)</span>`;
        if (this._specialWeapon) eqHtml += ` · <span style="color:${this._specialWeapon.color}">⚔${this._specialWeapon.name} (${this._specialWeapon.dmg}dmg) [Q]</span>`;
        eqDiv.innerHTML = eqHtml;
        panel.appendChild(eqDiv);
        // Grid 8x4
        const grid = document.createElement('div');
        grid.style.cssText = 'display:grid;grid-template-columns:repeat(8,44px);gap:3px;';
        for (let i = 0; i < 32; i++) {
            const slot = document.createElement('div');
            const isHotbar = i < 8;
            slot.style.cssText = `width:44px;height:44px;background:${isHotbar ? 'rgba(80,80,120,0.5)' : 'rgba(40,40,60,0.5)'};
                border:${i === this.p.slot && isHotbar ? '2px solid #ffcc44' : '1px solid rgba(255,255,255,0.15)'};
                border-radius:6px;display:flex;align-items:center;justify-content:center;position:relative;cursor:pointer;
                transition:background 0.1s;`;
            slot.addEventListener('mouseenter', () => { slot.style.background = 'rgba(100,100,160,0.6)'; });
            slot.addEventListener('mouseleave', () => { slot.style.background = isHotbar ? 'rgba(80,80,120,0.5)' : 'rgba(40,40,60,0.5)'; });
            const it = this.p.inv[i];
            if (it && it.c > 0) {
                const cvs = document.createElement('canvas'); cvs.width = 28; cvs.height = 28; cvs.style.cssText = 'width:28px;height:28px;image-rendering:pixelated;border-radius:3px;';
                const icx = cvs.getContext('2d'); icx.imageSmoothingEnabled = false;
                ItemDrop.renderIcon(icx, it.t, 0, 0, 28, 28);
                slot.appendChild(cvs);
                const cnt = document.createElement('div');
                cnt.style.cssText = 'position:absolute;bottom:1px;right:3px;font-size:10px;color:#fff;text-shadow:0 0 3px #000;';
                cnt.textContent = it.c;
                slot.appendChild(cnt);
                // Tile name tooltip
                const name = Object.entries(T).find(([, v]) => v === it.t)?.[0] || '???';
                slot.title = `${name} x${it.c}`;
            }
            if (isHotbar) {
                const num = document.createElement('div');
                num.style.cssText = 'position:absolute;top:1px;left:3px;font-size:9px;color:rgba(255,255,255,0.4);';
                num.textContent = i + 1;
                slot.appendChild(num);
            }
            // Click to swap with selected slot
            slot.addEventListener('click', () => {
                if (this._invSelected !== undefined && this._invSelected !== i) {
                    // Swap
                    const tmp = this.p.inv[i];
                    this.p.inv[i] = this.p.inv[this._invSelected];
                    this.p.inv[this._invSelected] = tmp;
                    this._invSelected = undefined;
                    this.updateHotbar();
                } else {
                    this._invSelected = i;
                    this._renderInventory();
                }
            });
            // Highlight selected
            if (this._invSelected === i) {
                slot.style.border = '2px solid #44ffaa';
                slot.style.background = 'rgba(68,255,170,0.15)';
            }
            grid.appendChild(slot);
        }
        panel.appendChild(grid);
        // Accessories section
        if (this._accessories.length > 0) {
            const accDiv = document.createElement('div');
            accDiv.style.cssText = 'margin-top:8px;padding:6px;background:rgba(68,255,170,0.05);border:1px solid rgba(68,255,170,0.2);border-radius:6px;';
            let accHtml = '<div style="font-size:10px;color:#44ffaa;font-weight:bold;margin-bottom:4px;">🎒 Аксессуары</div>';
            for (const accId of this._accessories) {
                const acc = ACCESSORIES.find(a => a.id === accId);
                if (acc) accHtml += `<div style="font-size:9px;color:${acc.color};margin:2px 0;">${acc.name} — <span style="color:#aaa">${acc.desc}</span></div>`;
            }
            accDiv.innerHTML = accHtml;
            panel.appendChild(accDiv);
        }

        // Special weapon
        if (this._specialWeapon) {
            const swDiv = document.createElement('div');
            swDiv.style.cssText = `margin-top:4px;padding:4px 6px;background:rgba(255,255,255,0.03);border:1px solid ${this._specialWeapon.color}33;border-radius:4px;`;
            swDiv.innerHTML = `<span style="color:${this._specialWeapon.color};font-size:10px;font-weight:bold;">⚔ ${this._specialWeapon.name}</span> <span style="color:#aaa;font-size:9px;">${this._specialWeapon.desc} · ${this._specialWeapon.dmg}dmg · [Q]</span>`;
            panel.appendChild(swDiv);
        }

        // Hint
        const hint = document.createElement('div');
        hint.style.cssText = 'font-size:10px;color:rgba(255,255,255,0.4);text-align:center;margin-top:6px;';
        hint.textContent = 'Click to select, click another slot to swap. Top row = hotbar. [B] Бестиарий';
        panel.appendChild(hint);
    }
    mouseWorld() {
        const vw = this.cv.width / this.zoom, vh = this.cv.height / this.zoom;
        return { x: Math.floor((this.cam.x - vw / 2 + this.mouse.x / this.zoom) / TS), y: Math.floor((this.cam.y - vh / 2 + this.mouse.y / this.zoom) / TS) };
    }
    collides(x, y) {
        const l = Math.floor(x / TS), r = Math.floor((x + PW - 1) / TS), t = Math.floor(y / TS), b = Math.floor((y + PH - 1) / TS);
        for (let tx = l; tx <= r; tx++)for (let ty = t; ty <= b; ty++)if (SOLID.has(this.w.gt(tx, ty))) return true; return false;
    }
    addShake(str, dur) { this.cam.shakeT = Math.max(this.cam.shakeT, dur); this.cam.shakeStr = Math.max(this.cam.shakeStr, str); }
    addPart(x, y, vx, vy, c, l, sz) { if (this.parts.length < this.maxParts) this.parts.push({ x, y, vx, vy, c, l, ml: l, sz: sz || 3 }); }
    addScreenFlash(color, dur) { this.screenFlash = dur || 80; this.screenFlashColor = color || '#fff'; }
    addZoomPunch(str) { this.zoomPunch = str || 0.15; }

    // === JUICY XP GAIN ===
    addXp(amount, worldX, worldY) {
        if (amount <= 0) return;
        this.xp += amount;
        // Floating "+N XP" text (small)
        const col = amount >= 50 ? '#ffd700' : amount >= 10 ? '#44ff88' : '#33dd66';
        this.dmgNums.add(worldX, worldY - 4, `+${amount}`, col);
        // Spawn XP orbs — fewer, smaller orbs
        const orbCount = amount >= 100 ? 6 : amount >= 50 ? 4 : amount >= 10 ? 2 : 1;
        for (let i = 0; i < orbCount; i++) {
            this.xpOrbs.push(new XpOrb(worldX, worldY, amount, this.p.x + PW / 2, this.p.y + PH / 2));
        }
        // Sparkle particles at source (fewer)
        for (let i = 0; i < Math.min(amount, 3); i++) {
            const ang = Math.random() * Math.PI * 2;
            this.addPart(worldX, worldY, Math.cos(ang) * 2.5, Math.sin(ang) * 2.5 - 1, col, 20, 2);
        }
        // Milestone check — every 100 XP
        const newMilestone = Math.floor(this.xp / 100);
        if (newMilestone > this._xpLastMilestone) {
            this._xpLastMilestone = newMilestone;
            this.addScreenFlash('rgba(100,255,150,0.3)', 250);
            this.addShake(4, 150);
            this._xpPulse = 1.8;
            // Big text
            this.dmgNums.add(this.p.x + PW / 2, this.p.y - 20, `★ LEVEL ${newMilestone} ★`, '#ffd700');
        }
    }

    spawnMobs(dt) {
        this.mobSpawnTimer += dt;
        let evtMult = 1;
        if (this._currentEvent && EVENTS[this._currentEvent]) {
            const evt = EVENTS[this._currentEvent];
            evtMult = (evt.waves && evt.waves[this._eventWave]) ? evt.waves[this._eventWave].mult : (evt.mobMult || 1);
        }
        const spawnInterval = this._currentEvent ? Math.floor(2000 / evtMult) : 2000;
        if (this.mobSpawnTimer < spawnInterval) return; this.mobSpawnTimer = 0;
        const maxMobs = this._currentEvent ? 25 : 15;
        if (this.mobs.length >= maxMobs) return;
        const ptx = Math.floor(this.p.x / TS);
        const surfY = this.w.heightMap[Math.max(0, Math.min(WW - 1, ptx))];
        const isNight = this.dayTime < 0.25 || this.dayTime > 0.75;
        const underground = Math.floor(this.p.y / TS) > surfY + 10;
        const dist = 25 + Math.floor(Math.random() * 15), ddx = (Math.random() > 0.5 ? 1 : -1) * dist;
        const sx = ptx + ddx; if (sx < 5 || sx >= WW - 5) return;
        const biome = this.w.biomeMap[sx];
        let sy = underground ? Math.floor(this.p.y / TS) + Math.floor((Math.random() - 0.5) * 10) : Math.floor(this.w.heightMap[sx]) - 2;
        if (sy < 0 || sy >= WH - 5) return;
        for (let y = Math.max(0, sy - 5); y < Math.min(WH - 3, sy + 10); y++) {
            if (!SOLID.has(this.w.gt(sx, y)) && SOLID.has(this.w.gt(sx, y + 1))) { sy = y; break; }
        }
        if (SOLID.has(this.w.gt(sx, sy))) return;
        const cands = [];
        // Check if spawn location is near a village
        let nearVillage = false;
        if (this.w.villageLocations) {
            for (const vl of this.w.villageLocations) { if (Math.abs(sx - vl) < 50) { nearVillage = true; break; } }
        }
        // In villages, replenish villagers and guards
        if (nearVillage) {
            const villagersNear = this.mobs.filter(m => m.type === 'VILLAGER' && Math.abs(Math.floor(m.x / TS) - sx) < 60).length;
            const guardsNear = this.mobs.filter(m => m.type === 'GUARD' && Math.abs(Math.floor(m.x / TS) - sx) < 60).length;
            if (villagersNear < 4) {
                const vdef = MOB_TYPES['VILLAGER'];
                const mob = new Mob('VILLAGER', sx * TS, sy * TS - vdef.h);
                mob.ai.profession = Math.floor(Math.random() * 7);
                this.mobs.push(mob);
            } else if (guardsNear < 1) {
                const gdef = MOB_TYPES['GUARD'];
                this.mobs.push(new Mob('GUARD', sx * TS, sy * TS - gdef.h));
            }
            return;
        }
        const isSky = Math.floor(this.p.y / TS) < 25;
        for (const [mt, def] of Object.entries(MOB_TYPES)) {
            if (def.isVillager) continue;
            if (def.eventOnly) continue; // Event-only mobs don't spawn naturally
            if (def.skyOnly && !isSky) continue;
            if (!def.skyOnly && isSky) continue;
            if (def.night && !isNight && !underground) continue;
            if (def.underground && !underground) continue;
            if (def.biomes && !def.biomes.includes(biome) && !def.skyOnly) continue;
            if (!def.biomes && !def.night && !def.underground && !def.skyOnly) continue;
            if (def.isMiniBoss && Math.random() > 0.05) continue;
            cands.push(mt);
        }
        if (isNight) cands.push('ZOMBIE', 'SKELETON');
        // Event mob spawning — use current wave mobs if available
        if (this._currentEvent && EVENTS[this._currentEvent]) {
            const evt = EVENTS[this._currentEvent];
            let evtMobs = evt.mobs;
            if (evt.waves && evt.waves[this._eventWave]) {
                evtMobs = evt.waves[this._eventWave].mobs;
            }
            for (const em of evtMobs) { if (MOB_TYPES[em]) cands.push(em, em, em); }
        }
        // Living Caves underground biome mobs
        if (underground) {
            const underBiome = this.w.underBiomeMap[Math.max(0, Math.min(WW - 1, sx))];
            if (underBiome === 10) {
                cands.push('NERVE_WORM', 'ANTIBODY', 'ANTIBODY', 'ANTIBODY');
                if (Math.random() > 0.93) cands.push('FLESH_GOLEM');
            }
        }
        if (underground && cands.length === 0) cands.push('BAT', 'CAVE_SPIDER', 'DEEP_EYE', 'SKELETON_MINER', 'SHROOM_BEETLE', 'FROST_BAT');
        if (underground && Math.random() > 0.92) cands.push('CAVE_WORM');
        if (cands.length === 0) return;
        const type = cands[Math.floor(Math.random() * cands.length)];
        const def = MOB_TYPES[type];
        this.mobs.push(new Mob(type, sx * TS, def.ground ? sy * TS - def.h : sy * TS - 20));
        // Shroom beetles spawn in groups
        if (type === 'SHROOM_BEETLE') {
            for (let sb = 0; sb < 2 + Math.floor(Math.random() * 3); sb++) {
                const ox = (Math.random() - 0.5) * 40, oy = (Math.random() - 0.5) * 20;
                this.mobs.push(new Mob('SHROOM_BEETLE', sx * TS + ox, sy * TS - def.h + oy));
            }
        }
        // Antibodies spawn in swarms
        if (type === 'ANTIBODY') {
            for (let ab = 0; ab < 3 + Math.floor(Math.random() * 4); ab++) {
                const ox = (Math.random() - 0.5) * 30, oy = (Math.random() - 0.5) * 20;
                this.mobs.push(new Mob('ANTIBODY', sx * TS + ox, sy * TS - def.h + oy));
            }
        }
        // Chronolisks spawn in pairs
        if (type === 'CHRONOLISK' && Math.random() > 0.5) {
            const ox = (Math.random() - 0.5) * 30;
            this.mobs.push(new Mob('CHRONOLISK', sx * TS + ox, sy * TS - def.h));
        }
    }

    update(dt) {
        if (this.hitstop > 0) { this.hitstop -= dt; return; }
        // Slow-mo decay
        if (this.slowMo > 0) { this.slowMo -= dt; this.slowMoFactor = 0.3; } else this.slowMoFactor = 1;
        const effectiveDt = dt * this.slowMoFactor;
        const p = this.p;
        if (this.timeEnabled) {
            const prevDay = this.dayTime;
            this.dayTime = (this.dayTime + this.daySpeed * effectiveDt) % 1;
            // Track day count for moon phases (detect day rollover)
            if (prevDay > 0.9 && this.dayTime < 0.1) this._dayCount++;
        }
        p.iframes = Math.max(0, p.iframes - dt); p.dmgFlash = Math.max(0, p.dmgFlash - dt);
        p.swingTimer = Math.max(0, p.swingTimer - dt);
        p.squashX += (1 - p.squashX) * 0.15; p.squashY += (1 - p.squashY) * 0.15;
        p.blinkTimer += dt; p.breathTimer += dt;
        // Screen flash decay
        this.screenFlash = Math.max(0, this.screenFlash - dt);
        // Smooth zoom interpolation toward target
        if (Math.abs(this.zoom - this.targetZoom) > 0.001) {
            this.zoom += (this.targetZoom - this.zoom) * 0.12;
        } else {
            this.zoom = this.targetZoom;
        }

        const ptx = Math.floor((p.x + PW / 2) / TS), pty = Math.floor((p.y + PH / 2) / TS);
        const biome = this.w.biomeMap[Math.max(0, Math.min(WW - 1, ptx))];
        // Smooth biome weights for visual blending
        const bw = this.w.getBiomeWeights(ptx);
        if (!this.smoothBiomeWeights) this.smoothBiomeWeights = Object.assign({}, bw);
        // Lerp smooth weights toward target
        const BW_LERP = 0.015;
        for (const b in bw) { this.smoothBiomeWeights[b] = (this.smoothBiomeWeights[b] || 0) + (bw[b] - (this.smoothBiomeWeights[b] || 0)) * BW_LERP; }
        for (const b in this.smoothBiomeWeights) { if (!bw[b]) this.smoothBiomeWeights[b] *= (1 - BW_LERP); if (this.smoothBiomeWeights[b] < 0.001) delete this.smoothBiomeWeights[b]; }
        // Blend atmosphere from weights
        let tFs = 0, tDm = 0, tFogR = 0, tFogG = 0, tFogB = 0, tTintR = 0, tTintG = 0, tTintB = 0, tTintA = 0;
        for (const b in this.smoothBiomeWeights) {
            const w = this.smoothBiomeWeights[b];
            const a = BIOME_ATM[b] || BIOME_ATM[2];
            tFs += a.fs * w; tDm += a.dm * w;
            // Parse fog color 'rgba(r,g,b,'
            const fm = a.fog.match(/\d+/g);
            if (fm) { tFogR += parseInt(fm[0]) * w; tFogG += parseInt(fm[1]) * w; tFogB += parseInt(fm[2]) * w; }
            if (a.tint) { const tr = parseInt(a.tint.slice(1, 3), 16), tg = parseInt(a.tint.slice(3, 5), 16), tb = parseInt(a.tint.slice(5, 7), 16); tTintR += tr * w; tTintG += tg * w; tTintB += tb * w; tTintA += w; }
        }
        this.curAtm.fs += (tFs - this.curAtm.fs) * 0.02;
        this.curAtm.dm += (tDm - this.curAtm.dm) * 0.02;
        this.curAtm.fogR += (tFogR - this.curAtm.fogR) * 0.02; this.curAtm.fogG += (tFogG - this.curAtm.fogG) * 0.02; this.curAtm.fogB += (tFogB - this.curAtm.fogB) * 0.02;
        this.curAtm.fog = `rgba(${this.curAtm.fogR | 0},${this.curAtm.fogG | 0},${this.curAtm.fogB | 0},`;
        this.curAtm.tintR += (tTintR - this.curAtm.tintR) * 0.02; this.curAtm.tintG += (tTintG - this.curAtm.tintG) * 0.02; this.curAtm.tintB += (tTintB - this.curAtm.tintB) * 0.02; this.curAtm.tintAlpha += (tTintA - this.curAtm.tintAlpha) * 0.02;
        this.curAtm.tint = this.curAtm.tintAlpha > 0.05 ? `#${((this.curAtm.tintR / this.curAtm.tintAlpha) | 0).toString(16).padStart(2, '0')}${((this.curAtm.tintG / this.curAtm.tintAlpha) | 0).toString(16).padStart(2, '0')}${((this.curAtm.tintB / this.curAtm.tintAlpha) | 0).toString(16).padStart(2, '0')}` : null;

        this.cam.shakeT = Math.max(0, this.cam.shakeT - dt);
        if (this.cam.shakeT > 0) { const s = this.cam.shakeStr * (this.cam.shakeT / 200); this.cam.shakeX = (Math.random() - 0.5) * s; this.cam.shakeY = (Math.random() - 0.5) * s; }
        else { this.cam.shakeX = 0; this.cam.shakeY = 0; this.cam.shakeStr = 0; }

        // === AMBIENT SOUNDS (biome-specific, occasional) ===
        this._ambientSoundTimer -= dt;
        if (this._ambientSoundTimer <= 0) {
            this._ambientSoundTimer = 8000 + Math.random() * 12000; // Every 8-20 seconds
            const underground = pty > 35;
            if (underground) {
                sfx.ambientCave();
            } else {
                // Surface biome sounds
                if ([0, 1, 18, 29].includes(biome)) sfx.ambientWind(0.3 + Math.random() * 0.4);
                else if ([8, 9, 28].includes(biome)) sfx.ambientWater();
                else if ([7, 22].includes(biome) && Math.random() > 0.5) sfx.ambientWater(); // Jungle/ruins occasional water
            }
        }

        const ml = this.keys['a'] || this.keys['arrowleft'], mr = this.keys['d'] || this.keys['arrowright'];
        const mu = this.keys['w'] || this.keys['arrowup'], md = this.keys['s'] || this.keys['arrowdown'];
        const jmp = this.keys[' '];
        // Craft notification timer
        if (this._craftNotifyTimer > 0) this._craftNotifyTimer -= dt;

        // === PLAYER DEATH / RESPAWN ===
        if (this._dead) {
            this._deathTimer -= dt;
            if (this._deathTimer <= 0) {
                // Respawn!
                this._dead = false;
                p.hp = p.maxHp;
                p.x = this.w.spawnX * TS; p.y = this.w.spawnY * TS;
                p.vx = 0; p.vy = 0;
                p.iframes = 2000; p.dmgFlash = 500;
                this._prevHp = p.hp; this._dmgBarPct = 100;
                this.addScreenFlash('rgba(255,255,255,0.6)', 400);
            }
        } else {
            // === PLAYER ALIVE LOGIC ===
            p.onLadder = this.w.gt(ptx, pty) === T.LADDER || this.w.gt(ptx, Math.floor(p.y / TS)) === T.LADDER;

            // === LIQUID IMMERSION CHECK ===
            // Check precise liquid depth based on liquid levels
            let submergedHeight = 0;
            const playerBottom = p.y + PH;
            const playerTop = p.y;

            // Check tiles intersecting with player vertical range
            const startTileY = Math.floor(playerTop / TS);
            const endTileY = Math.floor((playerBottom - 0.1) / TS); // -0.1 to avoid floor tile when standing

            for (let ty = startTileY; ty <= endTileY; ty++) {
                const tAtBody = this.w.gt(ptx, ty);
                if (LIQUID.has(tAtBody)) {
                    const lvl = this.w.gll(ptx, ty); // 0.0 to 1.0 (empty to full)
                    if (lvl > 0.05) {
                        // Calculate liquid surface Y in world coordinates
                        // Liquid fills from bottom: top of liquid is (ty + 1) * TS - lvl * TS
                        // BUT in this engine, liquid levels seem to be treated as "fullness".
                        // Assuming simple block-based liquid for now, but refining depth

                        // Calculate overlap between player body segment and liquid block
                        // Block range: [ty * TS, (ty + 1) * TS]
                        // Liquid exists in: [ty * TS + (1-lvl)*TS, (ty + 1) * TS] ??? 
                        // Actually, standard Terraria liquids fill from bottom up.
                        // Let's assume lvl 1.0 = full block.
                        const liquidTop = (ty + 1) * TS - (lvl * TS);
                        const liquidBottom = (ty + 1) * TS;

                        const overlapTop = Math.max(playerTop, liquidTop);
                        const overlapBottom = Math.min(playerBottom, liquidBottom);

                        if (overlapBottom > overlapTop) {
                            submergedHeight += (overlapBottom - overlapTop);
                        }
                    }
                }
            }

            p.liquidDepth = Math.min(1, submergedHeight / PH);
            const feetTile = this.w.gt(ptx, Math.floor((p.y + PH - 2) / TS));
            const waistTile = this.w.gt(ptx, Math.floor((p.y + PH / 2) / TS));
            const headTile = this.w.gt(ptx, Math.floor(p.y / TS));
            const inLiquidFeet = LIQUID.has(feetTile);
            const inLiquidWaist = LIQUID.has(waistTile);
            const inLiquidHead = LIQUID.has(headTile);
            // Compute immersion fraction (0 = dry, 1 = fully submerged)

            const prevInLiquid = p.wasInLiquid;
            p.wasInLiquid = p.liquidDepth > 0.1;
            p.liquidType = inLiquidWaist ? waistTile : (inLiquidFeet ? feetTile : 0);
            p.inLiquid = p.liquidDepth;

            // === SPLASH EFFECT on entering/exiting water ===
            if (p.wasInLiquid && !prevInLiquid && Math.abs(p.vy) > 1.5) {
                const splashForce = Math.min(Math.abs(p.vy) / 6, 1);
                const splashCol = p.liquidType === T.LAVA ? '#ff6622' : (p.liquidType === T.HONEY ? '#ffaa22' : '#4488cc');
                const splashCount = 6 + Math.floor(splashForce * 10);
                for (let i = 0; i < splashCount; i++) {
                    const ang = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.8;
                    const spd = 1.5 + Math.random() * splashForce * 4;
                    this.addPart(p.x + PW / 2 + (Math.random() - 0.5) * PW, p.y + PH * p.liquidDepth,
                        Math.cos(ang) * spd, Math.sin(ang) * spd - 1, splashCol, 30 + Math.random() * 20, 2 + Math.random() * 2);
                }
                // Ripple particles at surface
                for (let i = 0; i < 4; i++) {
                    this.addPart(p.x + PW / 2 + (Math.random() - 0.5) * 20, p.y + PH * p.liquidDepth,
                        (Math.random() - 0.5) * 3, -0.5, 'rgba(255,255,255,0.5)', 20, 1);
                }
                if (splashForce > 0.3) this.addShake(splashForce * 2, 60);
                // Splash sound!
                sfx.splash(splashForce);
            }
            // Sound when exiting water
            if (!p.wasInLiquid && prevInLiquid) {
                sfx.splash(0.3);
            }

            // Liquid speed modifiers
            const isSwimming = p.liquidDepth > 0.4;
            const liquidSpdMult = isSwimming ? (p.liquidType === T.HONEY ? 0.35 : (p.liquidType === T.LAVA ? 0.45 : 0.55)) : 1;
            const spdMult = (this._buffs.speed ? this._buffs.speed.value : 1) * liquidSpdMult;
            if (ml) { p.vx = -MSPD * spdMult; p.right = false; } else if (mr) { p.vx = MSPD * spdMult; p.right = true; } else p.vx *= (isSwimming ? 0.85 : 0.7);

            if (p.ground) p.coyoteTime = 80; else p.coyoteTime = Math.max(0, p.coyoteTime - dt);
            if (jmp) p.jumpBuffer = 100; else p.jumpBuffer = Math.max(0, p.jumpBuffer - dt);

            if (p.onLadder) {
                p.vy *= 0.8; if (mu) p.vy = -MSPD; else if (md) p.vy = MSPD; else p.vy *= 0.5;
                if (p.jumpBuffer > 0) { p.vy = JFORCE * 0.7; p.jumpBuffer = 0; sfx.jump(); p.squashX = 1.3; p.squashY = 0.7; }
            }
            else if (isSwimming) {
                // === SWIMMING PHYSICS ===
                const swimGrav = p.liquidType === T.HONEY ? GRAV * 0.08 : (p.liquidType === T.LAVA ? GRAV * 0.15 : GRAV * 0.12);
                p.vy += swimGrav;
                // Buoyancy — slow pull upward when not pressing down
                if (!md && p.liquidDepth > 0.5) p.vy -= GRAV * 0.08;
                // Swimming up
                if (mu || (p.jumpBuffer > 0)) {
                    const swimForce = p.liquidType === T.HONEY ? -1.2 : (p.liquidType === T.LAVA ? -1.5 : -2.0);
                    p.vy = Math.max(swimForce, p.vy + swimForce * 0.3);
                    if (p.jumpBuffer > 0) p.jumpBuffer = 0;
                    p.swimTimer += dt;
                    // Swim stroke sounds
                    if (!p._swimSndTimer) p._swimSndTimer = 0;
                    p._swimSndTimer += dt;
                    if (p._swimSndTimer > 400) { p._swimSndTimer = 0; sfx.swimStroke(); }
                    // Swimming animation particles (bubbles)
                    if (Math.random() > 0.7) {
                        const bubCol = p.liquidType === T.LAVA ? '#ff8844' : (p.liquidType === T.HONEY ? '#ffcc44' : '#88ccff');
                        this.addPart(p.x + PW / 2 + (Math.random() - 0.5) * 8, p.y + PH * 0.6,
                            (Math.random() - 0.5) * 0.5, -0.8 - Math.random() * 0.5, bubCol, 30 + Math.random() * 20, 1.5 + Math.random());
                    }
                }
                // Swimming down
                if (md) p.vy = Math.min(2, p.vy + 0.3);
                // Dampen vertical speed in liquid
                p.vy *= 0.92;
                // Can still jump OUT of liquid surface
                // If near surface (depth < 0.55), allow a stronger jump to exit water
                if (p.liquidDepth < 0.55 && (p.jumpBuffer > 0 || mu)) {
                    // Extra upward nudge if just holding UP near surface
                    if (mu && !p.jumpBuffer) p.vy -= 0.5;

                    if (p.jumpBuffer > 0) {
                        p.vy = JFORCE * 0.85; // Increased exit force (was 0.6)
                        p.jumpBuffer = 0;
                        sfx.jump(); p.squashX = 0.85; p.squashY = 1.2;
                    }
                }
            }
            else {
                p.vy += this._hasAccessory('feather_fall') ? GRAV * 0.4 : GRAV;
                if (p.jumpBuffer > 0 && p.coyoteTime > 0) {
                    p.vy = JFORCE; p.ground = false; p.coyoteTime = 0; p.jumpBuffer = 0;
                    sfx.jump(); p.squashX = 0.8; p.squashY = 1.3;
                    this._hasDoubleJumped = false;
                } else if (p.jumpBuffer > 0 && !p.ground && !this._hasDoubleJumped && this._hasAccessory('double_jump')) {
                    p.vy = JFORCE * 0.85; p.jumpBuffer = 0;
                    this._hasDoubleJumped = true;
                    sfx.jump(); p.squashX = 0.75; p.squashY = 1.35;
                    // Cloud puff effect
                    for (let i = 0; i < 8; i++) {
                        const a = Math.random() * Math.PI * 2;
                        this.addPart(p.x + PW / 2, p.y + PH, Math.cos(a) * 2.5, Math.sin(a) * 1.5, '#cceeff', 25, 2 + Math.random() * 2);
                    }
                    this.addScreenFlash('rgba(170,220,255,0.15)', 80);
                }
            }
            // Feather fall — cap fall speed
            if (this._hasAccessory('feather_fall') && p.vy > 3) p.vy = 3;
            p.vy = Math.max(-15, Math.min(15, p.vy));

            // === BUBBLE PARTICLES while submerged ===
            if (p.liquidDepth > 0.5) {
                p.bubbleTimer += dt;
                if (p.bubbleTimer > 300 + Math.random() * 400) {
                    p.bubbleTimer = 0;
                    const bubCol = p.liquidType === T.LAVA ? '#ff6633' : (p.liquidType === T.HONEY ? '#ffbb33' : '#aaddff');
                    this.addPart(p.x + PW / 2 + (Math.random() - 0.5) * 10, p.y + PH * 0.3 + Math.random() * PH * 0.3,
                        (Math.random() - 0.5) * 0.3, -0.4 - Math.random() * 0.6, bubCol, 40 + Math.random() * 30, 1 + Math.random() * 1.5);
                    // Occasional bubble sound
                    if (Math.random() > 0.6) sfx.bubble();
                }
                // Underwater ambience sound every 5-8 seconds
                if (!p._underwaterSndTimer) p._underwaterSndTimer = 0;
                p._underwaterSndTimer += dt;
                if (p._underwaterSndTimer > 5000 + Math.random() * 3000) {
                    p._underwaterSndTimer = 0;
                    sfx.underwaterAmbience();
                }
            } else {
                p._underwaterSndTimer = 0;
            }

            p.wasGround = p.ground;
            const nx = p.x + p.vx; if (!this.collides(nx, p.y)) p.x = nx; else p.vx = 0;
            const ny = p.y + p.vy; if (!this.collides(p.x, ny)) p.y = ny; else {
                if (p.vy > 0) {
                    if (!p.wasGround && p.vy > 3 && !isSwimming) {
                        const landForce = Math.min(p.vy / 8, 1);
                        sfx.land(); p.squashX = 1.2 + landForce * 0.2; p.squashY = 0.7 - landForce * 0.1;
                        this.addShake(1 + landForce * 3, 50 + landForce * 80);
                        const landParts = 4 + Math.floor(landForce * 8);
                        for (let i = 0; i < landParts; i++)this.addPart(p.x + PW / 2 + (Math.random() - 0.5) * 14, p.y + PH, (Math.random() - 0.5) * (3 + landForce * 3), -Math.random() * (2 + landForce * 3), '#8a7a5a', 25 + Math.random() * 15, 2 + Math.random() * 2);
                    }
                    p.ground = true;
                } p.vy = 0;
            }
            p.ground = this.collides(p.x, p.y + 1);
            if (p.ground) this._hasDoubleJumped = false;

            if (p.ground && Math.abs(p.vx) > 1) { p.stepTimer += dt; if (p.stepTimer > 200) { p.stepTimer = 0; sfx.step(); } }
            if (Math.abs(p.vx) > 0.5) { p.at += dt * 0.01; if (p.at > 1) { p.at = 0; p.af = (p.af + 1) % 4; } } else p.af = 0;

            // === DROWNING MECHANIC ===
            if (p.liquidDepth > 0.8 && p.liquidType === T.WATER) {
                p.swimTimer = (p.swimTimer || 0) + dt;
                // Start drowning after 30 seconds
                if (p.swimTimer > 30000 && p.iframes <= 0) {
                    const drowningInterval = Math.max(500, 2000 - (p.swimTimer - 30000) / 10);
                    if (!p._lastDrownTick || p.swimTimer - p._lastDrownTick > drowningInterval) {
                        p._lastDrownTick = p.swimTimer;
                        p.hp = Math.max(0, p.hp - 3);
                        p.dmgFlash = 150;
                        sfx.hurt();
                        // Panic bubbles
                        for (let i = 0; i < 5; i++) {
                            this.addPart(p.x + PW / 2 + (Math.random() - 0.5) * 8, p.y + 4,
                                (Math.random() - 0.5) * 1.5, -1 - Math.random() * 2, '#aaddff', 25, 2 + Math.random());
                        }
                    }
                }
            } else {
                // Recover breath when out of water
                p.swimTimer = Math.max(0, (p.swimTimer || 0) - dt * 3);
                p._lastDrownTick = 0;
            }

            // GOD_MODE: keep HP maxed
            if (GOD_MODE) { p.hp = p.maxHp; }
            // Check death
            if (p.hp <= 0 && !GOD_MODE) {
                this._dead = true;
                this._deathTimer = 2000; // respawn in 2 seconds
                // Gore chunks from player!
                this.gore.spawnFromPlayer(p, null, this.w);
                // Death effects
                sfx.mobDie();
                this.addShake(8, 400);
                this.addScreenFlash('rgba(180,0,0,0.5)', 500);
                // Blood particles
                for (let i = 0; i < 15; i++) {
                    const ang = Math.random() * Math.PI * 2;
                    this.addPart(p.x + PW / 2, p.y + PH / 2, Math.cos(ang) * 4, Math.sin(ang) * 4 - 2, '#cc2222', 30, 3);
                }
                // Save score on death
                if (this.xp > this.bestXp) {
                    this.bestXp = this.xp;
                    try { localStorage.setItem('terraria_best', String(this.xp)); } catch { }
                    if (this.onScoreUpdate) this.onScoreUpdate(this.xp);
                }
            } else {
                // HP Regeneration: 1 HP per second when not taking damage for 3s
                this._noDmgTimer += dt;
                if (p.hp < this._prevHp) this._noDmgTimer = 0; // reset on damage
                const regenDelay = this._hasAccessory('regen') ? 1500 : 3000;
                const regenAmount = this._hasAccessory('regen') ? 3 : 1;
                if (this._noDmgTimer > regenDelay && p.hp < p.maxHp && !GOD_MODE) {
                    this._regenTimer += dt;
                    if (this._regenTimer >= 1000) {
                        this._regenTimer -= 1000;
                        p.hp = Math.min(p.maxHp, p.hp + regenAmount);
                    }
                } else {
                    this._regenTimer = 0;
                }

                const ptile = this.w.gt(ptx, pty);
                const armorDef = ARMOR_TIERS[this.equipArmor].def;
                const reduceDmg = (raw) => {
                    let d = Math.max(1, Math.ceil(raw * (1 - armorDef)));
                    if (this._buffs.shield) d = Math.ceil(d / 2);
                    return d;
                };
                if (ptile === T.SPIKE && p.iframes <= 0) { p.hp = Math.max(0, p.hp - reduceDmg(5)); p.iframes = 500; p.dmgFlash = 200; sfx.hurt(); }
                if (ptile === T.LAVA && p.iframes <= 0 && !this._hasAccessory('fire_immune')) { p.hp = Math.max(0, p.hp - reduceDmg(10)); p.iframes = 300; p.dmgFlash = 200; sfx.hurt(); }
                if (ptile === T.THORN && p.iframes <= 0) { p.hp = Math.max(0, p.hp - reduceDmg(3)); p.iframes = 400; p.dmgFlash = 150; sfx.hurt(); }
                // Track sky visit
                if (pty < 25 && !this._visitedSky) this._visitedSky = true;

                // Mining & attacking
                if (this.mouse.l) {
                    const mp = this.mouseWorld(); const dx2 = mp.x - ptx, dy2 = mp.y - pty;
                    if (Math.sqrt(dx2 * dx2 + dy2 * dy2) < 7) {
                        if (p.swingTimer <= 0) {
                            p.swingTimer = 220; p.swingDir = p.right ? 1 : -1; p.swingAngle = -Math.PI / 3 * p.swingDir;
                            sfx.swing();
                            // Squash player on swing for weight feel
                            p.squashX = p.right ? 0.85 : 1.15; p.squashY = 1.1;
                        }
                        let hitMobThisFrame = false;
                        for (const mob of this.mobs) {
                            const mx = Math.floor((mob.x + mob.w / 2) / TS), my = Math.floor((mob.y + mob.h / 2) / TS);
                            if (Math.abs(mx - mp.x) <= 1 && Math.abs(my - mp.y) <= 1 && mob.hitTimer <= 0) {
                                // Critical hit chance — weapon damage scales with tier
                                const weapTier = WEAPON_TIERS[this.equipWeapon];
                                let baseDmg = weapTier.dmg;
                                if (this._buffs.rage) baseDmg *= 2;
                                const critBonus = this._hasAccessory('crit_up') ? 0.15 : 0;
                                const isCrit = Math.random() < 0.15 + this.equipWeapon * 0.01 + critBonus;
                                const dmg = isCrit ? Math.ceil(baseDmg * 1.8) : baseDmg;
                                mob.hp -= dmg; mob.hitTimer = 350;
                                // Juicier knockback
                                const kbDir = mob.x > p.x ? 1 : -1;
                                mob.vx += kbDir * (isCrit ? 9 : 6); mob.vy = isCrit ? -7 : -4.5;
                                mob.squash = isCrit ? 1.7 : 1.4; mob.stretch = isCrit ? 0.5 : 0.7;
                                // Hitstop — longer for crits
                                if (!hitMobThisFrame && this.hitstop <= 0) { this.hitstop = isCrit ? 65 : 40; hitMobThisFrame = true; }
                                this.addShake(isCrit ? 5 : 3, isCrit ? 120 : 70);
                                sfx.mobHit();
                                // Screen flash
                                this.addScreenFlash(isCrit ? 'rgba(255,220,60,0.25)' : 'rgba(255,255,255,0.12)', isCrit ? 100 : 60);
                                // Damage numbers — crits are bigger and gold
                                const numCol = isCrit ? '#ffdd00' : '#ff4444';
                                this.dmgNums.add(mob.x + mob.w / 2, mob.y - 8, isCrit ? `💥${dmg}` : dmg, numCol);
                                // Hit particles — more, varied, juicier
                                const hitX = mob.x + mob.w / 2, hitY = mob.y + mob.h / 2;
                                const pc = isCrit ? 12 : 7;
                                const hitCols = isCrit ? ['#ffdd00', '#ffaa00', '#ff6600', '#fff', '#ff4444'] : ['#ff6666', '#cc3333', '#aa2222', '#884444', '#ffaaaa'];
                                for (let i = 0; i < pc; i++) {
                                    const ang = (Math.random() - 0.5) * Math.PI + (kbDir > 0 ? 0 : Math.PI);
                                    const spd = 2 + Math.random() * (isCrit ? 6 : 4);
                                    this.addPart(hitX + (Math.random() - 0.5) * 6, hitY + (Math.random() - 0.5) * 6,
                                        Math.cos(ang) * spd, Math.sin(ang) * spd - 1.5,
                                        hitCols[Math.floor(Math.random() * hitCols.length)],
                                        25 + Math.random() * 20, 2 + Math.random() * (isCrit ? 3 : 1.5));
                                }
                                // Knockback trail sparks from mob
                                for (let i = 0; i < 3; i++) {
                                    this.addPart(hitX, hitY, -kbDir * (1 + Math.random() * 2), (Math.random() - 0.5) * 2,
                                        'rgba(255,200,100,0.8)', 15 + Math.random() * 10, 1.5);
                                }
                                // Prism Crab damage reflection
                                if (mob.type === 'PRISM_CRAB' && !mob.dead && Math.random() < 0.3) {
                                    const reflDmg = Math.ceil(dmg * 0.25);
                                    p.hp = Math.max(0, p.hp - reflDmg); p.dmgFlash = 150;
                                    this.dmgNums.add(p.x + PW / 2, p.y - 8, `↩${reflDmg}`, '#ccddff');
                                    const rCols = ['#ff4444', '#ffaa44', '#ffff44', '#44ff44', '#4444ff', '#aa44ff'];
                                    for (let ri = 0; ri < 4; ri++) {
                                        const ra = Math.random() * Math.PI * 2;
                                        this.addPart(mob.x + mob.w / 2, mob.y + mob.h / 2, Math.cos(ra) * 3, Math.sin(ra) * 3, rCols[ri % rCols.length], 20, 2);
                                    }
                                }
                                if (mob.hp <= 0) {
                                    mob.dead = true; sfx.mobDie();
                                    this.addShake(6, 180);
                                    this.addScreenFlash('rgba(255,80,80,0.2)', 120);
                                    this.hitstop = Math.max(this.hitstop, 80);
                                    // Gore chunks — physics-based mob texture fragments!
                                    this.gore.spawnFromMob(mob, this.w, performance.now());
                                    // Sporeling split mechanic — spawn 2 smaller ones on death
                                    if (mob.type === 'SPORELING' && mob.maxHp > 8) {
                                        for (let si = 0; si < 2; si++) {
                                            const sm = new Mob('SPORELING', mob.x + (si === 0 ? -8 : 8), mob.y);
                                            sm.hp = Math.floor(mob.maxHp * 0.4); sm.maxHp = sm.hp;
                                            sm.w = Math.max(6, mob.w - 3); sm.h = Math.max(6, mob.h - 3);
                                            sm.vx = (si === 0 ? -2 : 2) + (Math.random() - 0.5); sm.vy = -3;
                                            this.mobs.push(sm);
                                        }
                                        for (let si = 0; si < 8; si++) {
                                            const sa = Math.random() * Math.PI * 2;
                                            this.addPart(hitX, hitY, Math.cos(sa) * 2, Math.sin(sa) * 2 - 1, '#cc44aa', 25, 2);
                                        }
                                    }
                                    // Small accent particles
                                    for (let i = 0; i < 6; i++) {
                                        const ang = Math.random() * Math.PI * 2;
                                        this.addPart(hitX, hitY, Math.cos(ang) * 3, Math.sin(ang) * 3 - 1, '#fff', 15, 2);
                                    }
                                    // Bestiary: record kill
                                    this._recordBestiary(mob.type, true);
                                    // Accessory/Special Weapon drop check
                                    this._checkAccessoryDrop(mob);
                                    this._checkSpecialWeaponDrop(mob);
                                    // XP for killing mob
                                    {
                                        const mt = MOB_TYPES[mob.type]; let mxp = 5;
                                        if (mt && mt.hp >= 40) mxp = 15;
                                        if (mt && mt.hp >= 80) mxp = 30;
                                        if (mt && mt.hp >= 150) mxp = 50;
                                        if (mt && mt.isMiniBoss) mxp = 100;
                                        // Event XP multiplier
                                        if (this._currentEvent && EVENTS[this._currentEvent]) mxp = Math.floor(mxp * (EVENTS[this._currentEvent].xpMult || 1));
                                        // XP magnet buff
                                        if (this._buffs.xp_magnet && this._buffs.xp_magnet.timer > 0) mxp *= 2;
                                        this.addXp(mxp, mob.x + mob.w / 2, mob.y + mob.h / 2);
                                        this.stats.mobsKilled++;
                                        this._onMobKilled();
                                        if (this._currentEvent) this._eventMobsKilled++;
                                        // Trader currency from kills
                                        this._traderCurrency += Math.max(1, Math.floor(mxp / 5));
                                        // === LOOT DROPS ===
                                        this._dropLoot(mob);
                                    }
                                }
                            }
                        }
                        // Boss hit detection
                        if (this.boss && this.boss.alive) {
                            const b = this.boss;
                            const hitWX = mp.x * TS + TS / 2, hitWY = mp.y * TS + TS / 2;
                            if (b instanceof MoonLord) {
                                const allParts = [b.head, b.lHand, b.rHand];
                                let hitAny = false;
                                for (const bp of allParts) {
                                    if (bp.hp <= 0) continue;
                                    if (Math.abs(bp.x - hitWX) < 20 && Math.abs(bp.y - hitWY) < 20) {
                                        if (b.takeDamage(10, this, hitWX, hitWY)) { if (this.hitstop <= 0) this.hitstop = 40; hitAny = true; }
                                        break;
                                    }
                                }
                                if (!hitAny && b.coreVuln) {
                                    const cby = b.cy + Math.sin(b.floatOff) * 8;
                                    if (Math.abs(b.cx - hitWX) < 18 && Math.abs(cby - hitWY) < 18) {
                                        if (b.takeDamage(10, this, hitWX, hitWY)) { if (this.hitstop <= 0) this.hitstop = 40; }
                                    }
                                }
                            } else {
                                const bx = Math.floor((b.x + b.w / 2) / TS), by = Math.floor((b.y + b.h / 2) / TS);
                                if (Math.abs(bx - mp.x) <= 2 && Math.abs(by - mp.y) <= 2) {
                                    if (b.takeDamage(10, this)) { if (this.hitstop <= 0) this.hitstop = 40; hitMobThisFrame = true; }
                                }
                                // Minion hit
                                if (b.minions) for (const mi of b.minions) {
                                    const mix = Math.floor((mi.x + mi.w / 2) / TS), miy = Math.floor((mi.y + mi.h / 2) / TS);
                                    if (Math.abs(mix - mp.x) <= 1 && Math.abs(miy - mp.y) <= 1 && mi.hitTimer <= 0) {
                                        mi.hp -= 10; mi.hitTimer = 200; sfx.mobHit();
                                        this.dmgNums.add(mi.x + mi.w / 2, mi.y - 5, 10, '#ff4444');
                                    }
                                }
                            }
                        }
                        const tile = this.w.gt(mp.x, mp.y);
                        if (tile !== T.AIR && !LIQUID.has(tile)) {
                            if (!p.mtgt || p.mtgt.x !== mp.x || p.mtgt.y !== mp.y) { p.mtgt = { x: mp.x, y: mp.y }; p.mt = 0; }
                            p.mt += dt;
                            if (Math.random() > 0.6) { const c2 = TC[tile] || '#888'; this.addPart(mp.x * TS + 8, mp.y * TS + 8, (Math.random() - 0.5) * 2, -Math.random() * 2, c2, 20); }
                            sfx.hit(0.8 + Math.random() * 0.4);
                            const pickSpeed = PICKAXE_TIERS[this.equipPick].speed;
                            let mtime = Math.floor(400 / pickSpeed);
                            if ([T.DBRICK, T.OBSIDIAN, T.ANCBRICK, T.MAGBRICK, T.CRYPTBRICK].includes(tile)) mtime = Math.floor(1800 / pickSpeed);
                            if ([T.DEEPSLATE, T.GRANITE, T.MARBLE, T.BASALT, T.HELLSTONE].includes(tile)) mtime = Math.floor(1000 / pickSpeed);
                            if ([T.DIAMOND, T.TITANIUM].includes(tile)) mtime = Math.floor(1200 / pickSpeed);
                            if (p.mt >= mtime) {
                                sfx.blockBreak(); this.addShake(2, 60);
                                for (let i = 0; i < 8; i++)this.addPart(mp.x * TS + 8, mp.y * TS + 8, (Math.random() - 0.5) * 3, -Math.random() * 3, TC[tile] || '#888', 40);
                                this.w.st(mp.x, mp.y, T.AIR);
                                // Unsettle nearby water when a block is broken
                                this.liq.unsettle(mp.x, mp.y);
                                // Spawn item drop on ground
                                this.droppedItems.push(new ItemDrop(mp.x * TS + 3, mp.y * TS + 3, tile, 1));
                                // XP for mining block — rarer blocks give more XP
                                {
                                    let bxp = 1;
                                    if ([T.IRON, T.COPPER].includes(tile)) bxp = 3;
                                    if ([T.GOLD, T.CRYSTAL].includes(tile)) bxp = 5;
                                    if ([T.DIAMOND, T.TITANIUM, T.HELLSTONE].includes(tile)) bxp = 10;
                                    if ([T.OBSIDIAN, T.ANCBRICK, T.MAGBRICK, T.CRYPTBRICK].includes(tile)) bxp = 7;
                                    this.addXp(bxp, mp.x * TS + 8, mp.y * TS + 8);
                                    this.stats.blocksMined++;
                                    this._onBlockMined();
                                }
                                this.updateHotbar(); p.mt = 0; p.mtgt = null; this.w.updateLight(mp.x, mp.y, 12);
                            }
                        }
                    }
                }
                if (this.mouse.r) {
                    const mp = this.mouseWorld(); const dx2 = mp.x - ptx, dy2 = mp.y - pty;
                    if (Math.sqrt(dx2 * dx2 + dy2 * dy2) < 7) {
                        const tile = this.w.gt(mp.x, mp.y);
                        if (tile === T.AIR || LIQUID.has(tile)) {
                            const it = p.inv[p.slot];
                            if (it && it.c > 0) {
                                const px1 = Math.floor(p.x / TS), py1 = Math.floor(p.y / TS), px2 = Math.floor((p.x + PW) / TS), py2 = Math.floor((p.y + PH) / TS);
                                if (mp.x < px1 || mp.x > px2 || mp.y < py1 || mp.y > py2 || !SOLID.has(it.t)) {
                                    this.w.st(mp.x, mp.y, it.t); it.c--; this.updateHotbar(); this.mouse.r = false;
                                    sfx.blockPlace(); this.w.updateLight(mp.x, mp.y, 12);
                                    // Unsettle nearby water when a block is placed
                                    this.liq.unsettle(mp.x, mp.y);
                                }
                            }
                        }
                    }
                }
                if (p.swingTimer > 0) {
                    const prog = 1 - p.swingTimer / 220;
                    // Eased swing with overshoot for snappy feel
                    const eased = prog < 0.5 ? 4 * prog * prog * prog : 1 - Math.pow(-2 * prog + 2, 3) / 2;
                    p.swingAngle = (-Math.PI / 3 + eased * Math.PI * 0.85) * p.swingDir;
                }
            }
        }

        this.spawnMobs(dt);
        for (const mob of this.mobs) {
            mob.update(dt, this.w, p);
            // Bestiary: record when mob is seen (within 400px)
            if (!mob.dead && !mob.def.isVillager && Math.abs(mob.x - p.x) < 400 && Math.abs(mob.y - p.y) < 400) {
                this._recordBestiary(mob.type, false);
            }
        }
        this.mobs = this.mobs.filter(m => !m.dead && Math.abs(m.x - p.x) < 1200 && Math.abs(m.y - p.y) < 900);
        // Boss update
        if (this.boss) {
            if (this.boss.alive) { this.boss.update(dt, p, this); } else {
                // XP for defeating boss
                const isMl = this.boss instanceof MoonLord;
                const bossXp = isMl ? 1000 : 500;
                const bx = isMl ? this.boss.cx : this.boss.x + this.boss.w / 2;
                const by = isMl ? this.boss.cy : this.boss.y + this.boss.h / 2;
                this.addXp(bossXp, bx, by);
                this.stats.bossesKilled++;
                this.boss = null;
            }
        }
        // Boss UI
        const bbw = document.getElementById('boss-bar-wrap');
        if (this.boss && this.boss.alive) {
            bbw.style.display = 'block';
            const isMl = this.boss instanceof MoonLord;
            const bName = isMl ? (this.boss.coreVuln ? '🌙 Moon Lord — Core Exposed!' : '🌙 Moon Lord') : (this.boss.phase === 2 ? '👁 Eye of Cthulhu — Enraged!' : '👁 Eye of Cthulhu');
            document.getElementById('boss-name').textContent = bName;
            document.getElementById('boss-bar-fill').style.width = Math.max(0, this.boss.hp / this.boss.maxHp * 100) + '%';
            if (isMl) { document.getElementById('boss-bar-fill').style.background = 'linear-gradient(90deg,#22aa88,#44ffcc)'; document.getElementById('boss-name').style.color = '#44ffcc'; }
            else { document.getElementById('boss-bar-fill').style.background = 'linear-gradient(90deg,#ff2244,#ff6644)'; document.getElementById('boss-name').style.color = '#ff4466'; }
            document.getElementById('boss-status').textContent = 'BOSS FIGHT!';
            document.getElementById('boss-status').style.color = isMl ? '#44ffcc' : '#ff4466';
        } else {
            bbw.style.display = 'none';
            document.getElementById('boss-status').textContent = '';
        }
        if (p.hp < p.prevHp && p.iframes <= 0) {
            p.iframes = 500; p.dmgFlash = 200;
            // Thorns accessory — reflect damage to nearby attackers
            if (this._hasAccessory('thorns')) {
                for (const mob of this.mobs) {
                    if (mob.def.passive || mob.def.isVillager || mob.dead) continue;
                    const tdx = Math.abs(mob.x - p.x), tdy = Math.abs(mob.y - p.y);
                    if (tdx < 40 && tdy < 40) {
                        const thornDmg = Math.ceil((p.prevHp - p.hp) * 0.4);
                        mob.hp -= thornDmg; mob.hitTimer = 200;
                        this.dmgNums.add(mob.x + mob.w / 2, mob.y - 8, `🌹${thornDmg}`, '#cc4444');
                        for (let i = 0; i < 4; i++) {
                            const a = Math.random() * Math.PI * 2;
                            this.addPart(mob.x + mob.w / 2, mob.y + mob.h / 2, Math.cos(a) * 3, Math.sin(a) * 3, '#cc4444', 18, 2);
                        }
                    }
                }
            }
        }
        p.prevHp = p.hp;
        // === DASH UPDATE ===
        this._updateDash(dt);
        // === PROJECTILES UPDATE ===
        this._updateProjectiles(dt);
        // === ACCESSORY NOTIFICATIONS ===
        if (this._accessoryNotifyTimer > 0) this._accessoryNotifyTimer -= dt;
        if (this._specialWeaponNotifyTimer > 0) this._specialWeaponNotifyTimer -= dt;
        if (this._bestiaryNotifyTimer > 0) this._bestiaryNotifyTimer -= dt;
        this._specialAttackCd = Math.max(0, this._specialAttackCd - dt);
        // === AUTO-CRAFTING: upgrade equipment when materials are available ===
        this._checkCrafting();
        // === ACHIEVEMENTS ===
        this._checkAchievements(dt);
        // === EVENT SYSTEM UPDATE ===
        this._updateEvents(dt);
        // === BUFF TIMERS ===
        this._updateBuffs(dt);
        // === TRADER PROXIMITY CHECK ===
        this._checkTraderProximity();
        this._checkVillagerProximity();
        this._updateQuestProgress();
        // === WORLD BOSS CHECK ===
        this._checkWorldBoss(dt, biome);
        this._updateWeather(effectiveDt);
        this._updateTides(effectiveDt);
        this._updateStorms(effectiveDt, biome);
        this._updateWorldMutation(effectiveDt);
        // === METEOR SHOWER EFFECT ===
        if (this._currentEvent === 'METEOR_SHOWER') {
            if (Math.random() > 0.95) {
                const mx = this.p.x + (Math.random() - 0.5) * 800;
                const my = this.p.y - 200 + Math.random() * 100;
                for (let i = 0; i < 8; i++) this.addPart(mx, my, (Math.random() - 0.5) * 5 + 2, Math.random() * 5 + 3, i % 2 ? '#ff8844' : '#ffcc44', 40, 3);
                this.addShake(2, 60);
            }
        }
        this.liq.tick(ptx, pty, 60);
        // Unsettle any water near recently modified blocks
        window._worldRef = this.w;
        this.dmgNums.update(dt);
        this.gore.update(dt, this.w);
        for (let i = this.parts.length - 1; i >= 0; i--) {
            const pt = this.parts[i]; pt.x += pt.vx; pt.y += pt.vy;
            pt.vy += 0.15; pt.vx *= 0.98;  // slight drag
            pt.l--; if (pt.l <= 0) this.parts.splice(i, 1);
        }
        this.cam.x += (p.x + PW / 2 - this.cam.x) * 0.08; this.cam.y += (p.y + PH / 2 - this.cam.y) * 0.08;
        const vw = this.cv.width / this.zoom, vh = this.cv.height / this.zoom;
        const surfY = this.w.heightMap[Math.max(0, Math.min(WW - 1, ptx))];
        const underground = pty > surfY + 10;
        const isNight = this.dayTime < 0.15 || this.dayTime > 0.85;
        this.ambient.update(dt, this.cam.x, this.cam.y, vw, vh, biome, underground, isNight, this._weather);

        // === UPDATE HP BAR ===
        const hpPct = Math.max(0, p.hp / p.maxHp * 100);
        const hpFill = document.getElementById('hp-bar-fill');
        const hpDmg = document.getElementById('hp-bar-dmg');
        const hpText = document.getElementById('hp-bar-text');
        const hpWrap = document.getElementById('hp-bar-wrap');
        if (hpFill) {
            hpFill.style.width = hpPct + '%';
            // Color based on HP percentage
            if (hpPct > 60) hpFill.style.background = 'linear-gradient(90deg, #44cc55, #66dd77, #55cc66)';
            else if (hpPct > 30) hpFill.style.background = 'linear-gradient(90deg, #ddaa22, #ffcc33, #eebb22)';
            else hpFill.style.background = 'linear-gradient(90deg, #dd3344, #ff5555, #ee4444)';
        }
        if (hpText) hpText.textContent = `${p.hp} / ${p.maxHp}`;
        // Damage ghost bar — shows where HP was before hit
        if (this._prevHp === undefined) this._prevHp = p.hp;
        if (this._dmgBarPct === undefined) this._dmgBarPct = 100;
        if (p.hp < this._prevHp) {
            // Took damage! Set ghost bar at old HP, trigger shake
            this._dmgBarPct = this._prevHp / p.maxHp * 100;
            if (hpWrap) { hpWrap.classList.remove('hp-bar-hurt'); void hpWrap.offsetWidth; hpWrap.classList.add('hp-bar-hurt'); }
        }
        this._prevHp = p.hp;
        // Ghost bar smoothly shrinks toward current HP
        this._dmgBarPct += (hpPct - this._dmgBarPct) * 0.05;
        if (hpDmg) hpDmg.style.width = Math.max(hpPct, this._dmgBarPct) + '%';
        // Critical health class
        if (hpWrap) {
            if (hpPct <= 25) hpWrap.classList.add('hp-bar-critical');
            else hpWrap.classList.remove('hp-bar-critical');
        }

        document.getElementById('pos').textContent = `${ptx}, ${pty}`;
        const depth = Math.floor(pty - surfY);
        if (depth > 10) {
            document.getElementById('biome').textContent = this.w.getUnderBiomeName(ptx);
            document.getElementById('depth').textContent = `↓${depth}`;
        }
        else {
            document.getElementById('biome').textContent = this.w.getBiomeName(ptx);
            document.getElementById('depth').textContent = depth > 0 ? `↓${depth}` : '~';
        }
        document.getElementById('time-info').textContent = this.timeEnabled ? (isNight ? '🌙' : '☀') : '⏸';
        // Mutation phase indicator
        const mutDisp = document.getElementById('mutation-display');
        if (mutDisp && this._mutation.phase > 0) {
            const phaseNames = ['', '⚡ Cracks Spreading', '🌱 Roots Growing', '🌳 Tree Rising', '🌳 World Tree', '✨ World Tree Bloomed'];
            mutDisp.style.display = 'block';
            mutDisp.textContent = phaseNames[this._mutation.phase] || '';
            mutDisp.style.color = this._mutation.phase >= 4 ? '#44ff88' : '#cc88ff';
            mutDisp.style.textShadow = `0 0 8px ${this._mutation.phase >= 4 ? '#44ff88' : '#9944dd'}`;
        } else if (mutDisp) { mutDisp.style.display = 'none'; }
        document.getElementById('mob-count').textContent = `👾${this.mobs.length}`;
        // Event display
        const evtDisp = document.getElementById('event-display');
        if (evtDisp) {
            if (this._currentEvent && EVENTS[this._currentEvent]) {
                const evt = EVENTS[this._currentEvent];
                const remain = Math.max(0, Math.ceil(this._eventTimer / 1000));
                evtDisp.style.display = 'block';
                evtDisp.textContent = `${evt.name} — ${remain}s`;
                evtDisp.style.color = evt.color;
                evtDisp.style.textShadow = `0 0 8px ${evt.color}`;
            } else {
                evtDisp.style.display = 'none';
            }
        }
        // Trader currency display
        const tcDisp = document.getElementById('trader-currency');
        if (tcDisp) tcDisp.textContent = `💰${this._traderCurrency}`;
        // Equipment HUD
        const eqDisp = document.getElementById('equip-display');
        if (eqDisp) {
            const wt = WEAPON_TIERS[this.equipWeapon];
            const at = ARMOR_TIERS[this.equipArmor];
            const pt = PICKAXE_TIERS[this.equipPick];
            let eqHtml = `<span style="color:${wt.color}">🗡${wt.name}</span> <span style="color:${at.color || '#888'}">🛡${at.name}</span> <span style="color:${pt.color}">⛏${pt.name}</span>`;
            if (this._specialWeapon) eqHtml += ` <span style="color:${this._specialWeapon.color}">⚔${this._specialWeapon.name}</span>`;
            eqDisp.innerHTML = eqHtml;
        }
        // Craft notification
        const craftNotif = document.getElementById('craft-notify');
        if (craftNotif) {
            if (this._craftNotifyTimer > 0) {
                craftNotif.style.display = 'block';
                craftNotif.style.opacity = Math.min(1, this._craftNotifyTimer / 500);
                craftNotif.textContent = this._craftNotify;
            } else {
                craftNotif.style.display = 'none';
            }
        }
        // Achievement popup
        const achPopup = document.getElementById('ach-popup');
        if (achPopup) {
            if (this._achDisplayTimer > 0) {
                this._achDisplayTimer -= dt;
                achPopup.style.display = 'block';
                const ach = this._achQueue[0];
                if (ach) achPopup.textContent = `🏆 ${ach.name}: ${ach.desc}`;
                achPopup.style.opacity = this._achDisplayTimer > 2000 ? Math.min(1, (3000 - this._achDisplayTimer) / 500) : Math.min(1, this._achDisplayTimer / 500);
            } else {
                achPopup.style.display = 'none';
                if (this._achQueue.length > 0) this._achQueue.shift();
            }
        }

        // === XP / STATS UPDATE ===
        // Distance XP: 1 XP per 10 tiles walked (silent, no orbs)
        const dxW = Math.abs(p.x - this._lastPx), dyW = Math.abs(p.y - this._lastPy);
        const distTiles = Math.sqrt(dxW * dxW + dyW * dyW) / TS;
        this.stats.distWalked += distTiles;
        if (distTiles > 0.5) { this.xp += Math.floor(distTiles / 10) || 0; }
        this._lastPx = p.x; this._lastPy = p.y;
        // Max depth tracking (silent)
        if (depth > 0 && depth > this.stats.maxDepth) { this.stats.maxDepth = depth; this.xp += 1; }
        // Update XP orbs
        for (let i = this.xpOrbs.length - 1; i >= 0; i--) {
            const orb = this.xpOrbs[i];
            orb.update(dt, p.x + PW / 2, p.y + PH / 2);
            if (orb.collected) {
                this._xpFlash = 300;
                this._xpPulse = 1.4;
                for (let j = 0; j < 3; j++) {
                    const a2 = Math.random() * Math.PI * 2;
                    this.addPart(p.x + PW / 2, p.y + PH / 2, Math.cos(a2) * 2, Math.sin(a2) * 2, orb.col, 12, 2);
                }
                this.xpOrbs.splice(i, 1);
            } else if (orb.life <= 0) {
                this.xpOrbs.splice(i, 1);
            }
        }
        // Update dropped items
        for (let i = this.droppedItems.length - 1; i >= 0; i--) {
            const item = this.droppedItems[i];
            // Magnet accessory — 3x magnet range
            if (this._hasAccessory('magnet')) { item.magnetDist = 120; item.magnetSpeed = 24; }
            item.update(dt, p.x + PW / 2, p.y + PH / 2, this.w);
            if (item.collected) {
                // Try add to inventory
                let added = false;
                for (let inv of p.inv) {
                    if (inv && inv.t === item.t) { inv.c += item.c; added = true; break; }
                }
                if (!added) {
                    for (let si = 0; si < p.inv.length; si++) {
                        if (!p.inv[si] || p.inv[si].c <= 0) { p.inv[si] = { t: item.t, c: item.c }; added = true; break; }
                    }
                }
                if (added) {
                    sfx.pickup();
                    const itemCol = TC[item.t] || '#fff';
                    const tName = Object.entries(T).find(([, v]) => v === item.t)?.[0] || '???';
                    // Floating item name
                    const label = item.c > 1 ? `+${item.c} ${tName}` : `+${tName}`;
                    this.dmgNums.add(p.x + PW / 2, p.y - 8, label, itemCol);
                    // Pickup sparkle particles — colored by item
                    for (let j = 0; j < 6; j++) {
                        const a2 = Math.random() * Math.PI * 2;
                        const spd = 1.5 + Math.random() * 2;
                        this.addPart(p.x + PW / 2, p.y + PH / 2,
                            Math.cos(a2) * spd, Math.sin(a2) * spd - 1,
                            j % 2 === 0 ? itemCol : '#fff',
                            20 + Math.random() * 10, 1.5 + Math.random());
                    }
                    this.updateHotbar();
                } else {
                    // Inventory full — drop item back (don't collect)
                    item.collected = false;
                    item.magnetDist = 0; // stop trying to collect
                    item.life = item.elapsed + 10000; // extra 10s
                }
                if (added) this.droppedItems.splice(i, 1);
            }
        }
        // HUD pulse decay
        if (this._xpFlash > 0) this._xpFlash -= dt;
        this._xpPulse += (1 - this._xpPulse) * 0.08;
        // Update stats HUD
        const sBlocks = document.getElementById('stat-blocks');
        const sKills = document.getElementById('stat-kills');
        const sScore = document.getElementById('stat-score');
        const xpDisp = document.getElementById('xp-display');
        if (sBlocks) sBlocks.textContent = this.stats.blocksMined;
        if (sKills) sKills.textContent = this.stats.mobsKilled;
        if (sScore) sScore.textContent = this.xp;
        if (xpDisp) {
            // Pulse effect on XP display
            const flashAlpha = Math.max(0, this._xpFlash / 300);
            xpDisp.style.color = flashAlpha > 0.1 ? `rgb(${Math.floor(68 + 187 * flashAlpha)},${Math.floor(255)},${Math.floor(100 + 100 * flashAlpha)})` : '#44ff88';
            xpDisp.style.textShadow = flashAlpha > 0.1 ? `0 0 ${6 + flashAlpha * 8}px rgba(100,255,150,${flashAlpha * 0.8})` : '0 0 4px rgba(68,255,136,0.3)';
            xpDisp.style.transform = `scale(${this._xpPulse})`;
        }
        // Save best XP periodically (every 5 seconds)
        this._xpSaveTimer += dt;
        if (this._xpSaveTimer > 5000) {
            this._xpSaveTimer = 0;
            if (this.xp > this.bestXp) {
                this.bestXp = this.xp;
                try { localStorage.setItem('terraria_best', String(this.xp)); } catch { }
                // Submit to leaderboard API
                if (this.onScoreUpdate) this.onScoreUpdate(this.xp);
            }
        }
    }

    // === LOOT DROP SYSTEM ===
    _dropLoot(mob) {
        const drops = LOOT_TABLE[mob.type];
        if (!drops) return;
        for (const drop of drops) {
            if (Math.random() > drop.chance) continue;
            const count = drop.min + Math.floor(Math.random() * (drop.max - drop.min + 1));
            // Spawn item drop on ground
            const dropX = mob.x + mob.w / 2 - 5;
            const dropY = mob.y + mob.h / 2 - 5;
            this.droppedItems.push(new ItemDrop(dropX, dropY, drop.t, count));
            const tName = Object.entries(T).find(([, v]) => v === drop.t)?.[0] || '???';
            this.dmgNums.add(mob.x + mob.w / 2, mob.y - 16, `+${count} ${tName}`, TC[drop.t] || '#fff');
        }
    }

    // === AUTO-CRAFTING SYSTEM ===
    _checkCrafting() {
        // Check weapon upgrades
        const nextW = this.equipWeapon + 1;
        if (nextW < WEAPON_TIERS.length) {
            const tier = WEAPON_TIERS[nextW];
            if (tier.mat !== null) {
                const invSlot = this.p.inv.find(s => s && s.t === tier.mat && s.c >= tier.matCount);
                if (invSlot) {
                    invSlot.c -= tier.matCount;
                    this.equipWeapon = nextW;
                    this._craftNotify = `⚔ Crafted ${tier.name}!`;
                    this._craftNotifyTimer = 3000;
                    this.addScreenFlash('rgba(255,220,100,0.3)', 300);
                    this.addShake(4, 150);
                    this.dmgNums.add(this.p.x + PW / 2, this.p.y - 30, `⚔ ${tier.name}`, tier.color);
                    sfx.blockBreak();
                    this.updateHotbar();
                }
            }
        }
        // Check armor upgrades
        const nextA = this.equipArmor + 1;
        if (nextA < ARMOR_TIERS.length) {
            const tier = ARMOR_TIERS[nextA];
            if (tier.mat !== null) {
                const invSlot = this.p.inv.find(s => s && s.t === tier.mat && s.c >= tier.matCount);
                if (invSlot) {
                    invSlot.c -= tier.matCount;
                    this.equipArmor = nextA;
                    this._craftNotify = `🛡 Crafted ${tier.name}!`;
                    this._craftNotifyTimer = 3000;
                    this.addScreenFlash('rgba(100,200,255,0.3)', 300);
                    this.dmgNums.add(this.p.x + PW / 2, this.p.y - 40, `🛡 ${tier.name}`, tier.color);
                    sfx.blockBreak();
                    this.updateHotbar();
                }
            }
        }
        // Check pickaxe upgrades
        const nextP = this.equipPick + 1;
        if (nextP < PICKAXE_TIERS.length) {
            const tier = PICKAXE_TIERS[nextP];
            if (tier.mat !== null) {
                const invSlot = this.p.inv.find(s => s && s.t === tier.mat && s.c >= tier.matCount);
                if (invSlot) {
                    invSlot.c -= tier.matCount;
                    this.equipPick = nextP;
                    this._craftNotify = `⛏ Crafted ${tier.name}!`;
                    this._craftNotifyTimer = 3000;
                    this.addScreenFlash('rgba(180,180,180,0.3)', 300);
                    this.dmgNums.add(this.p.x + PW / 2, this.p.y - 50, `⛏ ${tier.name}`, tier.color);
                    sfx.blockBreak();
                    this.updateHotbar();
                }
            }
        }
    }

    // === ACHIEVEMENT SYSTEM ===
    _checkAchievements(dt) {
        for (const ach of ACHIEVEMENTS) {
            if (this._achievements[ach.id]) continue;
            if (ach.check(this)) {
                this._achievements[ach.id] = true;
                this._achQueue.push(ach);
                if (this._achDisplayTimer <= 0) this._achDisplayTimer = 3000;
                this.addScreenFlash('rgba(255,215,0,0.3)', 400);
                this.addShake(3, 200);
                this.addXp(25, this.p.x + PW / 2, this.p.y);
            }
        }
    }

    // === EVENT SYSTEM ===
    _updateEvents(dt) {
        if (this._currentEvent) {
            this._eventTimer -= dt;
            const evt = EVENTS[this._currentEvent];
            // Wave system
            if (evt && evt.waves) {
                this._eventWaveTimer += dt;
                const wave = evt.waves[this._eventWave];
                if (wave && this._eventWaveTimer >= wave.duration && this._eventWave < evt.waves.length - 1) {
                    // Advance to next wave
                    this._eventWave++;
                    this._eventWaveTimer = 0;
                    const nextWave = evt.waves[this._eventWave];
                    if (nextWave && nextWave.announce) {
                        this._eventNotify = nextWave.announce;
                        this._eventNotifyTimer = 4000;
                        this.addScreenFlash(evt.color + '44', 400);
                        this.addShake(4, 200);
                    }
                }
            }
            if (this._eventTimer <= 0) {
                // Event completed — give rewards!
                this._eventCompleted = true;
                this._giveEventReward(this._currentEvent);
                this._currentEvent = null;
                this._eventCooldown = 240000 + Math.random() * 360000; // 4-10 min cooldown
                this._eventNotify = '🎉 Event survived! Rewards dropped!';
                this._eventNotifyTimer = 4000;
                this._eventWave = 0;
                this._eventWaveTimer = 0;
                this._eventMobsKilled = 0;
            }
        } else {
            this._eventCooldown -= dt;
            if (this._eventCooldown <= 0) {
                const evtKeys = Object.keys(EVENTS);
                this._currentEvent = evtKeys[Math.floor(Math.random() * evtKeys.length)];
                const evt = EVENTS[this._currentEvent];
                this._eventTimer = evt.duration;
                this._eventNotify = evt.name;
                this._eventNotifyTimer = 5000;
                this._eventWave = 0;
                this._eventWaveTimer = 0;
                this._eventMobsKilled = 0;
                this._eventCompleted = false;
                this.addScreenFlash(evt.color + '55', 600);
                this.addShake(5, 300);
            }
        }
        // Notification timer
        if (this._eventNotifyTimer > 0) this._eventNotifyTimer -= dt;
        const evNotif = document.getElementById('event-notify');
        if (evNotif) {
            if (this._eventNotifyTimer > 0) {
                evNotif.style.display = 'block';
                evNotif.textContent = this._eventNotify;
                evNotif.style.opacity = Math.min(1, this._eventNotifyTimer / 1000);
                if (this._currentEvent && EVENTS[this._currentEvent]) {
                    evNotif.style.color = EVENTS[this._currentEvent].color;
                    evNotif.style.textShadow = `0 0 12px ${EVENTS[this._currentEvent].color}`;
                }
            } else evNotif.style.display = 'none';
        }
    }

    _giveEventReward(eventKey) {
        const rewards = EVENT_REWARDS[eventKey];
        if (!rewards) return;
        for (const r of rewards) {
            const count = r.min + Math.floor(Math.random() * (r.max - r.min + 1));
            const dropX = this.p.x + PW / 2 - 5 + (Math.random() - 0.5) * 30;
            const dropY = this.p.y - 20;
            this.droppedItems.push(new ItemDrop(dropX, dropY, r.t, count));
        }
        this.addScreenFlash('#ffdd44aa', 500);
        this.addShake(3, 200);
        this.addXp(50 + this._eventMobsKilled * 5, this.p.x + PW / 2, this.p.y);
    }

    // === BUFFS ===
    _updateBuffs(dt) {
        const p = this.p;
        for (const key in this._buffs) {
            this._buffs[key].timer -= dt;
            if (this._buffs[key].timer <= 0) {
                delete this._buffs[key];
                this.dmgNums.add(p.x + PW / 2, p.y - 15, `${key} ended`, '#888');
            }
        }
        // Apply speed buff
        if (this._buffs.speed) { /* Applied in movement */ }
        // Fire aura — burn nearby enemies
        if (this._buffs.fire_aura) {
            for (const mob of this.mobs) {
                if (mob.def.passive || mob.def.isVillager) continue;
                const dx = mob.x - p.x, dy = mob.y - p.y;
                if (Math.sqrt(dx * dx + dy * dy) < 80) {
                    if (Math.random() > 0.95) {
                        mob.hp -= 3; mob.hitTimer = 150;
                        this.addPart(mob.x + mob.w / 2, mob.y + mob.h / 2, (Math.random() - .5) * 3, -Math.random() * 3, '#ff6600', 15, 2);
                    }
                }
            }
        }
    }

    _applyBuff(effect, value) {
        const p = this.p;
        switch (effect) {
            case 'heal': p.hp = Math.min(p.maxHp, p.hp + value); this.dmgNums.add(p.x + PW / 2, p.y - 10, `+${value} HP`, '#44ff44'); break;
            case 'speed': this._buffs.speed = { timer: value, value: 1.5 }; this.dmgNums.add(p.x + PW / 2, p.y - 15, '⚡ Speed!', '#ffdd44'); break;
            case 'shield': this._buffs.shield = { timer: value }; this.dmgNums.add(p.x + PW / 2, p.y - 15, '🛡 Shield!', '#44aaff'); break;
            case 'fire_aura': this._buffs.fire_aura = { timer: value }; this.dmgNums.add(p.x + PW / 2, p.y - 15, '🔥 Fire!', '#ff6600'); break;
            case 'xp_magnet': this._buffs.xp_magnet = { timer: value }; this.dmgNums.add(p.x + PW / 2, p.y - 15, '💎 XP x2!', '#88ff88'); break;
            case 'rage': this._buffs.rage = { timer: value }; this.dmgNums.add(p.x + PW / 2, p.y - 15, '🗡 Rage!', '#ff4444'); break;
        }
        this.addScreenFlash('rgba(255,255,100,0.2)', 200);
    }

    // === TRADER INTERACTION ===
    _checkTraderProximity() {
        this._traderTarget = null;
        for (const mob of this.mobs) {
            if (mob.def.isTrader && !mob.dead) {
                const dx = Math.abs(mob.x - this.p.x), dy = Math.abs(mob.y - this.p.y);
                if (dx < 40 && dy < 40) { this._traderTarget = mob; break; }
            }
        }
        // Show/hide trade prompt (only if no villager prompt active)
        const tradePrompt = document.getElementById('trade-prompt');
        if (tradePrompt) {
            if (this._traderTarget && !this._traderShopOpen) {
                tradePrompt.style.display = 'block';
                tradePrompt.textContent = `[E] Торговать (💰${this._traderCurrency})`;
            } else if (!this._villagerTarget) {
                tradePrompt.style.display = 'none';
            }
        }
        // Update shop panel
        this._updateTraderShop();
    }

    _openTraderShop() {
        if (!this._traderTarget) return;
        this._traderShopOpen = true;
        const panel = document.getElementById('trader-shop');
        if (!panel) return;
        panel.style.display = 'block';
        panel.innerHTML = '';
        const title = document.createElement('div');
        title.style.cssText = 'font-size:14px;font-weight:bold;color:#ffd700;margin-bottom:6px;text-align:center;';
        title.textContent = `🏪 Trader — 💰${this._traderCurrency}`;
        panel.appendChild(title);
        for (let i = 0; i < TRADER_ITEMS.length; i++) {
            const item = TRADER_ITEMS[i];
            const row = document.createElement('div');
            row.style.cssText = `display:flex;align-items:center;gap:6px;padding:4px 6px;margin:2px 0;background:rgba(0,0,0,0.4);border-radius:4px;cursor:pointer;border:1px solid ${this._traderCurrency >= item.cost ? item.color + '66' : '#33333366'};transition:background 0.15s;`;
            row.onmouseenter = () => row.style.background = 'rgba(255,255,255,0.1)';
            row.onmouseleave = () => row.style.background = 'rgba(0,0,0,0.4)';
            const canAfford = this._traderCurrency >= item.cost;
            row.innerHTML = `<span style="font-size:16px">${item.icon}</span><div style="flex:1"><div style="color:${canAfford ? item.color : '#666'};font-size:11px;font-weight:bold">${item.name}</div><div style="color:#999;font-size:9px">${item.desc}</div></div><div style="color:${canAfford ? '#ffd700' : '#555'};font-size:11px;font-weight:bold">💰${item.cost}</div>`;
            if (canAfford) {
                row.onclick = () => { this._buyItem(i); };
            } else {
                row.style.opacity = '0.5';
            }
            panel.appendChild(row);
        }
        const closeBtn = document.createElement('div');
        closeBtn.style.cssText = 'text-align:center;color:#aaa;font-size:10px;margin-top:6px;cursor:pointer;';
        closeBtn.textContent = '[E] or click to close';
        closeBtn.onclick = () => this._closeTraderShop();
        panel.appendChild(closeBtn);
    }

    _closeTraderShop() {
        this._traderShopOpen = false;
        const panel = document.getElementById('trader-shop');
        if (panel) panel.style.display = 'none';
    }

    _buyItem(index) {
        const item = TRADER_ITEMS[index];
        if (this._traderCurrency < item.cost) return;
        this._traderCurrency -= item.cost;
        this._applyBuff(item.effect, item.value);
        sfx.pickup();
        this.addScreenFlash(item.color + '33', 200);
        // Refresh shop
        this._openTraderShop();
    }

    _updateTraderShop() {
        // Auto-close if moved away
        if (this._traderShopOpen && !this._traderTarget) this._closeTraderShop();
    }

    // === VILLAGER INTERACTION & QUEST SYSTEM ===
    _checkVillagerProximity() {
        this._villagerTarget = null;
        for (const mob of this.mobs) {
            if (mob.def.isVillager && !mob.def.isTrader && !mob.dead) {
                const dx = Math.abs(mob.x - this.p.x), dy = Math.abs(mob.y - this.p.y);
                if (dx < 35 && dy < 35) { this._villagerTarget = mob; break; }
            }
        }
        // Show interact prompt
        const prompt = document.getElementById('trade-prompt');
        if (prompt && this._villagerTarget && !this._traderTarget) {
            const prof = VILLAGER_PROFESSIONS[this._villagerTarget.ai.profession];
            const profName = prof ? prof.name : 'Житель';
            if (this._activeQuest && this._activeQuest.villager === this._villagerTarget) {
                prompt.style.display = 'block';
                prompt.textContent = `[E] Сдать квест — ${profName}`;
            } else if (!this._activeQuest) {
                prompt.style.display = 'block';
                prompt.textContent = `[E] Поговорить — ${profName}`;
            } else {
                prompt.style.display = 'none';
            }
        }
        // Quest cooldown
        if (this._questCooldown > 0) this._questCooldown -= 16;
        if (this._questNotifyTimer > 0) this._questNotifyTimer -= 16;
    }

    _interactVillager(villager) {
        if (!villager || villager.dead) return;
        // If we have an active quest targeted at this villager — try to complete
        if (this._activeQuest && this._activeQuest.villager === villager) {
            this._tryCompleteQuest();
            return;
        }
        // If quest cooldown active
        if (this._questCooldown > 0) {
            villager.ai.speechText = 'Позже приходи!';
            villager.ai.speechShow = 2000;
            return;
        }
        // Give a new quest
        if (!this._activeQuest) {
            this._giveQuest(villager);
        }
    }

    _giveQuest(villager) {
        const templates = VILLAGE_QUESTS;
        const tmpl = templates[Math.floor(Math.random() * templates.length)];
        const quest = { type: tmpl.type, reward: tmpl.reward, villager: villager, icon: tmpl.icon };

        if (tmpl.type === 'kill') {
            const n = tmpl.targets[Math.floor(Math.random() * tmpl.targets.length)];
            quest.desc = `Убей ${n} монстров`;
            quest.target = n;
            quest.progress = 0;
        } else if (tmpl.type === 'gather') {
            const item = tmpl.items[Math.floor(Math.random() * tmpl.items.length)];
            quest.desc = `Принеси ${item.n} ${item.t}`;
            quest.target = item.n;
            quest.tileType = T[item.t];
            quest.progress = 0;
        } else if (tmpl.type === 'depth') {
            const n = tmpl.targets[Math.floor(Math.random() * tmpl.targets.length)];
            quest.desc = `Достигни глубины ${n}`;
            quest.target = n;
            quest.progress = 0;
        } else if (tmpl.type === 'mine') {
            const n = tmpl.targets[Math.floor(Math.random() * tmpl.targets.length)];
            quest.desc = `Добудь ${n} блоков`;
            quest.target = n;
            quest.progress = 0;
        } else if (tmpl.type === 'survive_night') {
            quest.desc = 'Переживи ночь';
            quest.target = 1;
            quest.progress = 0;
        }

        this._activeQuest = quest;
        villager.ai.speechText = quest.icon + ' ' + quest.desc;
        villager.ai.speechShow = 3000;
        this._questNotify = quest.icon + ' Квест: ' + quest.desc;
        this._questNotifyTimer = 4000;
        sfx.pickup();
    }

    _updateQuestProgress() {
        if (!this._activeQuest) return;
        const q = this._activeQuest;
        if (q.type === 'depth') {
            q.progress = Math.max(q.progress, this.stats.maxDepth);
        }
        if (q.type === 'survive_night') {
            if (this.dayTime > 0.25 && this.dayTime < 0.3 && q.progress === 0) {
                q.progress = 1; // survived the night
            }
        }
    }

    _onMobKilled() {
        if (this._activeQuest && this._activeQuest.type === 'kill') {
            this._activeQuest.progress++;
        }
    }

    _onBlockMined() {
        if (this._activeQuest && this._activeQuest.type === 'mine') {
            this._activeQuest.progress++;
        }
    }

    _tryCompleteQuest() {
        if (!this._activeQuest) return;
        const q = this._activeQuest;
        let complete = false;

        if (q.type === 'kill' || q.type === 'mine') {
            complete = q.progress >= q.target;
        } else if (q.type === 'depth') {
            complete = this.stats.maxDepth >= q.target;
        } else if (q.type === 'survive_night') {
            complete = q.progress >= 1;
        } else if (q.type === 'gather') {
            // Check if player has enough items
            let count = 0;
            for (const slot of this.p.inv) {
                if (slot && slot.t === q.tileType) count += slot.c;
            }
            if (count >= q.target) {
                // Remove items from inventory
                let remaining = q.target;
                for (let i = 0; i < this.p.inv.length && remaining > 0; i++) {
                    if (this.p.inv[i] && this.p.inv[i].t === q.tileType) {
                        const take = Math.min(remaining, this.p.inv[i].c);
                        this.p.inv[i].c -= take;
                        remaining -= take;
                        if (this.p.inv[i].c <= 0) this.p.inv[i] = null;
                    }
                }
                complete = true;
            }
        }

        if (complete) {
            // Reward!
            this._traderCurrency += q.reward.coins;
            this.addXp(q.reward.xp, q.villager.x, q.villager.y);
            q.villager.ai.speechText = 'Спасибо! 💰+' + q.reward.coins;
            q.villager.ai.speechShow = 3000;
            this._questNotify = '✅ Квест выполнен! +' + q.reward.coins + '💰 +' + q.reward.xp + 'XP';
            this._questNotifyTimer = 4000;
            sfx.pickup();
            this.addScreenFlash('#ffd70033', 300);
            this._activeQuest = null;
            this._questCooldown = 30000; // 30s before next quest
        } else {
            // Not complete yet
            if (q.type === 'gather') {
                q.villager.ai.speechText = 'Недостаточно ресурсов!';
            } else {
                q.villager.ai.speechText = `Прогресс: ${q.progress}/${q.target}`;
            }
            q.villager.ai.speechShow = 2000;
        }
    }

    // === WORLD BOSS SPAWNING ===
    _checkWorldBoss(dt, biome) {
        if (this.boss) return; // already fighting a boss
        this._worldBossTimer += dt;
        if (this._worldBossTimer < 180000) return; // every 3 min check
        this._worldBossTimer = 0;
        for (const [key, def] of Object.entries(WORLD_BOSSES)) {
            if (this._worldBossSpawned[key]) continue;
            if (def.biomes.includes(biome) && Math.random() > 0.5) {
                // Spawn as EyeOfCthulhu-style boss with custom stats
                this.boss = new EyeOfCthulhu(this.p.x, this.p.y - 100);
                this.boss.maxHp = def.hp;
                this.boss.hp = def.hp;
                this.boss._worldBossKey = key;
                this._worldBossSpawned[key] = true;
                this._eventNotify = def.name + ' has appeared!';
                this._eventNotifyTimer = 5000;
                this.addScreenFlash('rgba(255,100,100,0.3)', 500);
                this.addShake(6, 300);
                break;
            }
        }
    }

    _updateWeather(dt) {
        const w = this._weather;
        w.timer -= dt;
        // Smooth interpolation of weather values
        w.intensity += (w.targetIntensity - w.intensity) * 0.002 * (dt / 16);
        w.cloudDensity += (w.targetCloudDensity - w.cloudDensity) * 0.003 * (dt / 16);
        w.windSpeed += (w.targetWind - w.windSpeed) * 0.004 * (dt / 16);
        // State transitions
        if (w.timer <= 0) {
            switch (w.state) {
                case 'clear':
                    if (Math.random() < 0.5) {
                        w.state = 'cloudy'; w.targetCloudDensity = 0.3 + Math.random() * 0.3;
                        w.targetIntensity = 0; w.targetWind = Math.random() * 0.3;
                        w.timer = 20000 + Math.random() * 40000;
                    } else {
                        w.timer = 30000 + Math.random() * 60000; // stay clear
                    }
                    break;
                case 'cloudy':
                    if (Math.random() < 0.45) {
                        w.state = 'rain'; w.targetCloudDensity = 0.6 + Math.random() * 0.3;
                        w.targetIntensity = 0.4 + Math.random() * 0.4; w.targetWind = 0.2 + Math.random() * 0.5;
                        w.timer = 25000 + Math.random() * 45000;
                    } else {
                        w.state = 'clearing'; w.targetCloudDensity = 0; w.targetIntensity = 0; w.targetWind = 0;
                        w.timer = 15000 + Math.random() * 20000;
                    }
                    break;
                case 'rain':
                    if (Math.random() < 0.3) {
                        w.state = 'storm'; w.targetCloudDensity = 0.85 + Math.random() * 0.15;
                        w.targetIntensity = 0.8 + Math.random() * 0.2; w.targetWind = 0.5 + Math.random() * 0.5;
                        w.timer = 15000 + Math.random() * 30000;
                    } else {
                        w.state = 'clearing'; w.targetCloudDensity = 0.1; w.targetIntensity = 0; w.targetWind = 0;
                        w.timer = 15000 + Math.random() * 25000;
                    }
                    break;
                case 'storm':
                    w.state = 'clearing'; w.targetCloudDensity = 0.15; w.targetIntensity = 0; w.targetWind = 0.1;
                    w.timer = 20000 + Math.random() * 30000;
                    break;
                case 'clearing':
                    w.state = 'clear'; w.targetCloudDensity = 0; w.targetIntensity = 0; w.targetWind = 0;
                    w.timer = 40000 + Math.random() * 80000;
                    break;
            }
        }
        // Update rain drops (screen-space, generated in render)
        for (let i = w.rainDrops.length - 1; i >= 0; i--) {
            const d = w.rainDrops[i];
            d.x += (w.windSpeed * 2 + 0.5) * dt * 0.06;
            d.y += (3 + w.intensity * 4) * dt * 0.06;
            d.l -= dt;
            if (d.l <= 0) w.rainDrops.splice(i, 1);
        }
        // Shooting star timer
        if (this.dayTime < 0.15 || this.dayTime > 0.82) {
            this._shootingStarTimer -= dt;
            if (this._shootingStarTimer <= 0 && !this._shootingStar) {
                this._shootingStarTimer = 25000 + Math.random() * 50000;
                this._shootingStar = {
                    sx: Math.random(), sy: Math.random() * 0.35,
                    progress: 0, duration: 600 + Math.random() * 400,
                    angle: 0.3 + Math.random() * 0.5,
                    len: 0.05 + Math.random() * 0.08,
                    color: Math.random() > 0.7 ? '#aaccff' : '#fff'
                };
            }
            if (this._shootingStar) {
                this._shootingStar.progress += dt;
                if (this._shootingStar.progress >= this._shootingStar.duration) this._shootingStar = null;
            }
        }
    }
    _updateTides(dt) {
        this._tidalPhase = (this._tidalPhase + dt * 0.00002) % 1;
        this._tidalTimer += dt;
        if (this._tidalTimer < 3000) return; // update every 3s
        this._tidalTimer = 0;
        const tideLevel = Math.sin(this._tidalPhase * Math.PI * 2) * 0.5 + 0.5; // 0=low, 1=high
        const ptx = Math.floor(this.p.x / TS);
        const range = 80;
        for (let x = Math.max(0, ptx - range); x < Math.min(WW, ptx + range); x++) {
            if (this.w.biomeMap[x] !== 28) continue;
            const sy = Math.floor(this.w.heightMap[x]);
            const tideDepth = Math.floor(tideLevel * 4); // 0-4 tiles of water
            for (let dy = 0; dy <= 4; dy++) {
                const y = sy + dy;
                if (y >= WH || y < 0) continue;
                const t = this.w.gt(x, y);
                if (dy < tideDepth && t === T.AIR) {
                    this.w.st(x, y, T.WATER); this.w.sll(x, y, Math.min(1, tideLevel));
                } else if (dy >= tideDepth && t === T.WATER) {
                    this.w.st(x, y, T.AIR); this.w.sll(x, y, 0);
                }
            }
        }
    }

    _updateStorms(dt, biome) {
        this._lightningFlash = Math.max(0, this._lightningFlash - dt);
        // Remove expired bolts
        this._lightningBolts = this._lightningBolts.filter(b => { b.timer -= dt; return b.timer > 0; });
        // Only spawn lightning in Storm Peaks
        const bw = this.smoothBiomeWeights || {};
        const stormWeight = bw[29] || 0;
        if (stormWeight < 0.3) return;
        this._stormTimer += dt;
        const stormInterval = 4000 + Math.random() * 6000; // 4-10s between strikes
        if (this._stormTimer < stormInterval) return;
        this._stormTimer = 0;
        // Lightning strike near player
        const ptx = Math.floor(this.p.x / TS);
        const strikeX = ptx + Math.floor((Math.random() - 0.5) * 40);
        if (strikeX < 5 || strikeX >= WW - 5) return;
        const strikeSY = Math.floor(this.w.heightMap[Math.max(0, Math.min(WW - 1, strikeX))]);
        this._lightningBolts.push({ x: strikeX * TS + TS / 2, y: 0, bx: strikeX * TS + TS / 2, by: strikeSY * TS, timer: 400 });
        this._lightningFlash = 200;
        this.addShake(3, 150);
        // Lightning can charge nearby crystals (visual only — adds light)
        // Damage player if very close
        const px = this.p.x + PW / 2, py = this.p.y + PH / 2;
        const ldist = Math.sqrt((px - strikeX * TS) ** 2 + (py - strikeSY * TS) ** 2);
        if (ldist < TS * 2) {
            const ldmg = 8 + Math.floor(Math.random() * 7);
            this.p.hp = Math.max(0, this.p.hp - ldmg);
            this.p.dmgFlash = 300;
            this.dmgNums.add(this.p.x + PW / 2, this.p.y - 10, `⚡${ldmg}`, '#88ccff');
            sfx.hurt();
        }
        // Particles from strike point
        for (let i = 0; i < 8; i++) {
            this.addPart(strikeX * TS + 8, strikeSY * TS,
                (Math.random() - 0.5) * 4, -Math.random() * 3,
                Math.random() > 0.5 ? '#88ccff' : '#fff', 400 + Math.random() * 300, 2);
        }
    }

    // === WORLD MUTATION SYSTEM ===
    _updateWorldMutation(dt) {
        const m = this._mutation;
        m.timer += dt;
        m.phaseTimer += dt;
        m.glowPulse = Math.sin(m.timer / 500) * 0.5 + 0.5;
        m.particleTimer += dt;

        // Phase transitions based on total game time
        const PHASE_TIMES = [0, 30000, 60000, 90000, 120000, 150000]; // 0, 30s, 60s, 90s, 120s, 150s

        // Advance phase
        if (m.phase < 5) {
            const nextPhaseTime = PHASE_TIMES[m.phase + 1];
            if (nextPhaseTime && m.timer >= nextPhaseTime) {
                m.phase++;
                m.phaseTimer = 0;
                this._onMutationPhaseChange(m.phase);
            }
        }

        switch (m.phase) {
            case 1: this._updateMutationCracks(dt); break;
            case 2: this._updateMutationRoots(dt); break;
            case 3: this._updateMutationTreeGrow(dt); break;
            case 4: this._updateMutationTreeComplete(dt); break;
            case 5: this._updateMutationBloomed(dt); break;
        }
    }

    _onMutationPhaseChange(phase) {
        const m = this._mutation;
        switch (phase) {
            case 1: {
                // "First Cracks" — announce and spawn initial cracks
                this._eventNotify = '⚡ The world trembles... Something stirs beneath.';
                this._eventNotifyTimer = 6000;
                this.addScreenFlash('rgba(150,50,200,0.3)', 600);
                this.addShake(6, 400);
                sfx.bossRoar();
                // Seed initial cracks near player and randomly across the world
                const ptx = Math.floor(this.p.x / TS);
                for (let i = 0; i < 8; i++) {
                    const cx = ptx + Math.floor((Math.random() - 0.5) * 200);
                    if (cx < 5 || cx >= WW - 5) continue;
                    const sy = Math.floor(this.w.heightMap[Math.max(0, Math.min(WW - 1, cx))]);
                    m.cracks.push({ x: cx, y: sy, intensity: 0.3 + Math.random() * 0.7, growTimer: 0, maxDepth: 5 + Math.floor(Math.random() * 15) });
                }
                break;
            }
            case 2: {
                // "Root Emergence" — roots begin spreading underground
                this._eventNotify = '🌱 Ancient roots breach the surface!';
                this._eventNotifyTimer = 6000;
                this.addScreenFlash('rgba(100,200,50,0.3)', 600);
                this.addShake(5, 300);
                sfx.bossRoar();
                // Calculate tree position — center of world at surface
                m.treeX = Math.floor(WW / 2);
                m.treeY = Math.floor(this.w.heightMap[m.treeX]);
                // Spawn root growth points radiating from tree center
                for (let i = 0; i < 12; i++) {
                    const angle = (i / 12) * Math.PI * 2;
                    const dir = Math.cos(angle) > 0 ? 1 : -1;
                    const depth = Math.floor(Math.sin(angle) * 8) + 10;
                    m.roots.push({
                        x: m.treeX, y: m.treeY + 5 + Math.abs(depth),
                        dir: dir, length: 0, maxLen: 40 + Math.floor(Math.random() * 80),
                        depth: depth, angle: angle
                    });
                }
                break;
            }
            case 3: {
                // "Tree Growing" — the World Tree starts growing
                this._eventNotify = '🌳 The World Tree is rising!';
                this._eventNotifyTimer = 6000;
                this.addScreenFlash('rgba(50,255,100,0.4)', 800);
                this.addShake(8, 500);
                sfx.bossRoar();
                break;
            }
            case 4: {
                // "Tree Complete"
                this._eventNotify = '🌳 The World Tree has awakened!';
                this._eventNotifyTimer = 8000;
                this.addScreenFlash('rgba(100,255,200,0.5)', 1000);
                this.addShake(10, 600);
                sfx.bossRoar();
                break;
            }
            case 5: {
                // "Bloomed" — tree reaches full power
                this._eventNotify = '✨ The World Tree blooms with ancient power!';
                this._eventNotifyTimer = 8000;
                this.addScreenFlash('rgba(200,255,100,0.4)', 800);
                this.addShake(6, 400);
                // Place World Heart inside the tree trunk
                const hx = m.treeX, hy = m.treeY - Math.floor(m.maxTreeHeight * 0.4);
                this.w.st(hx, hy, T.WORLDHEART);
                this.w.st(hx - 1, hy, T.WORLDCORE);
                this.w.st(hx + 1, hy, T.WORLDCORE);
                this.w.st(hx, hy - 1, T.WORLDCORE);
                this.w.st(hx, hy + 1, T.WORLDCORE);
                this.w.updateLight(hx, hy, 20);
                break;
            }
        }
    }

    _updateMutationCracks(dt) {
        const m = this._mutation;
        m.crackSpawnTimer += dt;

        // Spawn new cracks periodically
        if (m.crackSpawnTimer > 8000 && m.cracks.length < 30) {
            m.crackSpawnTimer = 0;
            const cx = Math.floor(Math.random() * (WW - 20)) + 10;
            const sy = Math.floor(this.w.heightMap[Math.max(0, Math.min(WW - 1, cx))]);
            m.cracks.push({ x: cx, y: sy, intensity: 0.3 + Math.random() * 0.7, growTimer: 0, maxDepth: 5 + Math.floor(Math.random() * 12) });
            // Small earthquake
            const pdist = Math.abs(cx - Math.floor(this.p.x / TS));
            if (pdist < 60) {
                this.addShake(2 + Math.floor(3 * (1 - pdist / 60)), 150);
            }
        }

        // Grow existing cracks downward
        for (const crack of m.cracks) {
            crack.growTimer += dt;
            if (crack.growTimer > 2000) {
                crack.growTimer = 0;
                // Dig crack deeper
                const depth = crack.maxDepth;
                const cx = crack.x, sy = crack.y;
                for (let dy = 0; dy < depth; dy++) {
                    const ty = sy + dy;
                    if (ty >= WH) break;
                    const existing = this.w.gt(cx, ty);
                    if (existing !== T.AIR && existing !== T.WORLDCRACK && existing !== T.WATER && existing !== T.LAVA) {
                        this.w.st(cx, ty, T.WORLDCRACK);
                        // Shard decorations on sides
                        if (Math.random() > 0.7 && this.w.gt(cx - 1, ty) === T.AIR) this.w.st(cx - 1, ty, T.CRACKSHARD);
                        if (Math.random() > 0.7 && this.w.gt(cx + 1, ty) === T.AIR) this.w.st(cx + 1, ty, T.CRACKSHARD);
                    }
                }
                // Mutate nearby surface tiles
                for (let dx = -3; dx <= 3; dx++) {
                    const nx = cx + dx;
                    if (nx < 0 || nx >= WW) continue;
                    const nsy = Math.floor(this.w.heightMap[Math.max(0, Math.min(WW - 1, nx))]);
                    const st = this.w.gt(nx, nsy);
                    if (SOLID.has(st) && st !== T.WORLDCRACK && st !== T.MUTATEDGRASS && st !== T.MUTATEDSTONE && Math.random() > 0.5) {
                        this.w.st(nx, nsy, T.MUTATEDGRASS);
                        if (this.w.gt(nx, nsy + 1) !== T.AIR && Math.random() > 0.6) {
                            this.w.st(nx, nsy + 1, T.MUTATEDSTONE);
                        }
                    }
                }
                this.w.updateLight(cx, sy, 12);
                crack.maxDepth = Math.min(crack.maxDepth + 1, 25);
            }
        }

        // Spawn crack mobs near player
        m.crackSpawnTimer += dt * 0.5;
        if (m.particleTimer > 3000) {
            m.particleTimer = 0;
            // Particles from nearby cracks
            for (const crack of m.cracks) {
                const pdist = Math.abs(crack.x * TS - this.p.x);
                if (pdist < 400) {
                    for (let i = 0; i < 3; i++) {
                        this.addPart(
                            crack.x * TS + 8, crack.y * TS,
                            (Math.random() - 0.5) * 2, -Math.random() * 3 - 1,
                            Math.random() > 0.5 ? '#9944dd' : '#6622aa',
                            40 + Math.random() * 30, 2 + Math.random() * 2
                        );
                    }
                }
            }
        }
    }

    _updateMutationRoots(dt) {
        const m = this._mutation;
        m.rootGrowTimer += dt;

        if (m.rootGrowTimer > 500) { // grow roots every 0.5s
            m.rootGrowTimer = 0;
            for (const root of m.roots) {
                if (root.length >= root.maxLen) continue;
                root.length++;
                // Calculate next root position
                const rx = root.x + Math.floor(root.dir * root.length * 0.8);
                const ry = root.y + Math.floor(Math.sin(root.length * 0.15 + root.angle) * 3);
                if (rx < 2 || rx >= WW - 2 || ry < 2 || ry >= WH - 2) continue;

                const existing = this.w.gt(rx, ry);
                if (existing !== T.AIR) {
                    this.w.st(rx, ry, T.WORLDROOT);
                }
                // Smaller root veins branching off
                if (Math.random() > 0.7) {
                    const branchY = ry + (Math.random() > 0.5 ? 1 : -1);
                    if (branchY > 0 && branchY < WH && this.w.gt(rx, branchY) !== T.AIR) {
                        this.w.st(rx, branchY, T.ROOTVEIN);
                    }
                }
                // Surface disruption — roots peeking through
                const surfY = Math.floor(this.w.heightMap[Math.max(0, Math.min(WW - 1, rx))]);
                if (ry <= surfY + 3 && Math.random() > 0.6) {
                    if (this.w.gt(rx, surfY) !== T.AIR) {
                        this.w.st(rx, surfY, T.MUTATEDGRASS);
                    }
                    if (surfY > 0 && this.w.gt(rx, surfY - 1) === T.AIR && Math.random() > 0.7) {
                        this.w.st(rx, surfY - 1, T.ROOTVEIN);
                    }
                }
                this.w.updateLight(rx, ry, 8);
            }

            // Periodic earthquake as roots spread
            if (Math.random() > 0.85) {
                this.addShake(2, 100);
            }
        }

        // Mutate terrain near tree center — prepare the ground
        if (m.phaseTimer > 3000 && !m.announced.rootsMutate) {
            m.announced.rootsMutate = true;
            const cx = m.treeX;
            // Clear and flatten area for tree trunk
            for (let dx = -8; dx <= 8; dx++) {
                const nx = cx + dx;
                if (nx < 0 || nx >= WW) continue;
                const sy = Math.floor(this.w.heightMap[Math.max(0, Math.min(WW - 1, nx))]);
                // Place root blocks underground
                for (let dy = 1; dy < 10; dy++) {
                    if (sy + dy < WH) {
                        const dist = Math.abs(dx);
                        if (dist < 5 && Math.random() > 0.3) {
                            this.w.st(nx, sy + dy, T.WORLDROOT);
                        } else if (Math.random() > 0.6) {
                            this.w.st(nx, sy + dy, T.ROOTVEIN);
                        }
                    }
                }
                this.w.updateLight(nx, sy, 12);
            }
        }
    }

    _updateMutationTreeGrow(dt) {
        const m = this._mutation;
        m.treeGrowTimer += dt;

        if (m.treeGrowTimer > 200) { // grow every 200ms for dramatic effect
            m.treeGrowTimer = 0;
            if (m.treeGrowStep < m.maxTreeHeight) {
                m.treeGrowStep++;
                this._buildWorldTreeStep(m.treeGrowStep);

                // Visual feedback
                if (m.treeGrowStep % 5 === 0) {
                    this.addShake(2, 80);
                    // Growth particles
                    const tx = m.treeX * TS + TS / 2;
                    const ty = (m.treeY - m.treeGrowStep) * TS;
                    for (let i = 0; i < 5; i++) {
                        this.addPart(tx + (Math.random() - 0.5) * 30, ty,
                            (Math.random() - 0.5) * 3, -Math.random() * 4,
                            ['#22aa44', '#44cc66', '#88ff88', '#ddaa22'][Math.floor(Math.random() * 4)],
                            40 + Math.random() * 20, 2 + Math.random() * 2);
                    }
                }

                // Sound at milestones
                if (m.treeGrowStep % 15 === 0) {
                    sfx.blockPlace();
                    this.addShake(4, 200);
                }
            } else if (!m.treeBuilt) {
                m.treeBuilt = true;
                // Build canopy and final details
                this._buildWorldTreeCanopy();
                this._buildWorldTreePlatforms();
                this._buildWorldTreeRooms();
            }
        }
    }

    _buildWorldTreeStep(step) {
        const m = this._mutation;
        const cx = m.treeX;
        const y = m.treeY - step;
        if (y < 2 || y >= WH) return;

        // Trunk widens at base, narrows at top
        const baseWidth = 4;
        const topWidth = 2;
        const progress = step / m.maxTreeHeight;
        const width = Math.floor(baseWidth - (baseWidth - topWidth) * progress);

        // Main trunk
        for (let dx = -width; dx <= width; dx++) {
            const nx = cx + dx;
            if (nx < 0 || nx >= WW) continue;

            // Outer bark
            if (Math.abs(dx) === width) {
                this.w.st(nx, y, T.WORLDBARK);
            }
            // Inner wood
            else {
                this.w.st(nx, y, T.WORLDROOT);
            }
            this.w.sb(nx, y, T.WORLDBARK);
        }

        // Buttress roots at base (first 10 steps)
        if (step <= 10) {
            const buttressW = Math.floor((10 - step) * 1.2);
            for (let dx = -width - buttressW; dx <= width + buttressW; dx++) {
                if (Math.abs(dx) <= width) continue; // skip trunk area
                const nx = cx + dx;
                if (nx < 0 || nx >= WW) continue;
                const dist = Math.abs(dx) - width;
                // Buttress slopes down
                const by = y + Math.floor(dist * 0.6);
                if (by < WH && this.w.gt(nx, by) !== T.WORLDROOT) {
                    this.w.st(nx, by, T.WORLDBARK);
                    this.w.sb(nx, by, T.WORLDBARK);
                }
            }
        }

        // Natural irregularities — bulges and knots
        if (step > 5 && step < m.maxTreeHeight - 5) {
            // Random bulges
            if (Math.random() > 0.85) {
                const side = Math.random() > 0.5 ? 1 : -1;
                const bx = cx + side * (width + 1);
                if (bx >= 0 && bx < WW) {
                    this.w.st(bx, y, T.WORLDBARK);
                    if (bx + side >= 0 && bx + side < WW && Math.random() > 0.5) {
                        this.w.st(bx + side, y, T.WORLDMOSS);
                    }
                }
            }
            // Moss patches
            if (Math.random() > 0.8) {
                const side = Math.random() > 0.5 ? 1 : -1;
                const mx2 = cx + side * (width + 1);
                if (mx2 >= 0 && mx2 < WW && this.w.gt(mx2, y) === T.AIR) {
                    this.w.st(mx2, y, T.WORLDMOSS);
                }
            }
        }

        // Branches at certain heights
        if (step > 15 && step < m.maxTreeHeight - 5 && step % 7 === 0) {
            this._buildWorldTreeBranch(cx, y, step);
        }

        // Sap drips (light sources)
        if (step > 10 && Math.random() > 0.88) {
            const side = Math.random() > 0.5 ? width + 1 : -(width + 1);
            const sx2 = cx + side;
            if (sx2 >= 0 && sx2 < WW && this.w.gt(sx2, y) === T.AIR) {
                this.w.st(sx2, y, T.WORLDSAP);
            }
        }

        this.w.updateLight(cx, y, 12);
    }

    _buildWorldTreeBranch(cx, y, step) {
        const m = this._mutation;
        const progress = step / m.maxTreeHeight;
        // Alternate branch sides, sometimes both
        const sides = step % 14 < 7 ? [-1] : [1];
        if (Math.random() > 0.6) sides.push(-sides[0]);

        for (const side of sides) {
            const branchLen = Math.floor(8 + (1 - progress) * 15 + Math.random() * 8);
            const branchAngle = -0.3 - Math.random() * 0.4; // slight upward angle

            let bx = cx + side * (Math.floor(4 - (4 - 2) * progress) + 1);
            let by = y;

            for (let i = 0; i < branchLen; i++) {
                bx += side;
                by += Math.floor(branchAngle + Math.sin(i * 0.5) * 0.3);

                if (bx < 2 || bx >= WW - 2 || by < 2 || by >= WH) break;

                // Branch thickness — thick at base, thin at tip
                const thick = i < branchLen * 0.3 ? 2 : (i < branchLen * 0.6 ? 1 : 0);
                this.w.st(bx, by, T.WORLDBARK);
                this.w.sb(bx, by, T.WORLDBARK);

                for (let t = 1; t <= thick; t++) {
                    if (by + t < WH) { this.w.st(bx, by + t, T.WORLDROOT); this.w.sb(bx, by + t, T.WORLDROOT); }
                }

                // Leaf clusters at the end of branches
                if (i > branchLen * 0.5) {
                    for (let ldy = -2; ldy <= 1; ldy++) {
                        for (let ldx = -1; ldx <= 1; ldx++) {
                            const lx = bx + ldx, ly = by + ldy;
                            if (lx >= 0 && lx < WW && ly >= 0 && ly < WH) {
                                if (this.w.gt(lx, ly) === T.AIR && Math.random() > 0.3) {
                                    this.w.st(lx, ly, T.WORLDLEAF);
                                }
                            }
                        }
                    }
                }

                // Hanging vines from branches
                if (i > branchLen * 0.4 && Math.random() > 0.7) {
                    const vineLen = 3 + Math.floor(Math.random() * 6);
                    for (let v = 1; v <= vineLen; v++) {
                        if (by + v < WH && this.w.gt(bx, by + v) === T.AIR) {
                            this.w.st(bx, by + v, T.WORLDMOSS);
                        } else break;
                    }
                }
            }
        }
    }

    _buildWorldTreeCanopy() {
        const m = this._mutation;
        const cx = m.treeX;
        const topY = m.treeY - m.maxTreeHeight;

        // Multi-layered canopy — elliptical dome shape
        const canopyLayers = [
            { rx: 18, ry: 8, yOff: 0, density: 0.75 },
            { rx: 22, ry: 10, yOff: -3, density: 0.6 },
            { rx: 14, ry: 6, yOff: -8, density: 0.8 },
            { rx: 10, ry: 5, yOff: -12, density: 0.85 },
            { rx: 6, ry: 3, yOff: -15, density: 0.9 },
        ];

        for (const layer of canopyLayers) {
            for (let dx = -layer.rx; dx <= layer.rx; dx++) {
                for (let dy = -layer.ry; dy <= layer.ry; dy++) {
                    const ex = (dx * dx) / (layer.rx * layer.rx) + (dy * dy) / (layer.ry * layer.ry);
                    if (ex > 1) continue;

                    const lx = cx + dx;
                    const ly = topY + layer.yOff + dy;
                    if (lx < 0 || lx >= WW || ly < 1 || ly >= WH) continue;

                    if (this.w.gt(lx, ly) === T.AIR && Math.random() < layer.density) {
                        // Edge leaves are sparser and use different tile
                        if (ex > 0.7) {
                            this.w.st(lx, ly, Math.random() > 0.3 ? T.WORLDLEAF : T.WORLDMOSS);
                        } else {
                            this.w.st(lx, ly, T.WORLDLEAF);
                        }
                        this.w.sb(lx, ly, T.WORLDLEAF);
                    }
                }
            }
        }

        // Hanging vines from canopy edges
        for (let dx = -22; dx <= 22; dx++) {
            const lx = cx + dx;
            if (lx < 0 || lx >= WW) continue;
            // Find bottom of canopy at this x
            for (let dy = 10; dy >= -15; dy--) {
                const ly = topY + dy;
                if (ly >= 0 && ly < WH && this.w.gt(lx, ly) === T.WORLDLEAF) {
                    // Hang vine below
                    if (Math.random() > 0.6) {
                        const vineLen = 4 + Math.floor(Math.random() * 10);
                        for (let v = 1; v <= vineLen; v++) {
                            if (ly + v < WH && this.w.gt(lx, ly + v) === T.AIR) {
                                this.w.st(lx, ly + v, T.WORLDMOSS);
                            } else break;
                        }
                    }
                    break;
                }
            }
        }

        // Light crystals embedded in canopy
        for (let i = 0; i < 12; i++) {
            const rx = cx + Math.floor((Math.random() - 0.5) * 30);
            const ry = topY + Math.floor((Math.random() - 0.5) * 12) - 5;
            if (rx >= 0 && rx < WW && ry >= 0 && ry < WH) {
                if (this.w.gt(rx, ry) === T.WORLDLEAF) {
                    this.w.st(rx, ry, T.ROOTCRYSTAL);
                }
            }
        }

        this.w.updateLight(cx, topY, 30);
    }

    _buildWorldTreePlatforms() {
        const m = this._mutation;
        const cx = m.treeX;
        const topY = m.treeY - m.maxTreeHeight;

        // Platforms at branch intersections for exploration
        const platformHeights = [
            m.treeY - 15,
            m.treeY - 28,
            m.treeY - 42,
            m.treeY - 55,
            topY + 5,
        ];

        for (const py of platformHeights) {
            if (py < 2 || py >= WH) continue;
            const platW = 5 + Math.floor(Math.random() * 4);
            const platSide = Math.random() > 0.5 ? 1 : -1;
            const platX = cx + platSide * (6 + Math.floor(Math.random() * 8));

            for (let dx = 0; dx < platW; dx++) {
                const px2 = platX + dx * platSide;
                if (px2 >= 0 && px2 < WW) {
                    this.w.st(px2, py, T.TREEPLATFORM);
                    // Railing
                    if (dx === 0 || dx === platW - 1) {
                        if (py - 1 >= 0) this.w.st(px2, py - 1, T.WORLDBARK);
                        if (py - 2 >= 0) this.w.st(px2, py - 2, T.WORLDBARK);
                    }
                }
            }

            // Light source on each platform
            const lightX = platX + Math.floor(platW / 2) * platSide;
            if (lightX >= 0 && lightX < WW && py - 1 >= 0) {
                this.w.st(lightX, py - 1, T.WORLDSAP);
            }

            // Occasional chest
            if (Math.random() > 0.5) {
                const chestX = platX + Math.floor(platW * 0.3) * platSide;
                if (chestX >= 0 && chestX < WW && py - 1 >= 0 && this.w.gt(chestX, py - 1) === T.AIR) {
                    this.w.st(chestX, py - 1, T.CHEST);
                }
            }

            this.w.updateLight(platX, py, 12);
        }
    }

    _buildWorldTreeRooms() {
        const m = this._mutation;
        const cx = m.treeX;

        // Hollow rooms inside the trunk
        const roomHeights = [
            m.treeY - 10,
            m.treeY - 25,
            m.treeY - 40,
        ];

        for (const ry of roomHeights) {
            if (ry < 5 || ry >= WH - 5) continue;
            // Carve a small room
            for (let dx = -2; dx <= 2; dx++) {
                for (let dy = -2; dy <= 2; dy++) {
                    const rx = cx + dx, rry = ry + dy;
                    if (rx >= 0 && rx < WW && rry >= 0 && rry < WH) {
                        this.w.st(rx, rry, T.AIR);
                    }
                }
            }
            // Floor
            for (let dx = -2; dx <= 2; dx++) {
                const rx = cx + dx;
                if (rx >= 0 && rx < WW && ry + 3 < WH) {
                    this.w.st(rx, ry + 3, T.TREEHOME);
                }
            }
            // Walls — bark
            for (let dy = -2; dy <= 3; dy++) {
                if (cx - 3 >= 0) this.w.st(cx - 3, ry + dy, T.WORLDBARK);
                if (cx + 3 < WW) this.w.st(cx + 3, ry + dy, T.WORLDBARK);
            }
            // Light
            this.w.st(cx, ry - 2, T.WORLDSAP);
            // Ladder connecting rooms
            if (ry + 4 < WH) {
                for (let ldy = 4; ldy < 12; ldy++) {
                    if (ry + ldy < WH && this.w.gt(cx, ry + ldy) === T.WORLDROOT) {
                        this.w.st(cx, ry + ldy, T.LADDER);
                    }
                }
            }
            this.w.updateLight(cx, ry, 12);
        }

        // Entrance at base — clear a door-shaped opening
        const baseY = m.treeY;
        for (let dy = -3; dy <= 0; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
                const ex = cx + dx, ey = baseY + dy;
                if (ex >= 0 && ex < WW && ey >= 0 && ey < WH) {
                    this.w.st(ex, ey, T.AIR);
                }
            }
        }
        // Door frame
        if (cx - 2 >= 0) for (let dy = -4; dy <= 0; dy++) this.w.st(cx - 2, baseY + dy, T.WORLDBARK);
        if (cx + 2 < WW) for (let dy = -4; dy <= 0; dy++) this.w.st(cx + 2, baseY + dy, T.WORLDBARK);
        // Arch
        this.w.st(cx - 1, baseY - 4, T.WORLDBARK);
        this.w.st(cx, baseY - 4, T.WORLDSAP);
        this.w.st(cx + 1, baseY - 4, T.WORLDBARK);

        this.w.updateLight(cx, baseY, 15);
    }

    _updateMutationTreeComplete(dt) {
        const m = this._mutation;

        // Ambient healing near tree
        const treeDist = Math.abs(this.p.x - m.treeX * TS);
        if (treeDist < 300 && this.p.hp < this.p.maxHp) {
            this._regenTimer += dt * 2; // double regen near tree
        }

        // Spawn tree-themed mobs near the tree
        if (m.particleTimer > 5000) {
            m.particleTimer = 0;
            const nearTree = Math.abs(Math.floor(this.p.x / TS) - m.treeX) < 40;
            if (nearTree && this.mobs.length < 20) {
                const treeTypes = ['ROOT_GUARDIAN', 'TREE_SPIRIT', 'SAP_WISP'];
                const type = treeTypes[Math.floor(Math.random() * treeTypes.length)];
                const def = MOB_TYPES[type];
                if (def) {
                    const sx = m.treeX + Math.floor((Math.random() - 0.5) * 30);
                    const sy = m.treeY - Math.floor(Math.random() * 30) - 5;
                    this.mobs.push(new Mob(type, sx * TS, sy * TS));
                }
            }
        }

        // World Heart detection — check if player is near
        if (!m.treeHeartFound) {
            const hx = m.treeX, hy = m.treeY - Math.floor(m.maxTreeHeight * 0.4);
            const pdist = Math.sqrt((this.p.x - hx * TS) ** 2 + (this.p.y - hy * TS) ** 2);
            if (pdist < TS * 5) {
                m.treeHeartFound = true;
                this._eventNotify = '💖 You found the Heart of the World!';
                this._eventNotifyTimer = 6000;
                this.addScreenFlash('rgba(255,100,200,0.4)', 600);
                this.addShake(6, 300);
                this.addXp(200, this.p.x + PW / 2, this.p.y);
                sfx.bossRoar();
            }
        }

        // Canopy leaf particles falling
        if (m.particleTimer % 1000 < dt) {
            const nearTree = Math.abs(Math.floor(this.p.x / TS) - m.treeX) < 30;
            if (nearTree) {
                for (let i = 0; i < 2; i++) {
                    this.addPart(
                        this.p.x + (Math.random() - 0.5) * 400,
                        this.p.y - 200 + Math.random() * 100,
                        (Math.random() - 0.5) * 1.5,
                        Math.random() * 1 + 0.3,
                        ['#22aa44', '#44cc66', '#88ee88', '#aaff66'][Math.floor(Math.random() * 4)],
                        80 + Math.random() * 60, 3 + Math.random() * 2
                    );
                }
            }
        }
    }

    _updateMutationBloomed(dt) {
        const m = this._mutation;
        // Same as complete but with boss spawning
        this._updateMutationTreeComplete(dt);

        // Occasionally spawn Bark Golem near tree
        if (m.phaseTimer > 30000 && Math.random() > 0.999) {
            const nearTree = Math.abs(Math.floor(this.p.x / TS) - m.treeX) < 35;
            if (nearTree && this.mobs.filter(mob => mob.type === 'BARK_GOLEM').length === 0) {
                const def = MOB_TYPES['BARK_GOLEM'];
                if (def) {
                    const sx = m.treeX + Math.floor((Math.random() - 0.5) * 20);
                    const sy = m.treeY - 5;
                    this.mobs.push(new Mob('BARK_GOLEM', sx * TS, sy * TS - def.h));
                    this._eventNotify = '🌿 A Bark Golem awakens to defend the tree!';
                    this._eventNotifyTimer = 4000;
                    this.addShake(5, 300);
                    sfx.bossRoar();
                }
            }
        }
    }

    // === ACCESSORY HELPERS ===
    _hasAccessory(id) { return this._accessories.includes(id); }

    _checkAccessoryDrop(mob) {
        for (const acc of ACCESSORIES) {
            if (acc.mobs.includes(mob.type) && Math.random() < acc.dropChance) {
                if (this._accessories.includes(acc.id)) continue; // already have it
                if (this._accessories.length < this._accessorySlots) {
                    this._accessories.push(acc.id);
                } else {
                    continue; // slots full, skip
                }
                this._accessoryNotify = `✨ ${acc.name}: ${acc.desc}`;
                this._accessoryNotifyTimer = 5000;
                this.addScreenFlash(acc.color + '44', 500);
                this.addShake(5, 200);
                this.slowMo = 200; // brief slow-mo for drama
                this.dmgNums.add(mob.x + mob.w / 2, mob.y - 20, `✨ ${acc.name}`, acc.color);
                sfx.accessoryPickup();
                // Sparkle burst
                for (let i = 0; i < 15; i++) {
                    const a = Math.random() * Math.PI * 2;
                    this.addPart(mob.x + mob.w / 2, mob.y + mob.h / 2, Math.cos(a) * 4, Math.sin(a) * 4 - 2, acc.color, 35, 2 + Math.random() * 2);
                }
                break; // only one drop per kill
            }
        }
    }

    _checkSpecialWeaponDrop(mob) {
        for (const sw of SPECIAL_WEAPONS) {
            if (sw.mobs.includes(mob.type) && Math.random() < sw.dropChance) {
                // Replace if new weapon is better, or if no weapon
                if (this._specialWeapon && this._specialWeapon.dmg >= sw.dmg) continue;
                this._specialWeapon = { ...sw };
                this._specialWeaponNotify = `⚔ ${sw.name}: ${sw.desc} [Q]`;
                this._specialWeaponNotifyTimer = 5000;
                this.addScreenFlash(sw.color + '55', 600);
                this.addShake(6, 250);
                this.slowMo = 300; // dramatic slow-mo
                this.dmgNums.add(mob.x + mob.w / 2, mob.y - 25, `⚔ ${sw.name}`, sw.color);
                sfx.bossPhaseChange();
                // Epic burst
                for (let i = 0; i < 20; i++) {
                    const a = Math.random() * Math.PI * 2;
                    const spd = 3 + Math.random() * 5;
                    this.addPart(mob.x + mob.w / 2, mob.y + mob.h / 2, Math.cos(a) * spd, Math.sin(a) * spd - 2, i % 2 ? sw.color : '#fff', 40, 2 + Math.random() * 3);
                }
                break;
            }
        }
    }

    _fireSpecialWeapon() {
        const sw = this._specialWeapon;
        if (!sw) return;
        const p = this.p;
        const dir = p.right ? 1 : -1;
        const px = p.x + PW / 2 + dir * 8;
        const py = p.y + PH / 2 - 4;
        this._specialAttackCd = sw.projType === 'crystal' ? 350 : (sw.projType === 'lightning' ? 250 : 400);

        // Spawn projectile
        const proj = {
            x: px, y: py,
            vx: dir * (sw.projType === 'lightning' ? 10 : (sw.projType === 'crystal' ? 8 : 6)),
            vy: sw.projType === 'lightning' ? 0 : (Math.random() - 0.5) * 1.5,
            type: sw.projType, dmg: sw.dmg, life: 600, maxLife: 600,
            color: sw.projColor, baseColor: sw.color, size: sw.projType === 'lightning' ? 4 : 6
        };
        this._projectiles.push(proj);

        // Weapon-specific spawn effects
        p.swingTimer = 150; p.swingDir = dir;
        // Play weapon-specific sound
        if (sw.sound === 'fire') sfx.fireShot();
        else if (sw.sound === 'ice') sfx.iceShot();
        else if (sw.sound === 'lightning') sfx.lightningShot();
        else sfx.swing();
        this.addShake(2, 50);

        // Muzzle flash particles
        const flashCols = [sw.color, sw.projColor, '#fff'];
        for (let i = 0; i < 6; i++) {
            const a = (Math.random() - 0.5) * 0.8 + (dir > 0 ? 0 : Math.PI);
            const spd = 2 + Math.random() * 3;
            this.addPart(px, py, Math.cos(a) * spd, Math.sin(a) * spd, flashCols[i % 3], 15, 2);
        }
    }

    _updateProjectiles(dt) {
        for (let i = this._projectiles.length - 1; i >= 0; i--) {
            const proj = this._projectiles[i];
            proj.x += proj.vx * dt / 16;
            proj.y += proj.vy * dt / 16;
            proj.life -= dt;

            // Gravity for some projectile types
            if (proj.type === 'fire') proj.vy += 0.05;
            if (proj.type === 'poison') proj.vy += 0.03;

            // Trail particles
            if (Math.random() > 0.4) {
                const trailCols = {
                    fire: ['#ff4400', '#ff8800', '#ffcc00'],
                    ice: ['#44ccff', '#88eeff', '#ffffff'],
                    lightning: ['#ffee44', '#ffffff', '#aaccff'],
                    poison: ['#44cc44', '#88ff88', '#22aa22'],
                    crystal: ['#aa44ff', '#cc88ff', '#ffffff'],
                    blood: ['#cc2244', '#ff4466', '#881122']
                };
                const cols = trailCols[proj.type] || ['#fff'];
                this.addPart(proj.x, proj.y, (Math.random() - 0.5) * 1.5, (Math.random() - 0.5) * 1.5,
                    cols[Math.floor(Math.random() * cols.length)], 12 + Math.random() * 8, 1.5 + Math.random());
            }

            // Hit detection against mobs
            let hit = false;
            for (const mob of this.mobs) {
                if (mob.dead || mob.def.passive || mob.def.isVillager) continue;
                if (Math.abs(mob.x + mob.w / 2 - proj.x) < mob.w && Math.abs(mob.y + mob.h / 2 - proj.y) < mob.h) {
                    mob.hp -= proj.dmg; mob.hitTimer = 300;
                    const kbDir = proj.vx > 0 ? 1 : -1;
                    mob.vx += kbDir * 5; mob.vy = -3;
                    mob.squash = 1.5; mob.stretch = 0.6;
                    sfx.mobHit();
                    this.addShake(3, 80);
                    this.dmgNums.add(mob.x + mob.w / 2, mob.y - 8, proj.dmg, proj.color);

                    // Type-specific effects on hit
                    if (proj.type === 'fire') {
                        mob._burning = 3000; // burn for 3s
                        for (let j = 0; j < 8; j++) {
                            const a = Math.random() * Math.PI * 2;
                            this.addPart(mob.x + mob.w / 2, mob.y + mob.h / 2, Math.cos(a) * 3, Math.sin(a) * 3 - 1, j % 2 ? '#ff4400' : '#ffaa00', 25, 2);
                        }
                        this.addScreenFlash('rgba(255,100,0,0.15)', 80);
                    }
                    if (proj.type === 'ice') {
                        mob._frozen = 2000; // freeze for 2s
                        mob.vx = 0; mob.vy = 0;
                        for (let j = 0; j < 6; j++) {
                            const a = Math.random() * Math.PI * 2;
                            this.addPart(mob.x + mob.w / 2, mob.y + mob.h / 2, Math.cos(a) * 2, Math.sin(a) * 2, j % 2 ? '#88ddff' : '#fff', 30, 2);
                        }
                        this.addScreenFlash('rgba(100,200,255,0.12)', 80);
                    }
                    if (proj.type === 'lightning') {
                        // Chain to nearby mob
                        let chainTarget = null, chainDist = Infinity;
                        for (const m2 of this.mobs) {
                            if (m2 === mob || m2.dead || m2.def.passive) continue;
                            const d = Math.abs(m2.x - mob.x) + Math.abs(m2.y - mob.y);
                            if (d < 100 && d < chainDist) { chainTarget = m2; chainDist = d; }
                        }
                        if (chainTarget) {
                            chainTarget.hp -= Math.ceil(proj.dmg * 0.5); chainTarget.hitTimer = 200;
                            this.dmgNums.add(chainTarget.x + chainTarget.w / 2, chainTarget.y - 8, `⚡${Math.ceil(proj.dmg * 0.5)}`, '#ffee44');
                            // Lightning arc particles
                            for (let j = 0; j < 5; j++) {
                                const lx = mob.x + (chainTarget.x - mob.x) * (j / 5) + (Math.random() - 0.5) * 8;
                                const ly = mob.y + (chainTarget.y - mob.y) * (j / 5) + (Math.random() - 0.5) * 8;
                                this.addPart(lx, ly, 0, 0, '#ffee44', 10, 2);
                            }
                        }
                        this.addScreenFlash('rgba(255,255,200,0.2)', 60);
                    }
                    if (proj.type === 'poison') {
                        mob._poisoned = 4000; // poison for 4s
                        for (let j = 0; j < 5; j++) this.addPart(mob.x + mob.w / 2, mob.y + mob.h / 2, (Math.random() - 0.5) * 2, -Math.random() * 2, '#44cc44', 20, 2);
                    }
                    if (proj.type === 'blood') {
                        // Lifesteal
                        const heal = Math.ceil(proj.dmg * 0.15);
                        this.p.hp = Math.min(this.p.maxHp, this.p.hp + heal);
                        this.dmgNums.add(this.p.x + PW / 2, this.p.y - 10, `+${heal}`, '#ff4466');
                        for (let j = 0; j < 5; j++) this.addPart(mob.x + mob.w / 2, mob.y + mob.h / 2, (Math.random() - 0.5) * 3, -Math.random() * 3, '#cc2244', 20, 2);
                    }

                    // Check mob death from projectile
                    if (mob.hp <= 0) {
                        mob.dead = true; sfx.mobDie();
                        this.addShake(5, 150);
                        this.gore.spawnFromMob(mob, this.w, performance.now());
                        this._recordBestiary(mob.type, true);
                        this._checkAccessoryDrop(mob);
                        this._checkSpecialWeaponDrop(mob);
                        const mt = MOB_TYPES[mob.type]; let mxp = 5;
                        if (mt && mt.hp >= 40) mxp = 15;
                        if (mt && mt.hp >= 80) mxp = 30;
                        if (mt && mt.hp >= 150) mxp = 50;
                        if (mt && mt.isMiniBoss) mxp = 100;
                        if (this._currentEvent && EVENTS[this._currentEvent]) mxp = Math.floor(mxp * (EVENTS[this._currentEvent].xpMult || 1));
                        if (this._buffs.xp_magnet) mxp *= 2;
                        this.addXp(mxp, mob.x + mob.w / 2, mob.y + mob.h / 2);
                        this.stats.mobsKilled++;
                        this._onMobKilled();
                        if (this._currentEvent) this._eventMobsKilled++;
                        this._traderCurrency += Math.max(1, Math.floor(mxp / 5));
                        this._dropLoot(mob);
                    }
                    hit = true;
                    break;
                }
            }

            // Hit solid tiles
            const tx = Math.floor(proj.x / TS), ty = Math.floor(proj.y / TS);
            if (SOLID.has(this.w.gt(tx, ty))) hit = true;

            if (hit || proj.life <= 0) {
                // Impact burst
                for (let j = 0; j < 6; j++) {
                    const a = Math.random() * Math.PI * 2;
                    this.addPart(proj.x, proj.y, Math.cos(a) * 3, Math.sin(a) * 3, proj.color, 20, 2);
                }
                this._projectiles.splice(i, 1);
            }
        }

        // Apply burning/freezing/poison DOT
        for (const mob of this.mobs) {
            if (mob.dead) continue;
            if (mob._burning && mob._burning > 0) {
                mob._burning -= dt;
                if (Math.random() > 0.92) {
                    mob.hp -= 2; mob.hitTimer = 100;
                    this.addPart(mob.x + mob.w / 2, mob.y, (Math.random() - 0.5) * 2, -1 - Math.random() * 2, '#ff6600', 15, 2);
                }
            }
            if (mob._frozen && mob._frozen > 0) {
                mob._frozen -= dt;
                mob.vx *= 0.1; // slow to near stop
            }
            if (mob._poisoned && mob._poisoned > 0) {
                mob._poisoned -= dt;
                if (Math.random() > 0.95) {
                    mob.hp -= 1; mob.hitTimer = 80;
                    this.addPart(mob.x + mob.w / 2, mob.y, (Math.random() - 0.5) * 1, -Math.random() * 1.5, '#44cc44', 12, 1.5);
                }
            }
        }
    }

    // === DASH SYSTEM ===
    _startDash() {
        this._dashTimer = 150;
        this._dashCd = 800;
        this._dashDir = this.p.right ? 1 : -1;
        sfx.bossDash();
        this.addShake(2, 50);
        this.addScreenFlash('rgba(255,150,50,0.15)', 60);
        // Dash trail
        for (let i = 0; i < 5; i++) {
            this.addPart(this.p.x + PW / 2, this.p.y + PH / 2, -this._dashDir * (1 + Math.random()), (Math.random() - 0.5) * 2, '#ff8844', 15, 2);
        }
    }

    _updateDash(dt) {
        this._dashCd = Math.max(0, this._dashCd - dt);
        if (this._dashTimer > 0) {
            this._dashTimer -= dt;
            const dashSpd = 8;
            const nx = this.p.x + this._dashDir * dashSpd;
            if (!this.collides(nx, this.p.y)) {
                this.p.x = nx;
                this.p.iframes = 100; // brief invulnerability during dash
            }
            // Dash trail particles
            if (Math.random() > 0.5) {
                this.addPart(this.p.x + PW / 2, this.p.y + PH / 2 + (Math.random() - 0.5) * PH,
                    -this._dashDir * 2, (Math.random() - 0.5), '#ff884488', 10, 2);
            }
        }
    }

    // === BESTIARY ===
    _recordBestiary(type, killed) {
        if (!type || !MOB_TYPES[type] || MOB_TYPES[type].isVillager) return;
        if (!this._bestiary[type]) {
            this._bestiary[type] = { seen: true, killed: 0 };
            this._bestiaryNotify = `📖 Новый моб обнаружен: ${type}`;
            this._bestiaryNotifyTimer = 3000;
        }
        if (killed) {
            this._bestiary[type].killed = (this._bestiary[type].killed || 0) + 1;
        }
    }

    _renderBestiary() {
        let panel = document.getElementById('bestiary-panel');
        if (!this._bestiaryOpen) { if (panel) panel.style.display = 'none'; return; }
        if (!panel) {
            panel = document.createElement('div'); panel.id = 'bestiary-panel';
            panel.style.cssText = `position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
                background:rgba(10,10,25,0.95);border:2px solid rgba(200,180,100,0.4);border-radius:14px;
                padding:16px;z-index:55;backdrop-filter:blur(8px);box-shadow:0 8px 32px rgba(0,0,0,0.7);
                font-family:monospace;color:#fff;max-height:80vh;overflow-y:auto;min-width:360px;max-width:500px;`;
            this.cv.parentElement.appendChild(panel);
        }
        panel.style.display = 'block';
        panel.innerHTML = '';

        // Title
        const title = document.createElement('div');
        const totalMobs = Object.keys(MOB_TYPES).filter(k => !MOB_TYPES[k].isVillager && !MOB_TYPES[k].isTrader).length;
        const seenCount = Object.keys(this._bestiary).length;
        title.style.cssText = 'font-size:16px;font-weight:bold;margin-bottom:10px;text-align:center;color:#ffd700;text-shadow:0 0 8px rgba(255,200,50,0.4);';
        title.textContent = `📖 Бестиарий [B]  —  ${seenCount}/${totalMobs}`;
        panel.appendChild(title);

        // Progress bar
        const progWrap = document.createElement('div');
        progWrap.style.cssText = 'height:6px;background:rgba(255,255,255,0.1);border-radius:3px;margin-bottom:12px;overflow:hidden;';
        const progFill = document.createElement('div');
        progFill.style.cssText = `height:100%;background:linear-gradient(90deg,#ffd700,#ffaa00);border-radius:3px;width:${(seenCount / totalMobs * 100).toFixed(1)}%;transition:width 0.3s;`;
        progWrap.appendChild(progFill);
        panel.appendChild(progWrap);

        // Categories
        for (const [catKey, cat] of Object.entries(BESTIARY_CATEGORIES)) {
            const catDiv = document.createElement('div');
            catDiv.style.cssText = 'margin-bottom:8px;';
            const catTitle = document.createElement('div');
            const catSeen = cat.mobs.filter(m => this._bestiary[m]).length;
            catTitle.style.cssText = 'font-size:12px;font-weight:bold;color:#ccaa44;margin-bottom:4px;border-bottom:1px solid rgba(255,255,255,0.1);padding-bottom:2px;';
            catTitle.textContent = `${cat.name} (${catSeen}/${cat.mobs.length})`;
            catDiv.appendChild(catTitle);

            const grid = document.createElement('div');
            grid.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px;';

            for (const mobType of cat.mobs) {
                const entry = document.createElement('div');
                const data = this._bestiary[mobType];
                const seen = !!data;
                entry.style.cssText = `width:68px;height:68px;background:${seen ? 'rgba(40,40,60,0.7)' : 'rgba(20,20,30,0.5)'};
                    border:1px solid ${seen ? 'rgba(200,180,100,0.3)' : 'rgba(100,100,100,0.2)'};border-radius:6px;
                    display:flex;flex-direction:column;align-items:center;justify-content:center;position:relative;cursor:pointer;`;

                if (seen) {
                    // Render actual mob texture using MobRenderer into a small canvas
                    const mobCvs = document.createElement('canvas');
                    mobCvs.width = 40; mobCvs.height = 40;
                    mobCvs.style.cssText = 'width:40px;height:40px;image-rendering:pixelated;';
                    const mctx = mobCvs.getContext('2d');
                    mctx.imageSmoothingEnabled = false;
                    // Create a fake mob for rendering
                    const def = MOB_TYPES[mobType];
                    if (def) {
                        const fakeMob = {
                            type: mobType, def, id: 0, x: 20 - def.w / 2, y: 20 - def.h / 2,
                            w: def.w, h: def.h, hitTimer: 0, ground: true, right: true,
                            squash: 1, stretch: 1, colorIndex: 0, vx: 0, vy: 0,
                            ai: { state: 'idle_stand', walkDir: 1, speechShow: 0, speechText: '', profession: 0 },
                            segments: def.isWorm ? Array.from({ length: 8 }, () => ({ x: 20, y: 20 })) : undefined
                        };
                        try { MobRenderer.draw(mctx, fakeMob, 0); } catch (e) { }
                    }
                    entry.appendChild(mobCvs);

                    // Kill count
                    const killDiv = document.createElement('div');
                    killDiv.style.cssText = 'font-size:8px;color:#aaa;margin-top:1px;';
                    killDiv.textContent = `☠${data.killed || 0}`;
                    entry.appendChild(killDiv);

                    // Tooltip
                    const mt = MOB_TYPES[mobType];
                    entry.title = `${mobType}\nHP: ${mt.hp} DMG: ${mt.dmg}\nУбито: ${data.killed || 0}`;
                } else {
                    // Unknown mob — silhouette
                    const unknown = document.createElement('div');
                    unknown.style.cssText = 'font-size:20px;opacity:0.3;';
                    unknown.textContent = '❓';
                    entry.appendChild(unknown);
                    entry.title = '???';
                }

                grid.appendChild(entry);
            }

            catDiv.appendChild(grid);
            panel.appendChild(catDiv);
        }

        // Accessories section in bestiary
        const accTitle = document.createElement('div');
        accTitle.style.cssText = 'font-size:12px;font-weight:bold;color:#44ffaa;margin:10px 0 6px;border-bottom:1px solid rgba(255,255,255,0.1);padding-bottom:2px;';
        accTitle.textContent = `🎒 Аксессуары (${this._accessories.length}/${this._accessorySlots})`;
        panel.appendChild(accTitle);

        const accGrid = document.createElement('div');
        accGrid.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px;';
        for (const acc of ACCESSORIES) {
            const has = this._accessories.includes(acc.id);
            const accEl = document.createElement('div');
            accEl.style.cssText = `padding:4px 8px;background:${has ? 'rgba(68,255,170,0.1)' : 'rgba(40,40,60,0.4)'};
                border:1px solid ${has ? acc.color + '66' : 'rgba(100,100,100,0.2)'};border-radius:4px;font-size:10px;
                color:${has ? acc.color : '#555'};`;
            accEl.textContent = has ? `${acc.name}` : '???';
            if (has) accEl.title = acc.desc;
            accGrid.appendChild(accEl);
        }
        panel.appendChild(accGrid);

        // Special weapon info
        if (this._specialWeapon) {
            const swDiv = document.createElement('div');
            swDiv.style.cssText = `margin-top:8px;padding:6px;background:rgba(255,255,255,0.05);border-radius:6px;border:1px solid ${this._specialWeapon.color}44;`;
            swDiv.innerHTML = `<span style="color:${this._specialWeapon.color};font-weight:bold;font-size:11px">⚔ ${this._specialWeapon.name}</span>
                <span style="color:#aaa;font-size:9px;margin-left:6px">${this._specialWeapon.desc} · ${this._specialWeapon.dmg} dmg · [Q]</span>`;
            panel.appendChild(swDiv);
        }

        // Close hint
        const hint = document.createElement('div');
        hint.style.cssText = 'font-size:9px;color:rgba(255,255,255,0.3);text-align:center;margin-top:8px;';
        hint.textContent = '[B] закрыть';
        panel.appendChild(hint);
    }

    render() {
        window._gameRef = this;
        const cx = this.cx, w = this.cv.width, h = this.cv.height, time = performance.now();
        cx.save();
        const ptx = Math.floor(this.p.x / TS), pty = Math.floor(this.p.y / TS);
        const biome = this.w.biomeMap[Math.max(0, Math.min(WW - 1, ptx))];
        const sky = this.skyColBlended(this.smoothBiomeWeights || { [biome]: 1 });
        const gr = cx.createLinearGradient(0, 0, 0, h);
        gr.addColorStop(0, sky.z); gr.addColorStop(0.35, sky.m);
        gr.addColorStop(0.7, sky.h); gr.addColorStop(1, sky.g);
        cx.fillStyle = gr; cx.fillRect(0, 0, w, h);

        // === HORIZON GLOW (sunrise/sunset) ===
        const hgT = this.dayTime;
        if ((hgT > 0.10 && hgT < 0.22) || (hgT > 0.78 && hgT < 0.90)) {
            const isSunrise = hgT < 0.5;
            const hgProg = isSunrise ? (hgT - 0.10) / 0.12 : (0.90 - hgT) / 0.12;
            const hgIntensity = Math.sin(hgProg * Math.PI) * 0.35;
            if (hgIntensity > 0.01) {
                const hgX = isSunrise ? w * 0.25 : w * 0.75;
                const hgGrad = cx.createRadialGradient(hgX, h * 0.85, 0, hgX, h * 0.85, w * 0.6);
                const warmR = isSunrise ? 255 : 255, warmG = isSunrise ? 160 : 120, warmB = isSunrise ? 80 : 50;
                hgGrad.addColorStop(0, `rgba(${warmR},${warmG},${warmB},${hgIntensity})`);
                hgGrad.addColorStop(0.5, `rgba(${warmR},${warmG - 40},${warmB},${hgIntensity * 0.3})`);
                hgGrad.addColorStop(1, 'rgba(0,0,0,0)');
                cx.fillStyle = hgGrad; cx.fillRect(0, 0, w, h);
            }
        }

        // === STARS (visible at night) — multi-size, multi-color, constellations ===
        const starVis = (this.dayTime > 0.82 || this.dayTime < 0.12);
        const cloudFade = 1 - (this._weather ? this._weather.cloudDensity * 0.8 : 0);
        if (starVis && cloudFade > 0.05) {
            const sa = this.dayTime > 0.82 ? Math.min(1, (this.dayTime - 0.82) * 5) : Math.min(1, (0.12 - this.dayTime) * 5);
            const sr = new SRng(42);
            const starCols = ['255,255,255', '200,220,255', '255,240,200', '180,200,255', '255,200,180'];
            for (let i = 0; i < 100; i++) {
                const sx2 = sr.n() * w, sy = sr.n() * h * 0.55;
                const sClass = sr.n(); // size class
                const ss = sClass < 0.6 ? 0.8 + sr.n() * 0.7 : (sClass < 0.9 ? 1.5 + sr.n() * 1 : 2.5 + sr.n() * 1);
                const colIdx = Math.floor(sr.n() * starCols.length);
                const twinkleSpd = 800 + sr.n() * 1200;
                const twinkle = Math.sin(time / twinkleSpd + i * 2.3) * 0.35 + 0.65;
                const a = Math.min(1, sa) * twinkle * cloudFade;
                if (a < 0.02) continue;
                cx.globalAlpha = a;
                cx.fillStyle = `rgba(${starCols[colIdx]},${a})`;
                cx.fillRect(sx2, sy, ss, ss);
                // Bright stars get a subtle glow
                if (ss > 2) {
                    cx.globalAlpha = a * 0.15;
                    cx.fillRect(sx2 - 1, sy - 1, ss + 2, ss + 2);
                }
            }
            // Constellations — connect groups of stars with faint lines
            cx.globalAlpha = sa * 0.08 * cloudFade;
            cx.strokeStyle = 'rgba(180,200,255,0.5)'; cx.lineWidth = 0.5;
            const csr = new SRng(77);
            for (let c = 0; c < 4; c++) {
                const baseX = csr.n() * w * 0.8 + w * 0.1, baseY = csr.n() * h * 0.35 + 10;
                const pts = [];
                for (let p = 0; p < 4 + Math.floor(csr.n() * 4); p++) {
                    pts.push({ x: baseX + (csr.n() - 0.5) * 80, y: baseY + (csr.n() - 0.5) * 50 });
                }
                cx.beginPath();
                for (let p = 0; p < pts.length - 1; p++) {
                    cx.moveTo(pts[p].x, pts[p].y); cx.lineTo(pts[p + 1].x, pts[p + 1].y);
                }
                cx.stroke();
            }
            cx.globalAlpha = 1;
        }

        // === SHOOTING STAR ===
        if (this._shootingStar && starVis) {
            const ss = this._shootingStar;
            const prog = ss.progress / ss.duration;
            const alpha = prog < 0.2 ? prog / 0.2 : (prog > 0.7 ? (1 - prog) / 0.3 : 1);
            const sx = ss.sx * w + prog * ss.len * w * Math.cos(ss.angle) * 6;
            const sy = ss.sy * h + prog * ss.len * h * Math.sin(ss.angle) * 4;
            const tailLen = 30 + prog * 20;
            cx.globalAlpha = alpha * 0.9 * cloudFade;
            cx.strokeStyle = ss.color; cx.lineWidth = 1.5;
            cx.beginPath();
            cx.moveTo(sx, sy);
            cx.lineTo(sx - Math.cos(ss.angle) * tailLen, sy - Math.sin(ss.angle) * tailLen);
            cx.stroke();
            // Bright head
            cx.fillStyle = '#fff'; cx.globalAlpha = alpha * cloudFade;
            cx.fillRect(sx - 1, sy - 1, 2, 2);
            cx.globalAlpha = 1;
        }

        // === SUN (enhanced with weather interaction) ===
        if (this.dayTime > 0.12 && this.dayTime < 0.88) {
            const sunProgress = (this.dayTime - 0.12) / (0.88 - 0.12);
            const sunAngle = sunProgress * Math.PI;
            const sunX = w * 0.1 + Math.cos(Math.PI - sunAngle) * w * 0.4 + w * 0.4;
            const sunYarc = h * 0.45 - Math.sin(sunAngle) * h * 0.38;
            const sunAlpha = sunProgress < 0.08 ? sunProgress / 0.08 : (sunProgress > 0.92 ? (1 - sunProgress) / 0.08 : 1);
            const cd = this._weather ? this._weather.cloudDensity : 0;
            const sunDim = Math.max(0.15, 1 - cd * 0.7); // clouds dim the sun
            cx.globalAlpha = sunAlpha * sunDim;
            // Warm color shift near horizon
            const horizonFactor = 1 - Math.sin(sunAngle); // 1 at horizon, 0 at zenith
            const sunR = 255, sunG = Math.floor(224 - horizonFactor * 80), sunB = Math.floor(64 - horizonFactor * 50);
            // Outer glow
            cx.fillStyle = `rgba(${sunR},${sunG},${sunB},0.06)`; cx.beginPath(); cx.arc(sunX, sunYarc, 70, 0, Math.PI * 2); cx.fill();
            cx.fillStyle = `rgba(${sunR},${sunG},${sunB},0.1)`; cx.beginPath(); cx.arc(sunX, sunYarc, 48, 0, Math.PI * 2); cx.fill();
            cx.fillStyle = `rgba(${sunR},${sunG},${sunB},0.18)`; cx.beginPath(); cx.arc(sunX, sunYarc, 32, 0, Math.PI * 2); cx.fill();
            // Core
            cx.fillStyle = `rgb(${sunR},${sunG + 20},${Math.min(255, sunB + 40)})`; cx.beginPath(); cx.arc(sunX, sunYarc, 20, 0, Math.PI * 2); cx.fill();
            cx.fillStyle = '#fff8dd'; cx.beginPath(); cx.arc(sunX - 4, sunYarc - 4, 8, 0, Math.PI * 2); cx.fill();
            // Sun rays — more dramatic, rotate slowly
            if (sunProgress > 0.15 && sunProgress < 0.85 && cd < 0.5) {
                const rayAlpha = (0.5 - Math.abs(sunProgress - 0.5)) * 0.12 * (1 - cd);
                cx.globalAlpha = rayAlpha * sunDim;
                for (let r = 0; r < 12; r++) {
                    const ra = (r / 12) * Math.PI * 2 + time * 0.00015;
                    const innerR = 26 + Math.sin(ra * 3 + time * 0.001) * 4;
                    const outerR = 44 + Math.sin(ra * 2 + time * 0.0008) * 8;
                    cx.strokeStyle = `rgba(${sunR},${sunG},${Math.min(255, sunB + 60)},0.6)`; cx.lineWidth = 1.2;
                    cx.beginPath();
                    cx.moveTo(sunX + Math.cos(ra) * innerR, sunYarc + Math.sin(ra) * innerR);
                    cx.lineTo(sunX + Math.cos(ra) * outerR, sunYarc + Math.sin(ra) * outerR);
                    cx.stroke();
                }
            }
            // God rays near sunrise/sunset — long rays downward
            if ((sunProgress < 0.15 || sunProgress > 0.85) && cd < 0.4) {
                const grAlpha = (sunProgress < 0.15 ? sunProgress / 0.15 : (1 - sunProgress) / 0.15) * 0.08;
                cx.globalAlpha = grAlpha * sunDim;
                for (let r = 0; r < 5; r++) {
                    const spread = (r - 2) * 0.12;
                    cx.strokeStyle = `rgba(${sunR},${sunG},${sunB},0.3)`;
                    cx.lineWidth = 2 + r % 2;
                    cx.beginPath();
                    cx.moveTo(sunX, sunYarc + 24);
                    cx.lineTo(sunX + spread * 200, sunYarc + 200);
                    cx.stroke();
                }
            }
            cx.globalAlpha = 1;
        }

        // === MOON (with phases) ===
        const nightLen = 0.30;
        let moonProgress = -1;
        if (this.dayTime >= 0.85) moonProgress = (this.dayTime - 0.85) / nightLen;
        else if (this.dayTime <= 0.15) moonProgress = (0.15 + this.dayTime) / nightLen;
        if (moonProgress >= 0 && moonProgress <= 1) {
            const moonAngle = moonProgress * Math.PI;
            const moonX = w * 0.1 + Math.cos(Math.PI - moonAngle) * w * 0.4 + w * 0.4;
            const moonYarc = h * 0.40 - Math.sin(moonAngle) * h * 0.34;
            const moonAlpha = moonProgress < 0.1 ? moonProgress / 0.1 : (moonProgress > 0.9 ? (1 - moonProgress) / 0.1 : 1);
            const moonPhase = this._dayCount % 8; // 0-7: new, wax cres, first q, wax gib, full, wan gib, last q, wan cres
            const cd = this._weather ? this._weather.cloudDensity : 0;
            const moonDim = Math.max(0.1, 1 - cd * 0.6);
            cx.globalAlpha = moonAlpha * moonDim;
            // Moon glow — brighter for full moon
            const moonBright = 1 - Math.abs(moonPhase - 4) / 4;
            const glowR = 18 + moonBright * 30;
            cx.fillStyle = `rgba(180,190,220,${0.04 + moonBright * 0.06})`; cx.beginPath(); cx.arc(moonX, moonYarc, 50, 0, Math.PI * 2); cx.fill();
            cx.fillStyle = `rgba(200,200,230,${0.06 + moonBright * 0.04})`; cx.beginPath(); cx.arc(moonX, moonYarc, 35, 0, Math.PI * 2); cx.fill();
            // Moon body
            cx.fillStyle = '#e0e0f0'; cx.beginPath(); cx.arc(moonX, moonYarc, glowR, 0, Math.PI * 2); cx.fill();
            // Moon phase shadow — draw a dark circle offset to create crescent
            if (moonPhase !== 4) { // not full moon
                const shadowDir = moonPhase < 4 ? 1 : -1; // which side is shadowed
                const shadowAmount = Math.abs(moonPhase - 4) / 4; // 1=new, 0=full
                const shadowOff = shadowDir * glowR * (1 - shadowAmount) * 0.8;
                cx.fillStyle = '#0a0a1e';
                cx.beginPath(); cx.arc(moonX + shadowOff, moonYarc, glowR * (0.5 + shadowAmount * 0.55), 0, Math.PI * 2); cx.fill();
            }
            // Craters (visible on lit portion)
            if (moonPhase > 1 && moonPhase < 7) {
                const craterAlpha = moonAlpha * moonDim * (1 - Math.abs(moonPhase - 4) / 4 * 0.6);
                cx.globalAlpha = craterAlpha;
                cx.fillStyle = '#c0c0d8'; cx.beginPath(); cx.arc(moonX + 4, moonYarc - 3, 4, 0, Math.PI * 2); cx.fill();
                cx.fillStyle = '#c8c8e0'; cx.beginPath(); cx.arc(moonX - 3, moonYarc + 3, 2.5, 0, Math.PI * 2); cx.fill();
                cx.fillStyle = '#b8b8d0'; cx.beginPath(); cx.arc(moonX - 6, moonYarc - 4, 1.8, 0, Math.PI * 2); cx.fill();
            }
            cx.globalAlpha = 1;
        }

        // === CLOUDS ===
        const cloudD = this._weather ? this._weather.cloudDensity : 0;
        const windSpd = this._weather ? this._weather.windSpeed : 0;
        if (cloudD > 0.02) {
            const cNoise = new SRng(137);
            const numClouds = Math.floor(8 + cloudD * 20); // 8-28 clouds
            for (let i = 0; i < numClouds; i++) {
                const baseX = cNoise.n() * w * 1.4 - w * 0.2;
                const baseY = cNoise.n() * h * 0.45 + h * 0.02;
                const cw = 40 + cNoise.n() * 80;
                const ch = 12 + cNoise.n() * 18;
                const speed = 0.008 + cNoise.n() * 0.015 + windSpd * 0.02;
                const drift = (time * speed + i * 200) % (w + cw * 2) - cw;
                const cx2 = (baseX + drift) % (w + cw) - cw / 2;
                const alpha = cloudD * (0.15 + cNoise.n() * 0.2);
                if (alpha < 0.01) continue;
                // Storm clouds are darker
                const isStorm = this._weather && (this._weather.state === 'storm' || this._weather.state === 'rain');
                const gray = isStorm ? (140 + cNoise.n() * 40) : (210 + cNoise.n() * 40);
                cx.globalAlpha = Math.min(0.6, alpha);
                cx.fillStyle = `rgb(${gray | 0},${gray | 0},${(gray + 10) | 0})`;
                // Cloud shape — 3-5 overlapping rounded rects
                const blobs = 3 + Math.floor(cNoise.n() * 3);
                for (let b = 0; b < blobs; b++) {
                    const bx = cx2 + (b - blobs / 2) * cw * 0.25;
                    const by = baseY + (cNoise.n() - 0.5) * ch * 0.4;
                    const bw = cw * (0.3 + cNoise.n() * 0.3);
                    const bh = ch * (0.5 + cNoise.n() * 0.4);
                    cx.fillRect(bx, by, bw, bh);
                }
            }
            cx.globalAlpha = 1;
        }

        // === RAIN (screen-space, above everything pre-world) ===
        if (this._weather && this._weather.intensity > 0.05) {
            const wi = this._weather;
            // Spawn new drops
            const spawnRate = Math.floor(wi.intensity * 4);
            for (let s = 0; s < spawnRate; s++) {
                if (wi.rainDrops.length < wi.maxRainDrops) {
                    wi.rainDrops.push({
                        x: Math.random() * w, y: -5 - Math.random() * 20,
                        l: 300 + Math.random() * 200, speed: 0.7 + Math.random() * 0.5
                    });
                }
            }
            // Render rain
            const rainAlpha = Math.min(0.5, wi.intensity * 0.4);
            cx.globalAlpha = rainAlpha;
            const windOff = wi.windSpeed * 3;
            for (const d of wi.rainDrops) {
                const rx = d.x, ry = d.y;
                if (ry < 0 || ry > h || rx < -10 || rx > w + 10) continue;
                const len = 6 + wi.intensity * 6;
                cx.strokeStyle = 'rgba(160,190,220,0.6)';
                cx.lineWidth = 0.8;
                cx.beginPath();
                cx.moveTo(rx, ry);
                cx.lineTo(rx + windOff, ry + len);
                cx.stroke();
            }
            // Rain mist at bottom of screen during heavy rain
            if (wi.intensity > 0.5) {
                const mistAlpha = (wi.intensity - 0.5) * 0.2;
                const mistGrad = cx.createLinearGradient(0, h * 0.7, 0, h);
                mistGrad.addColorStop(0, 'rgba(0,0,0,0)');
                mistGrad.addColorStop(1, `rgba(160,180,200,${mistAlpha})`);
                cx.globalAlpha = 1;
                cx.fillStyle = mistGrad; cx.fillRect(0, h * 0.7, w, h * 0.3);
            }
            cx.globalAlpha = 1;
        }

        // === EVENT SKY EFFECTS ===
        if (this._currentEvent && EVENTS[this._currentEvent]) {
            const evt = EVENTS[this._currentEvent];
            // Sky tint overlay
            if (evt.skyTint) {
                const st = evt.skyTint;
                const pulse = Math.sin(time / 800) * 0.03;
                cx.fillStyle = `rgba(${st.r},${st.g},${st.b},${Math.min(0.5, st.a + pulse)})`;
                cx.fillRect(0, 0, w, h);
            }
            // Blood Moon — tint the moon red
            if (evt.moonTint && moonProgress >= 0 && moonProgress <= 1) {
                const moonAngle2 = moonProgress * Math.PI;
                const moonX2 = w * 0.1 + Math.cos(Math.PI - moonAngle2) * w * 0.4 + w * 0.4;
                const moonYarc2 = h * 0.40 - Math.sin(moonAngle2) * h * 0.34;
                cx.fillStyle = evt.moonTint;
                cx.globalAlpha = 0.4 + Math.sin(time / 500) * 0.1;
                cx.beginPath(); cx.arc(moonX2, moonYarc2, 24, 0, Math.PI * 2); cx.fill();
                // Blood drip effect
                cx.fillStyle = `rgba(200,20,20,${0.15 + Math.sin(time / 300) * 0.05})`;
                cx.beginPath(); cx.arc(moonX2, moonYarc2, 35, 0, Math.PI * 2); cx.fill();
                cx.globalAlpha = 1;
            }
            // Solar Eclipse — darken sun with corona
            if (evt.eclipseSun && this.dayTime > 0.12 && this.dayTime < 0.88) {
                const sunProgress2 = (this.dayTime - 0.12) / (0.88 - 0.12);
                const sunAngle2 = sunProgress2 * Math.PI;
                const sunX2 = w * 0.1 + Math.cos(Math.PI - sunAngle2) * w * 0.4 + w * 0.4;
                const sunYarc2 = h * 0.45 - Math.sin(sunAngle2) * h * 0.38;
                // Black disk over sun
                cx.fillStyle = '#000'; cx.beginPath(); cx.arc(sunX2, sunYarc2, 23, 0, Math.PI * 2); cx.fill();
                // Corona
                const coronaPulse = Math.sin(time / 200) * 0.1 + 0.3;
                cx.fillStyle = `rgba(255,200,100,${coronaPulse})`; cx.beginPath(); cx.arc(sunX2, sunYarc2, 30, 0, Math.PI * 2); cx.fill();
                cx.fillStyle = `rgba(255,150,50,${coronaPulse * 0.5})`; cx.beginPath(); cx.arc(sunX2, sunYarc2, 40, 0, Math.PI * 2); cx.fill();
            }
            // Event wave/progress indicator
            if (evt.waves) {
                cx.fillStyle = evt.color;
                cx.font = 'bold 12px monospace';
                cx.textAlign = 'center';
                const waveText = `Wave ${this._eventWave + 1}/${evt.waves.length} — ${Math.ceil(this._eventTimer / 1000)}s`;
                cx.fillText(waveText, w / 2, 30);
                // Wave progress bar
                const barW = 150, barH = 4;
                const barX = w / 2 - barW / 2, barY = 35;
                cx.fillStyle = 'rgba(0,0,0,0.5)'; cx.fillRect(barX, barY, barW, barH);
                const totalDur = evt.waves.reduce((s, wv) => s + wv.duration, 0);
                let elapsed = 0;
                for (let wi = 0; wi < this._eventWave; wi++) elapsed += evt.waves[wi].duration;
                elapsed += this._eventWaveTimer;
                const prog = Math.min(1, elapsed / totalDur);
                cx.fillStyle = evt.color; cx.fillRect(barX, barY, barW * prog, barH);
                // Kill counter
                cx.font = '10px monospace';
                cx.fillStyle = '#fff';
                cx.fillText(`${this._eventMobsKilled} kills`, w / 2, barY + 14);
            }
        }

        cx.scale(this.zoom, this.zoom);
        const vw = w / this.zoom, vh = h / this.zoom;
        cx.translate(-this.cam.x + vw / 2 + this.cam.shakeX, -this.cam.y + vh / 2 + this.cam.shakeY);

        const surfY = this.w.heightMap[Math.max(0, Math.min(WW - 1, ptx))];
        this.parallax.render(cx, this.cam.x, this.cam.y, vw, vh, biome, surfY, time, this.smoothBiomeWeights);

        const stx = Math.floor((this.cam.x - vw / 2) / TS) - 1, etx = Math.ceil((this.cam.x + vw / 2) / TS) + 1;
        const sty = Math.floor((this.cam.y - vh / 2) / TS) - 1, ety = Math.ceil((this.cam.y + vh / 2) / TS) + 1;

        for (let x = stx; x <= etx; x++)for (let y = sty; y <= ety; y++) {
            const bg = this.w.gb(x, y), fg = this.w.gt(x, y);
            if (bg > 0 && !SOLID.has(fg) && !LIQUID.has(fg)) {
                const l = this.w.gl(x, y); if (l > 0.01) {
                    const c = TC[bg]; if (c) {
                        cx.globalAlpha = l * 0.35; cx.fillStyle = c; cx.fillRect(x * TS, y * TS, TS, TS);
                    }
                }
            }
        }
        cx.globalAlpha = 1;

        const LIGHT_TILES = new Set([T.TORCH, T.DTORCH, T.LAVA, T.CRYSTAL, T.GLOWMUSH, T.LANTERN, T.EMBER, T.ACRYSTAL, T.RCRYSTAL, T.BIOVINE, T.CRYSTSHARD, T.HELLSTONE, T.PULSEVINE, T.SPORECAP, T.TIMECRACK, T.PRISMCORE, T.HEARTBLOCK, T.CHARGEDCRYSTAL, T.NERVEVEIN, T.EYEBLOCK, T.WORLDHEART, T.WORLDSAP, T.WORLDCORE, T.ROOTCRYSTAL, T.CRACKSHARD, T.WORLDCRACK]);
        for (let x = stx; x <= etx; x++)for (let y = sty; y <= ety; y++) {
            const t = this.w.gt(x, y); if (t === T.AIR) continue;
            const l = this.w.gl(x, y); if (l < 0.01 && !LIGHT_TILES.has(t)) continue;
            this.tex.render(cx, t, x * TS, y * TS, x, y, time);
        }

        // === BIOME DECORATIONS (rendered AFTER tiles, BEFORE lighting) ===
        if (Math.abs(this.cam.x - this._lastDecoCamX) > 8 * TS || this._cachedDecorations.length === 0) {
            this._cachedDecorations = this.biomeDecorator.generateForRegion(this.w, stx, etx, biome);
            this._lastDecoCamX = this.cam.x;
        }
        this.biomeDecorator.render(cx, this._cachedDecorations, this.cam.x, this.cam.y, time);

        const tilesW = etx - stx + 2, tilesH = ety - sty + 2;
        this.lightCv.width = tilesW; this.lightCv.height = tilesH;
        const lx = this.lightCx; lx.clearRect(0, 0, tilesW, tilesH);
        const dm = this.dayMod();
        for (let x = 0; x < tilesW; x++)for (let y = 0; y < tilesH; y++) {
            const wx = stx + x, wy = sty + y;
            // Separate sky light and block light
            const sl = this.w.gsl(wx, wy);
            const bl = this.w.gbl(wx, wy);
            // Sky zone (y < 35): always fully lit so sky islands aren't shadowed
            if (wy < 35) { lx.fillStyle = 'rgba(0,0,0,0)'; lx.fillRect(x, y, 1, 1); continue; }
            // Glow accessory — add light around player
            let glowBonus = 0;
            if (this._hasAccessory('glow')) {
                const gdx = wx - Math.floor(this.p.x / TS), gdy = wy - Math.floor(this.p.y / TS);
                const gDist = Math.sqrt(gdx * gdx + gdy * gdy);
                if (gDist < 8) glowBonus = Math.max(0, 1 - gDist / 8) * 0.7;
            }
            // Sky light affected by day/night and atmosphere. Block light cuts through.
            let fl = Math.max(sl * dm * this.curAtm.dm, bl, glowBonus);
            fl = Math.max(0, Math.min(1, fl));
            lx.fillStyle = `rgba(0,0,0,${1 - fl})`; lx.fillRect(x, y, 1, 1);
        }
        cx.imageSmoothingEnabled = true; cx.imageSmoothingQuality = 'low';
        cx.drawImage(this.lightCv, 0, 0, tilesW, tilesH, stx * TS, sty * TS, tilesW * TS, tilesH * TS);
        cx.imageSmoothingEnabled = false;

        if (this.curAtm.fs > 0.02) {
            const fogG = cx.createRadialGradient(this.cam.x, this.cam.y, vw * 0.15, this.cam.x, this.cam.y, vw * 0.55);
            fogG.addColorStop(0, this.curAtm.fog + '0)'); fogG.addColorStop(1, this.curAtm.fog + Math.min(0.4, this.curAtm.fs) + ')');
            cx.fillStyle = fogG; cx.fillRect(this.cam.x - vw / 2, this.cam.y - vh / 2, vw, vh);
        }

        // === LIGHTNING BOLTS ===
        for (const bolt of this._lightningBolts) {
            const bAlpha = Math.min(1, bolt.timer / 200);
            cx.strokeStyle = `rgba(180,220,255,${bAlpha})`;
            cx.lineWidth = 2;
            cx.beginPath();
            cx.moveTo(bolt.x, bolt.y);
            // Jagged path from sky to ground
            let bx2 = bolt.x, by2 = bolt.y;
            const segs = 8;
            const segH = (bolt.by - bolt.y) / segs;
            for (let s = 0; s < segs; s++) {
                bx2 += (Math.random() - 0.5) * 15;
                by2 += segH;
                cx.lineTo(bx2, by2);
            }
            cx.stroke();
            // Glow around bolt
            cx.strokeStyle = `rgba(140,200,255,${bAlpha * 0.3})`;
            cx.lineWidth = 6;
            cx.stroke();
        }
        // Lightning flash overlay
        if (this._lightningFlash > 0) {
            const lfAlpha = Math.min(0.4, this._lightningFlash / 200 * 0.4);
            cx.fillStyle = `rgba(200,220,255,${lfAlpha})`;
            cx.fillRect(this.cam.x - vw / 2, this.cam.y - vh / 2, vw, vh);
        }
        if (this.p.mtgt) {
            const mt = this.p.mtgt, tile = this.w.gt(mt.x, mt.y);
            const pSpd = PICKAXE_TIERS[this.equipPick].speed;
            let mtime = Math.floor(400 / pSpd);
            if ([T.DBRICK, T.OBSIDIAN, T.ANCBRICK, T.MAGBRICK, T.CRYPTBRICK].includes(tile)) mtime = Math.floor(1800 / pSpd);
            if ([T.DEEPSLATE, T.GRANITE, T.MARBLE, T.BASALT, T.HELLSTONE].includes(tile)) mtime = Math.floor(1000 / pSpd);
            if ([T.DIAMOND, T.TITANIUM].includes(tile)) mtime = Math.floor(1200 / pSpd);
            const prog = Math.min(1, this.p.mt / mtime); const px2 = mt.x * TS, py2 = mt.y * TS;
            cx.strokeStyle = 'rgba(0,0,0,0.6)'; cx.lineWidth = 1;
            if (prog > 0.2) { cx.beginPath(); cx.moveTo(px2 + 8, py2 + 2); cx.lineTo(px2 + 5, py2 + 8); cx.stroke(); }
            if (prog > 0.5) { cx.beginPath(); cx.moveTo(px2 + 10, py2 + 6); cx.lineTo(px2 + 12, py2 + 12); cx.stroke(); }
            if (prog > 0.75) {
                cx.beginPath(); cx.moveTo(px2 + 3, py2 + 10); cx.lineTo(px2 + 8, py2 + 14); cx.stroke();
                cx.beginPath(); cx.moveTo(px2 + 12, py2 + 2); cx.lineTo(px2 + 14, py2 + 7); cx.stroke();
            }
            cx.strokeStyle = 'rgba(255,255,0,0.4)'; cx.lineWidth = 0.5; cx.strokeRect(px2, py2, TS, TS);
            cx.fillStyle = 'rgba(255,255,0,0.12)'; cx.fillRect(px2, py2 + TS * (1 - prog), TS, TS * prog);
        }
        const mp = this.mouseWorld(); cx.strokeStyle = 'rgba(255,255,255,0.25)'; cx.lineWidth = 0.5; cx.strokeRect(mp.x * TS, mp.y * TS, TS, TS);

        for (const mob of this.mobs) mob.render(cx, time);
        if (this.boss && this.boss.alive) this.boss.render(cx, time);
        this.renderPlayer(cx, time);

        // === LIQUID OVERLAY — render liquid tiles ON TOP of entities ===
        // This creates the visual effect of entities being submerged
        for (let x = stx; x <= etx; x++) for (let y = sty; y <= ety; y++) {
            const lt = this.w.gt(x, y);
            if (!LIQUID.has(lt)) continue;
            const ll = this.w.gl(x, y);
            if (ll < 0.01) continue;
            const lLvl = this.w.gll(x, y);
            if (lLvl <= 0.01) continue;
            const S = TS;
            const lpx = x * S, lpy = y * S;
            const fillH = Math.max(1, Math.round(S * Math.min(1.0, lLvl)));
            const topY = lpy + S - fillH;
            const aboveLiq = LIQUID.has(this.w.gt(x, y - 1));
            const drawTopY = aboveLiq ? lpy : topY;
            const drawH = lpy + S - drawTopY;
            if (drawH <= 0) continue;

            if (lt === T.WATER) {
                // Semi-transparent water overlay on top of entities
                const wo = Math.sin(time / 400 + x * 0.7 + y * 0.3) * 1.2;
                cx.fillStyle = 'rgba(15,50,170,0.30)';
                cx.fillRect(lpx, drawTopY, S, drawH);
                // Subtle depth gradient
                if (drawH > 4) {
                    cx.fillStyle = 'rgba(8,30,120,0.12)';
                    cx.fillRect(lpx, drawTopY + drawH * 0.5, S, drawH * 0.5);
                }
                // Surface shimmer line (only if surface tile)
                if (!aboveLiq && lLvl > 0.05) {
                    cx.fillStyle = 'rgba(80,170,255,0.25)';
                    cx.fillRect(lpx, drawTopY + wo, S, 2);
                    cx.fillStyle = 'rgba(150,220,255,0.18)';
                    cx.fillRect(lpx + 2, drawTopY + wo - 0.5, S - 4, 1);
                }
            } else if (lt === T.LAVA) {
                const lo = Math.sin(time / 200 + x * 0.8 + y * 0.4);
                cx.fillStyle = `rgba(200,${45 + lo * 25 | 0},0,0.35)`;
                cx.fillRect(lpx, drawTopY, S, drawH);
                // Embers glow
                const lo2 = Math.sin(time / 150 + x * 1.2 + y * 0.6);
                cx.fillStyle = `rgba(255,${70 + lo2 * 40 | 0},0,0.18)`;
                cx.fillRect(lpx + 1, drawTopY + 1, S - 2, Math.max(0, drawH - 2));
            } else if (lt === T.HONEY) {
                cx.fillStyle = 'rgba(200,150,20,0.28)';
                cx.fillRect(lpx, drawTopY, S, drawH);
                cx.fillStyle = 'rgba(240,180,40,0.12)';
                cx.fillRect(lpx, drawTopY, S, Math.min(3, drawH));
            }
        }

        // Render dropped items
        for (const item of this.droppedItems) item.render(cx, time);
        this.gore.render(cx);
        this.dmgNums.render(cx);
        // Render XP orbs (world space, before particles)
        for (const orb of this.xpOrbs) orb.render(cx, time);
        for (const pt of this.parts) {
            const a = Math.max(0, pt.l / pt.ml);
            cx.globalAlpha = a;
            cx.fillStyle = pt.c;
            const sz = (pt.sz || 3) * (0.5 + a * 0.5);
            cx.fillRect(pt.x - sz / 2, pt.y - sz / 2, sz, sz);
            // Glow for bright particles
            if (pt.c === '#fff' || pt.c === '#ffdd00' || pt.c === '#ffaa00') {
                cx.globalAlpha = a * 0.3;
                cx.fillRect(pt.x - sz, pt.y - sz, sz * 2, sz * 2);
            }
        }
        cx.globalAlpha = 1;
        this.ambient.render(cx);

        // === RENDER PROJECTILES ===
        for (const proj of this._projectiles) {
            const lifeRatio = proj.life / proj.maxLife;
            cx.save();
            cx.globalAlpha = Math.min(1, lifeRatio * 2);
            // Glow
            cx.fillStyle = proj.color;
            const gs = proj.size * 2.5;
            cx.globalAlpha = lifeRatio * 0.25;
            cx.fillRect(proj.x - gs / 2, proj.y - gs / 2, gs, gs);
            // Core
            cx.globalAlpha = Math.min(1, lifeRatio * 2);
            cx.fillStyle = '#fff';
            cx.fillRect(proj.x - proj.size / 3, proj.y - proj.size / 3, proj.size * 0.66, proj.size * 0.66);
            cx.fillStyle = proj.color;
            cx.fillRect(proj.x - proj.size / 2, proj.y - proj.size / 2, proj.size, proj.size);
            // Type-specific visuals
            if (proj.type === 'fire') {
                cx.fillStyle = '#ffcc00';
                cx.fillRect(proj.x - 1, proj.y - 1, 2, 2);
            }
            if (proj.type === 'lightning') {
                cx.strokeStyle = `rgba(255,255,200,${lifeRatio * 0.5})`;
                cx.lineWidth = 1;
                cx.beginPath();
                cx.moveTo(proj.x - proj.vx * 2, proj.y + (Math.random() - 0.5) * 4);
                cx.lineTo(proj.x, proj.y);
                cx.stroke();
            }
            cx.restore();
        }

        // === RENDER FROZEN MOB EFFECT ===
        for (const mob of this.mobs) {
            if (mob._frozen && mob._frozen > 0) {
                cx.fillStyle = 'rgba(100,200,255,0.2)';
                cx.fillRect(mob.x - 1, mob.y - 1, mob.w + 2, mob.h + 2);
                cx.strokeStyle = 'rgba(150,220,255,0.4)';
                cx.lineWidth = 1;
                cx.strokeRect(mob.x - 1, mob.y - 1, mob.w + 2, mob.h + 2);
            }
            if (mob._burning && mob._burning > 0 && Math.random() > 0.7) {
                this.addPart(mob.x + Math.random() * mob.w, mob.y, (Math.random() - 0.5), -1 - Math.random(), '#ff6600', 10, 1.5);
            }
            if (mob._poisoned && mob._poisoned > 0 && Math.random() > 0.85) {
                this.addPart(mob.x + Math.random() * mob.w, mob.y + mob.h, (Math.random() - 0.5), -0.5, '#44cc44', 10, 1);
            }
        }

        // === UNDERWATER OVERLAY EFFECT ===
        if (this.p.liquidDepth > 0.1 && !this._dead) {
            const liqD = this.p.liquidDepth;
            const liqT = this.p.liquidType;
            const t2 = time;
            // Determine overlay color based on liquid type
            let liqR, liqG, liqB, liqA;
            if (liqT === T.LAVA) { liqR = 180; liqG = 40; liqB = 0; liqA = liqD * 0.35; }
            else if (liqT === T.HONEY) { liqR = 200; liqG = 150; liqB = 30; liqA = liqD * 0.25; }
            else { liqR = 15; liqG = 50; liqB = 160; liqA = liqD * 0.22; }

            // Pulsing opacity for immersion feel
            const pulse = Math.sin(t2 / 600) * 0.03;
            liqA = Math.min(0.45, liqA + pulse);

            // Main tint overlay
            cx.fillStyle = `rgba(${liqR},${liqG},${liqB},${liqA})`;
            cx.fillRect(this.cam.x - vw / 2, this.cam.y - vh / 2, vw, vh);

            // Animated caustic light patterns (water only)
            if (liqT === T.WATER && liqD > 0.3) {
                cx.save();
                const caustAlpha = Math.min(0.12, liqD * 0.08);
                cx.globalAlpha = caustAlpha;
                cx.fillStyle = 'rgba(100,200,255,1)';
                // Animated light shafts
                for (let i = 0; i < 6; i++) {
                    const shaftX = this.cam.x - vw / 2 + ((t2 / 800 + i * vw / 5.7) % (vw * 1.3)) - vw * 0.15;
                    const shaftW = 15 + Math.sin(t2 / 500 + i * 1.3) * 8;
                    const shaftA = Math.sin(t2 / 700 + i * 2.1) * 0.5 + 0.5;
                    cx.globalAlpha = caustAlpha * shaftA;
                    cx.fillRect(shaftX, this.cam.y - vh / 2, shaftW, vh);
                }
                // Wavy caustic circles
                cx.globalAlpha = caustAlpha * 0.6;
                for (let i = 0; i < 8; i++) {
                    const cx2 = this.cam.x + Math.sin(t2 / 900 + i * 1.7) * vw * 0.4;
                    const cy2 = this.cam.y + Math.cos(t2 / 1100 + i * 2.3) * vh * 0.35;
                    const cr = 20 + Math.sin(t2 / 400 + i) * 10;
                    cx.beginPath();
                    cx.arc(cx2, cy2, cr, 0, Math.PI * 2);
                    cx.fill();
                }
                cx.restore();
            }

            // Lava heat shimmer
            if (liqT === T.LAVA && liqD > 0.3) {
                const heatAlpha = Math.min(0.15, liqD * 0.1);
                const heatFlicker = Math.sin(t2 / 100) * 0.04;
                cx.fillStyle = `rgba(255,100,0,${heatAlpha + heatFlicker})`;
                cx.fillRect(this.cam.x - vw / 2, this.cam.y - vh / 2, vw, vh);
            }

            // Vignette darkening at edges when deep underwater
            if (liqD > 0.5) {
                const vigA = Math.min(0.4, (liqD - 0.5) * 0.6);
                const vigGrad = cx.createRadialGradient(this.cam.x, this.cam.y, vw * 0.2, this.cam.x, this.cam.y, vw * 0.55);
                vigGrad.addColorStop(0, 'rgba(0,0,0,0)');
                vigGrad.addColorStop(1, `rgba(${liqT === T.LAVA ? '80,10,0' : (liqT === T.HONEY ? '60,40,0' : '0,10,40')},${vigA})`);
                cx.fillStyle = vigGrad;
                cx.fillRect(this.cam.x - vw / 2, this.cam.y - vh / 2, vw, vh);
            }
        }

        // === BIOME-SPECIFIC OVERLAY EFFECTS ===
        const camL = this.cam.x - vw / 2, camT = this.cam.y - vh / 2;

        // Desert/Red Desert/Badlands — sandstorm haze overlay
        if ([5, 6, 17].includes(biome)) {
            const sandA = Math.sin(time / 1200) * 0.03 + 0.06;
            cx.fillStyle = `rgba(200,180,130,${sandA})`;
            cx.fillRect(camL, camT, vw, vh);
            // Wind streaks
            const streakA = Math.sin(time / 800) * 0.02 + 0.03;
            cx.fillStyle = `rgba(220,200,150,${streakA})`;
            for (let i = 0; i < 4; i++) {
                const sy2 = camT + (i / 4) * vh + Math.sin(time / 400 + i * 1.7) * 15;
                cx.fillRect(camL, sy2, vw, 2);
            }
        }

        // Tundra/Frozen Shore — blizzard overlay (white fog at edges)
        if ([0, 18].includes(biome)) {
            const blizA = Math.sin(time / 1000) * 0.03 + 0.05;
            cx.fillStyle = `rgba(220,230,245,${blizA})`;
            cx.fillRect(camL, camT, vw, vh);
            // Horizontal snow streaks
            const sA = Math.sin(time / 600) * 0.02 + 0.03;
            cx.fillStyle = `rgba(240,245,255,${sA})`;
            for (let i = 0; i < 3; i++) {
                const sy2 = camT + (i / 3) * vh + Math.sin(time / 500 + i * 2.1) * 20;
                cx.fillRect(camL, sy2, vw, 1);
            }
        }

        // Storm Peaks — storm overlay with flash + driving rain lines
        if (biome === 29) {
            // Dark storm clouds overlay
            const stormA = Math.sin(time / 900) * 0.04 + 0.08;
            cx.fillStyle = `rgba(30,35,50,${stormA})`;
            cx.fillRect(camL, camT, vw, vh);
            // Driving rain lines (cheap diagonal streaks)
            const rainA = Math.sin(time / 700) * 0.03 + 0.06;
            cx.strokeStyle = `rgba(160,190,220,${rainA})`;
            cx.lineWidth = 1;
            for (let i = 0; i < 8; i++) {
                const rx = camL + ((time * 0.15 + i * vw / 7.3) % (vw * 1.2)) - vw * 0.1;
                cx.beginPath();
                cx.moveTo(rx, camT);
                cx.lineTo(rx + 15, camT + vh);
                cx.stroke();
            }
        }

        // Tidal Flats — mist and tide shimmer
        if (biome === 28) {
            const tideMist = Math.sin(time / 1500) * 0.03 + 0.04;
            cx.fillStyle = `rgba(160,200,220,${tideMist})`;
            cx.fillRect(camL, camT, vw, vh);
            // Subtle water reflections from below
            const reflA = Math.sin(time / 800) * 0.02 + 0.02;
            cx.fillStyle = `rgba(120,180,210,${reflA})`;
            cx.fillRect(camL, camT + vh * 0.7, vw, vh * 0.3);
        }

        // Living Caves - pulsing vein overlay (optimized — fewer strokes)
        if (biome === 30 && pty > 35) {
            const veinPulse = Math.sin(time / 800) * 0.03 + 0.05;
            cx.fillStyle = `rgba(200,60,80,${veinPulse})`;
            for (let i = 0; i < 4; i++) {
                const vx = camL + (i / 4) * vw;
                for (let y = 0; y < vh; y += 25) {
                    const ox = Math.sin(time / 300 + y * 0.1) * 8;
                    cx.fillRect(vx + ox, camT + y, 2, 20);
                }
            }
            // Heartbeat pulse — subtle screen flash
            const heartbeat = Math.sin(time / 600);
            if (heartbeat > 0.9) {
                cx.fillStyle = `rgba(150,30,40,${(heartbeat - 0.9) * 0.3})`;
                cx.fillRect(camL, camT, vw, vh);
            }
        }

        // Prism Shores - rainbow shimmer overlay
        if (biome === 27) {
            const shimmer = Math.sin(time / 500) * 0.05 + 0.05;
            const offset = time / 1000;
            const sr = 128 + Math.sin(offset) * 60 | 0;
            const sg = 128 + Math.sin(offset + 2) * 60 | 0;
            const sb = 128 + Math.sin(offset + 4) * 60 | 0;
            cx.fillStyle = `rgba(${sr},${sg},${sb},${shimmer})`;
            cx.fillRect(camL, camT, vw, vh);
        }

        // Abyss - darkness with bioluminescent glow spots (mobile-friendly — no radialGradient)
        if (biome === 20 && pty > 40) {
            const abyssAlpha = Math.min(0.4, (pty - 40) * 0.02);
            cx.fillStyle = `rgba(5,5,15,${abyssAlpha})`;
            cx.fillRect(camL, camT, vw, vh);
            // Bio glow spots — layered rectangles instead of radialGradient
            for (let i = 0; i < 3; i++) {
                const bx = this.cam.x + Math.sin(time / 1000 + i * 2.1) * vw * 0.3;
                const by = this.cam.y + Math.cos(time / 800 + i * 1.7) * vh * 0.3;
                const glow = Math.sin(time / 400 + i) * 0.15 + 0.2;
                // Layered glow — 3 rectangles from large/dim to small/bright
                cx.fillStyle = `rgba(80,200,220,${glow * 0.05})`;
                cx.fillRect(bx - 30, by - 30, 60, 60);
                cx.fillStyle = `rgba(80,200,220,${glow * 0.12})`;
                cx.fillRect(bx - 18, by - 18, 36, 36);
                cx.fillStyle = `rgba(100,220,240,${glow * 0.2})`;
                cx.fillRect(bx - 8, by - 8, 16, 16);
            }
        }

        // Ember Wastes — heat shimmer
        if (biome === 21) {
            const heatA = Math.sin(time / 700) * 0.03 + 0.05;
            cx.fillStyle = `rgba(200,100,30,${heatA})`;
            cx.fillRect(camL, camT, vw, vh);
        }

        // Corruption — dark tendrils overlay
        if (biome === 10) {
            const corrA = Math.sin(time / 900) * 0.03 + 0.04;
            cx.fillStyle = `rgba(40,10,60,${corrA})`;
            cx.fillRect(camL, camT, vw, vh);
        }

        // Fungal Abyss - spore haze with pulsing
        if (biome === 25 && pty > 35) {
            const sporePulse = Math.sin(time / 600) * 0.06 + 0.1;
            cx.fillStyle = `rgba(150,50,180,${sporePulse})`;
            cx.fillRect(camL, camT, vw, vh);
            // Spore motes (cheap small rectangles)
            const moteA = Math.sin(time / 400) * 0.04 + 0.05;
            cx.fillStyle = `rgba(200,120,240,${moteA})`;
            for (let i = 0; i < 5; i++) {
                const mx = this.cam.x + Math.sin(time / 800 + i * 1.3) * vw * 0.35;
                const my = this.cam.y + Math.cos(time / 600 + i * 1.9) * vh * 0.3;
                cx.fillRect(mx - 4, my - 4, 8, 8);
            }
        }

        // Dark Forest — eerie darkness
        if (biome === 15 && pty <= 35) {
            const darkA = Math.sin(time / 1200) * 0.02 + 0.06;
            cx.fillStyle = `rgba(5,15,5,${darkA})`;
            cx.fillRect(camL, camT, vw, vh);
        }

        // Blood Plains — blood mist
        if (biome === 23) {
            const bloodA = Math.sin(time / 800) * 0.03 + 0.05;
            cx.fillStyle = `rgba(100,15,15,${bloodA})`;
            cx.fillRect(camL, camT, vw, vh);
        }

        // Swamp — murky fog
        if (biome === 8) {
            const swampA = Math.sin(time / 1100) * 0.03 + 0.05;
            cx.fillStyle = `rgba(50,60,30,${swampA})`;
            cx.fillRect(camL, camT, vw, vh);
        }

        // === WORLD MUTATION VISUAL EFFECTS ===
        if (this._mutation.phase >= 1) {
            const mut = this._mutation;
            // Crack glow effect
            for (const crack of mut.cracks) {
                const cdist = Math.abs(crack.x * TS - this.cam.x);
                if (cdist > vw) continue;
                const glowI = crack.intensity * (0.5 + mut.glowPulse * 0.5);
                const cgx = crack.x * TS + TS / 2;
                const cgy = crack.y * TS;
                const crGlow = cx.createRadialGradient(cgx, cgy, 0, cgx, cgy, TS * 3);
                crGlow.addColorStop(0, `rgba(150,50,220,${glowI * 0.2})`);
                crGlow.addColorStop(1, 'rgba(150,50,220,0)');
                cx.fillStyle = crGlow;
                cx.fillRect(cgx - TS * 3, cgy - TS * 3, TS * 6, TS * 6);
            }

            // Tree aura (phases 3+)
            if (mut.phase >= 3 && mut.treeX) {
                const tx = mut.treeX * TS + TS / 2;
                const ty = (mut.treeY - mut.treeGrowStep / 2) * TS;
                const tdist = Math.abs(tx - this.cam.x);
                if (tdist < vw * 2) {
                    const auraR = Math.min(mut.treeGrowStep * 3, 200);
                    const auraPulse = Math.sin(time / 1200) * 0.03 + 0.06;
                    const treeGlow = cx.createRadialGradient(tx, ty, auraR * 0.2, tx, ty, auraR);
                    treeGlow.addColorStop(0, `rgba(100,255,150,${auraPulse})`);
                    treeGlow.addColorStop(0.5, `rgba(80,200,100,${auraPulse * 0.5})`);
                    treeGlow.addColorStop(1, 'rgba(80,200,100,0)');
                    cx.fillStyle = treeGlow;
                    cx.fillRect(tx - auraR, ty - auraR, auraR * 2, auraR * 2);
                }
            }

            // World Heart glow (phase 5)
            if (mut.phase >= 5 && mut.treeX) {
                const hx = mut.treeX * TS + TS / 2;
                const hy = (mut.treeY - Math.floor(mut.maxTreeHeight * 0.4)) * TS;
                const heartPulse = Math.sin(time / 400) * 0.15 + 0.2;
                const heartGlow = cx.createRadialGradient(hx, hy, 0, hx, hy, TS * 5);
                heartGlow.addColorStop(0, `rgba(255,50,120,${heartPulse})`);
                heartGlow.addColorStop(0.5, `rgba(255,100,150,${heartPulse * 0.4})`);
                heartGlow.addColorStop(1, 'rgba(255,100,150,0)');
                cx.fillStyle = heartGlow;
                cx.fillRect(hx - TS * 5, hy - TS * 5, TS * 10, TS * 10);
            }
        }

        cx.restore();

        const vigBiomes = { 10: 0.35, 13: 0.3, 15: 0.4, 8: 0.2, 12: 0.25, 25: 0.45, 20: 0.5, 23: 0.35, 29: 0.3, 30: 0.55 };
        let vigStr = 0;
        const bwVig = this.smoothBiomeWeights || { [biome]: 1 };
        for (const b in bwVig) { if (vigBiomes[b]) vigStr += vigBiomes[b] * bwVig[b]; }
        if (vigStr > 0.01) {
            const vg = cx.createRadialGradient(w / 2, h / 2, w * 0.3, w / 2, h / 2, w * 0.7);
            vg.addColorStop(0, 'rgba(0,0,0,0)'); vg.addColorStop(1, `rgba(0,0,0,${vigStr})`);
            cx.fillStyle = vg; cx.fillRect(0, 0, w, h);
        }
        if (this.dayTime < 0.25 || this.dayTime > 0.75) {
            const na = this.dayTime > 0.75 ? Math.min(0.15, (this.dayTime - 0.75) * 0.6) : Math.min(0.15, (0.25 - this.dayTime) * 0.6);
            cx.fillStyle = `rgba(10,10,40,${na})`; cx.fillRect(0, 0, w, h);
        }
        // Screen flash overlay (juice!)
        if (this.screenFlash > 0) {
            cx.fillStyle = this.screenFlashColor;
            cx.globalAlpha = Math.min(1, this.screenFlash / 60) * 0.6;
            cx.fillRect(0, 0, w, h);
            cx.globalAlpha = 1;
        }

        // === ACCESSORY HUD — show equipped accessories ===
        if (this._accessories.length > 0) {
            cx.save();
            const accHudX = 10, accHudY = h - 140;
            for (let i = 0; i < this._accessories.length; i++) {
                const acc = ACCESSORIES.find(a => a.id === this._accessories[i]);
                if (!acc) continue;
                const ay = accHudY + i * 18;
                cx.globalAlpha = 0.8;
                cx.fillStyle = 'rgba(0,0,0,0.4)';
                cx.fillRect(accHudX, ay, 100, 16);
                cx.fillStyle = acc.color;
                cx.font = 'bold 9px monospace';
                cx.fillText(acc.name, accHudX + 3, ay + 12);
            }
            cx.restore();
        }

        // === SPECIAL WEAPON HUD ===
        if (this._specialWeapon) {
            cx.save();
            const swX = w - 160, swY = h - 45;
            cx.globalAlpha = 0.85;
            cx.fillStyle = 'rgba(0,0,0,0.5)';
            cx.fillRect(swX, swY, 150, 22);
            cx.fillStyle = this._specialWeapon.color;
            cx.font = 'bold 10px monospace';
            cx.fillText(`[Q] ${this._specialWeapon.name}`, swX + 4, swY + 15);
            // Cooldown indicator
            if (this._specialAttackCd > 0) {
                cx.fillStyle = 'rgba(0,0,0,0.4)';
                const cdPct = this._specialAttackCd / 400;
                cx.fillRect(swX, swY + 20, 150 * cdPct, 2);
            }
            cx.restore();
        }

        // === ACCESSORY NOTIFICATION POPUP ===
        if (this._accessoryNotifyTimer > 0) {
            cx.save();
            cx.globalAlpha = Math.min(1, this._accessoryNotifyTimer / 800);
            cx.font = 'bold 12px monospace';
            const ntw2 = cx.measureText(this._accessoryNotify).width + 24;
            cx.fillStyle = 'rgba(0,40,20,0.8)';
            cx.fillRect(w / 2 - ntw2 / 2, 100, ntw2, 28);
            cx.fillStyle = '#44ffaa';
            cx.textAlign = 'center';
            cx.fillText(this._accessoryNotify, w / 2, 119);
            cx.textAlign = 'left';
            cx.restore();
        }

        // === SPECIAL WEAPON NOTIFICATION POPUP ===
        if (this._specialWeaponNotifyTimer > 0) {
            cx.save();
            cx.globalAlpha = Math.min(1, this._specialWeaponNotifyTimer / 800);
            cx.font = 'bold 13px monospace';
            const swn = this._specialWeaponNotify;
            const swnW = cx.measureText(swn).width + 24;
            cx.fillStyle = 'rgba(40,10,10,0.85)';
            cx.fillRect(w / 2 - swnW / 2, 130, swnW, 28);
            cx.fillStyle = this._specialWeapon ? this._specialWeapon.color : '#ff8844';
            cx.textAlign = 'center';
            cx.fillText(swn, w / 2, 149);
            cx.textAlign = 'left';
            cx.restore();
        }

        // === BESTIARY NOTIFICATION ===
        if (this._bestiaryNotifyTimer > 0) {
            cx.save();
            cx.globalAlpha = Math.min(1, this._bestiaryNotifyTimer / 500);
            cx.font = 'bold 10px monospace';
            const bnText = this._bestiaryNotify;
            const bnW = cx.measureText(bnText).width + 20;
            cx.fillStyle = 'rgba(30,20,10,0.75)';
            cx.fillRect(w / 2 - bnW / 2, 55, bnW, 22);
            cx.fillStyle = '#ffd700';
            cx.textAlign = 'center';
            cx.fillText(bnText, w / 2, 70);
            cx.textAlign = 'left';
            cx.restore();
        }

        // === QUEST HUD ===
        if (this._activeQuest) {
            const q = this._activeQuest;
            const qx = 10, qy = h - 90;
            cx.save();
            cx.globalAlpha = 0.85;
            cx.fillStyle = 'rgba(0,0,0,0.6)';
            cx.fillRect(qx, qy, 180, 40);
            cx.fillStyle = '#ffd700';
            cx.font = 'bold 10px monospace';
            cx.fillText(q.icon + ' Квест', qx + 6, qy + 13);
            cx.fillStyle = '#ddd';
            cx.font = '9px monospace';
            cx.fillText(q.desc, qx + 6, qy + 25);
            // Progress bar
            const prog = Math.min(1, (q.progress || 0) / (q.target || 1));
            cx.fillStyle = 'rgba(255,255,255,0.15)';
            cx.fillRect(qx + 6, qy + 30, 168, 5);
            cx.fillStyle = prog >= 1 ? '#44ff88' : '#ffd700';
            cx.fillRect(qx + 6, qy + 30, 168 * prog, 5);
            cx.fillStyle = '#aaa';
            cx.font = '8px monospace';
            cx.fillText(`${q.progress || 0}/${q.target}`, qx + 140, qy + 25);
            cx.restore();
        }
        // Quest notification popup
        if (this._questNotifyTimer > 0) {
            cx.save();
            cx.globalAlpha = Math.min(1, this._questNotifyTimer / 500);
            cx.font = 'bold 11px monospace';
            const ntw = cx.measureText(this._questNotify).width + 20;
            cx.fillStyle = 'rgba(0,0,0,0.7)';
            cx.fillRect(w / 2 - ntw / 2, 70, ntw, 24);
            cx.fillStyle = '#ffd700';
            cx.textAlign = 'center';
            cx.fillText(this._questNotify, w / 2, 87);
            cx.textAlign = 'left';
            cx.restore();
        }

        // === UNDERWATER HUD WAVE EFFECT (screen-space) ===
        if (this.p.liquidDepth > 0.3 && !this._dead) {
            const waveT = time;
            const waveIntensity = Math.min(1, this.p.liquidDepth);
            // Wavy top edge effect to simulate water surface
            const waveH = 3 + waveIntensity * 4;
            cx.save();
            cx.globalAlpha = 0.15 * waveIntensity;
            cx.fillStyle = this.p.liquidType === T.LAVA ? '#ff4400' : (this.p.liquidType === T.HONEY ? '#ffaa00' : '#2266cc');
            for (let sx = 0; sx < w; sx += 4) {
                const wy = Math.sin(waveT / 300 + sx * 0.03) * waveH + Math.sin(waveT / 200 + sx * 0.05) * waveH * 0.5;
                cx.fillRect(sx, wy, 4, 2);
            }
            cx.restore();

            // Oxygen bubble indicator when fully submerged
            if (this.p.liquidDepth > 0.8 && this.p.liquidType === T.WATER) {
                cx.save();
                const bubX = w / 2, bubY = h - 60;
                const maxBubbles = 10;
                const breathLeft = Math.max(0, 1 - (this.p.swimTimer || 0) / 30000); // 30s breath
                const bubblesShown = Math.ceil(breathLeft * maxBubbles);
                for (let i = 0; i < maxBubbles; i++) {
                    const bx = bubX - (maxBubbles * 7) + i * 14;
                    const wobble = Math.sin(waveT / 400 + i * 0.8) * 1.5;
                    if (i < bubblesShown) {
                        cx.fillStyle = 'rgba(100,180,255,0.6)';
                        cx.strokeStyle = 'rgba(200,230,255,0.8)';
                        cx.lineWidth = 1;
                        cx.beginPath(); cx.arc(bx, bubY + wobble, 5, 0, Math.PI * 2); cx.fill(); cx.stroke();
                        // Highlight
                        cx.fillStyle = 'rgba(255,255,255,0.4)';
                        cx.beginPath(); cx.arc(bx - 1.5, bubY + wobble - 1.5, 1.5, 0, Math.PI * 2); cx.fill();
                    } else {
                        cx.fillStyle = 'rgba(60,80,120,0.2)';
                        cx.beginPath(); cx.arc(bx, bubY + wobble, 5, 0, Math.PI * 2); cx.fill();
                    }
                }
                cx.restore();
            }
        }

        // === WORLD TREE COMPASS ===
        if (this._mutation.phase >= 1) {
            const mut = this._mutation;
            const treeWorldX = mut.treeX * TS + TS / 2;
            const treeWorldY = (mut.treeY || Math.floor(this.w.heightMap[mut.treeX])) * TS;
            const px2 = this.p.x + PW / 2;
            const py2 = this.p.y + PH / 2;
            const ddx = treeWorldX - px2;
            const ddy = treeWorldY - py2;
            const distToTree = Math.sqrt(ddx * ddx + ddy * ddy);
            const distTiles = Math.floor(distToTree / TS);

            // Compass position — top center of screen
            const compX = w / 2;
            const compY = 50;
            const time2 = performance.now();

            // Arrow pointing toward tree
            const angle = Math.atan2(ddy, ddx);
            const pulse = Math.sin(time2 / 400) * 0.15 + 0.85;

            // Background circle
            cx.save();
            cx.globalAlpha = 0.7 * pulse;
            cx.fillStyle = mut.phase >= 3 ? 'rgba(34,170,68,0.3)' : 'rgba(100,50,180,0.3)';
            cx.beginPath();
            cx.arc(compX, compY, 22, 0, Math.PI * 2);
            cx.fill();

            // Arrow
            cx.translate(compX, compY);
            cx.rotate(angle);
            cx.globalAlpha = pulse;

            // Arrow shape
            const arrowCol = mut.phase >= 3 ? '#44ff88' : '#bb66ff';
            cx.fillStyle = arrowCol;
            cx.beginPath();
            cx.moveTo(16, 0);
            cx.lineTo(-6, -7);
            cx.lineTo(-2, 0);
            cx.lineTo(-6, 7);
            cx.closePath();
            cx.fill();

            // Arrow glow
            cx.globalAlpha = pulse * 0.3;
            cx.fillStyle = arrowCol;
            cx.beginPath();
            cx.moveTo(18, 0);
            cx.lineTo(-8, -9);
            cx.lineTo(-3, 0);
            cx.lineTo(-8, 9);
            cx.closePath();
            cx.fill();

            cx.restore();

            // Distance text
            cx.globalAlpha = 0.9;
            cx.font = '11px monospace';
            cx.textAlign = 'center';

            // Label
            const phaseLabels = ['', '⚡ Трещины', '🌱 Корни', '🌳 Древо', '🌳 Мировое Древо', '✨ Древо Расцвета'];
            const label = phaseLabels[mut.phase] || '';
            cx.fillStyle = mut.phase >= 3 ? '#44ff88' : '#cc88ff';
            cx.fillText(label, compX, compY + 32);

            // Distance
            if (distTiles > 5) {
                cx.fillStyle = '#fff';
                cx.font = '10px monospace';
                cx.fillText(`${distTiles} тайлов`, compX, compY + 44);
            } else if (mut.phase >= 3) {
                cx.fillStyle = '#88ffaa';
                cx.font = '10px monospace';
                cx.fillText('Вы здесь!', compX, compY + 44);
            }

            cx.globalAlpha = 1;
            cx.textAlign = 'left';
        }
    }

    renderPlayer(cx, time) {
        if (this._dead) return; // Don't render player while dead
        // Glow accessory visual
        if (this._hasAccessory('glow')) {
            const pulse = Math.sin(time / 500) * 0.08 + 0.15;
            cx.fillStyle = `rgba(136,255,221,${pulse})`;
            cx.beginPath();
            cx.arc(this.p.x + PW / 2, this.p.y + PH / 2, 20 + Math.sin(time / 300) * 3, 0, Math.PI * 2);
            cx.fill();
        }
        // Dash trail visual
        if (this._dashTimer > 0) {
            cx.globalAlpha = this._dashTimer / 150 * 0.3;
            cx.fillStyle = '#ff8844';
            cx.fillRect(this.p.x - this._dashDir * 6, this.p.y + 2, PW, PH - 4);
            cx.globalAlpha = 1;
        }
        const p = this.p, flash = p.dmgFlash > 0 && Math.floor(p.dmgFlash / 50) % 2 === 0;
        const pcx2 = p.x + PW / 2, pcy = p.y + PH;
        const breathOff = Math.sin(p.breathTimer / 800) * 0.5;
        const isBlinking = p.blinkTimer % 4000 < 120;
        cx.save(); cx.translate(pcx2, pcy); cx.scale(p.squashX, p.squashY); cx.translate(-pcx2, -pcy);
        const px = p.x, py = p.y + breathOff;
        cx.fillStyle = 'rgba(0,0,0,0.15)'; cx.beginPath(); cx.ellipse(px + PW / 2, p.y + PH, 7, 2, 0, 0, Math.PI * 2); cx.fill();
        const lo = Math.abs(p.vx) > 0.5 ? Math.sin(p.at * 8) * 2.5 : 0;
        // Equipment colors
        const armorCol = ARMOR_TIERS[this.equipArmor].color;
        const weapCol = WEAPON_TIERS[this.equipWeapon].color;
        // Legs — armor tint
        cx.fillStyle = flash ? '#ff2222' : (armorCol || '#1a3388'); cx.fillRect(px + 2, py + 16, 4, 8 + lo);
        cx.fillStyle = flash ? '#ff3333' : (armorCol || '#2244aa'); cx.fillRect(px + 6, py + 16, 4, 8 - lo);
        cx.fillStyle = flash ? '#cc1111' : '#1a1a44'; cx.fillRect(px + 1, py + 23 + lo, 5, 1); cx.fillRect(px + 6, py + 23 - lo, 5, 1);
        // Body — armor tint
        cx.fillStyle = flash ? '#ff4444' : (armorCol || '#3366ff'); cx.fillRect(px + 1, py + 6, 10, 11);
        cx.fillStyle = flash ? '#ff6666' : (armorCol ? this.lc(armorCol, '#fff', 0.2) : '#4488ff'); cx.fillRect(px + 2, py + 7, 8, 4);
        // Belt
        cx.fillStyle = '#664422'; cx.fillRect(px + 1, py + 15, 10, 2); cx.fillStyle = '#ccaa44'; cx.fillRect(px + 5, py + 15, 2, 2);
        // Arms
        const armSwing = Math.abs(p.vx) > 0.5 ? Math.sin(p.at * 8) * 3 : 0;
        cx.fillStyle = flash ? '#ffaaaa' : '#ffcc88'; cx.fillRect(px - 1, py + 7 + armSwing, 3, 7); cx.fillRect(px + 10, py + 7 - armSwing, 3, 7);
        // Armor shoulders (if equipped)
        if (armorCol && this.equipArmor >= 2) {
            cx.fillStyle = armorCol; cx.fillRect(px - 1, py + 6, 3, 3); cx.fillRect(px + 10, py + 6, 3, 3);
        }
        // Head
        cx.fillStyle = flash ? '#ffaaaa' : '#ffcc88'; cx.fillRect(px + 2, py, 8, 7);
        // Helmet (if armor >= 3)
        if (armorCol && this.equipArmor >= 3) {
            cx.fillStyle = armorCol; cx.fillRect(px + 1, py - 1, 10, 3);
            cx.fillStyle = this.lc(armorCol, '#000', 0.3); cx.fillRect(px + 1, py + 1, 2, 1);
            if (!p.right) cx.fillRect(px + 9, py + 1, 2, 1);
        } else {
            cx.fillStyle = '#6a3010'; cx.fillRect(px + 1, py - 1, 10, 3); cx.fillRect(px + 1, py + 1, 2, 2);
            if (!p.right) cx.fillRect(px + 9, py + 1, 2, 2);
        }
        // Eyes
        if (isBlinking) { cx.fillStyle = '#ffbb88'; if (p.right) cx.fillRect(px + 7, py + 3, 2, 1); else cx.fillRect(px + 3, py + 3, 2, 1); }
        else {
            cx.fillStyle = '#fff'; if (p.right) { cx.fillRect(px + 6, py + 2, 3, 3); cx.fillStyle = '#2244aa'; cx.fillRect(px + 8, py + 3, 1, 1); }
            else { cx.fillRect(px + 3, py + 2, 3, 3); cx.fillStyle = '#2244aa'; cx.fillRect(px + 3, py + 3, 1, 1); }
        }
        cx.fillStyle = '#cc9977'; if (p.right) cx.fillRect(px + 7, py + 5, 2, 1); else cx.fillRect(px + 3, py + 5, 2, 1);
        if (p.swingTimer > 0) {
            const handX = p.right ? (px + 11) : (px + 1);
            const handY = py + 10;
            cx.save(); cx.translate(handX, handY);
            const rot = p.right ? p.swingAngle : -p.swingAngle;
            cx.rotate(rot);
            // Draw weapon — color from tier or special weapon
            const activeWeapCol = this._specialWeapon ? this._specialWeapon.color : weapCol;
            cx.fillStyle = '#8B5E3C'; cx.fillRect(-1, -4, 2, 14);
            cx.fillStyle = activeWeapCol; cx.fillRect(-4, -13, 3, 9); cx.fillRect(1, -13, 3, 9);
            cx.fillStyle = this.lc(activeWeapCol, '#fff', 0.3); cx.fillRect(-5, -13, 2, 3); cx.fillRect(4, -13, 2, 3);
            // Gleam on blade
            cx.fillStyle = 'rgba(255,255,255,0.5)'; cx.fillRect(-3, -12, 1, 4);
            // Special weapon aura
            if (this._specialWeapon) {
                const auraPulse = Math.sin(time / 200) * 0.2 + 0.3;
                cx.globalAlpha = auraPulse;
                cx.fillStyle = this._specialWeapon.projColor;
                cx.fillRect(-6, -14, 12, 12);
                cx.globalAlpha = 1;
            }
            cx.restore();
            // Swing trail — colored by weapon tier
            const prog = 1 - p.swingTimer / 220;
            const trailAlpha = (1 - prog) * 0.35;
            const trailR = 16;
            cx.save(); cx.translate(handX, handY);
            for (let tl = 0; tl < 3; tl++) {
                const tlOff = tl * 0.06;
                cx.globalAlpha = trailAlpha * (1 - tl * 0.3);
                cx.strokeStyle = tl === 0 ? weapCol : (tl === 1 ? 'rgba(200,220,255,0.3)' : 'rgba(150,180,255,0.15)');
                cx.lineWidth = 2 + tl * 2;
                const startAng = p.right ? -Math.PI / 2.5 : Math.PI / 2.5;
                const endAng = p.right ? (startAng + prog * Math.PI * 0.75) : (startAng - prog * Math.PI * 0.75);
                cx.beginPath(); cx.arc(0, 0, trailR + tl * 3, startAng - tlOff, endAng + tlOff, !p.right); cx.stroke();
            }
            cx.globalAlpha = 1;
            cx.restore();
        }
        else if (this.mouse.l) { cx.fillStyle = '#8B5E3C'; if (p.right) cx.fillRect(px + 11, py + 8, 2, 8); else cx.fillRect(px - 1, py + 8, 2, 8); }
        cx.restore();
    }

    skyCol(biome) {
        const t = this.dayTime;
        // 4-stop gradient: zenith (top), mid (upper-mid), horizon (lower), glow (very bottom)
        // 8 phases: deep night, pre-dawn, dawn, morning, day, evening, dusk, night
        let zen, mid, hor, glo;
        const cd = this._weather ? this._weather.cloudDensity : 0;
        if (t < 0.08) {
            // Deep night
            zen = '#05051e'; mid = '#0a0a2e'; hor = '#121240'; glo = '#1a1a4a';
        } else if (t < 0.13) {
            // Pre-dawn — subtle blue-purple at horizon
            const f = (t - 0.08) / 0.05;
            zen = this.lc('#05051e', '#0a0a2e', f);
            mid = this.lc('#0a0a2e', '#1a1540', f);
            hor = this.lc('#121240', '#3a2255', f);
            glo = this.lc('#1a1a4a', '#6a3060', f);
        } else if (t < 0.18) {
            // Dawn — warm colors at horizon, dark blue above
            const f = (t - 0.13) / 0.05;
            zen = this.lc('#0a0a2e', '#1a3060', f);
            mid = this.lc('#1a1540', '#cc6633', f);
            hor = this.lc('#3a2255', '#ff8844', f);
            glo = this.lc('#6a3060', '#ffcc77', f);
        } else if (t < 0.24) {
            // Morning — transitioning to day
            const f = (t - 0.18) / 0.06;
            zen = this.lc('#1a3060', '#3377bb', f);
            mid = this.lc('#cc6633', '#5599cc', f);
            hor = this.lc('#ff8844', '#7bb8dd', f);
            glo = this.lc('#ffcc77', '#99ccee', f);
        } else if (t < 0.76) {
            // Full day
            zen = '#3377bb'; mid = '#5599cc'; hor = '#7bb8dd'; glo = '#99ccee';
        } else if (t < 0.82) {
            // Evening — warm tones creeping in
            const f = (t - 0.76) / 0.06;
            zen = this.lc('#3377bb', '#225588', f);
            mid = this.lc('#5599cc', '#cc6633', f);
            hor = this.lc('#7bb8dd', '#ff6633', f);
            glo = this.lc('#99ccee', '#ffaa55', f);
        } else if (t < 0.88) {
            // Dusk — deep oranges fading to purple
            const f = (t - 0.82) / 0.06;
            zen = this.lc('#225588', '#0a0a2e', f);
            mid = this.lc('#cc6633', '#2a1540', f);
            hor = this.lc('#ff6633', '#4a2255', f);
            glo = this.lc('#ffaa55', '#3a1a4a', f);
        } else {
            // Night
            zen = '#05051e'; mid = '#0a0a2e'; hor = '#121240'; glo = '#1a1a4a';
        }
        // Cloud darkening effect
        if (cd > 0.05) {
            const darkAmt = cd * 0.3;
            zen = this.lc(zen, '#444450', darkAmt);
            mid = this.lc(mid, '#505058', darkAmt);
            hor = this.lc(hor, '#606068', darkAmt);
            glo = this.lc(glo, '#707078', darkAmt);
        }
        // Biome tint
        const atm = BIOME_ATM[biome];
        if (atm && atm.tint && t > 0.15 && t < 0.85) {
            zen = this.lc(zen, atm.tint, 0.2);
            mid = this.lc(mid, atm.tint, 0.15);
            hor = this.lc(hor, atm.tint, 0.1);
        }
        return { z: zen, m: mid, h: hor, g: glo };
    }
    skyColBlended(weights) {
        let zr = 0, zg = 0, zb = 0, mr = 0, mg = 0, mb = 0;
        let hr2 = 0, hg = 0, hb = 0, glr = 0, glg = 0, glb = 0;
        for (const b in weights) {
            const w = weights[b];
            const s = this.skyCol(parseInt(b));
            const zp = this._parseCol(s.z), mp = this._parseCol(s.m);
            const hp = this._parseCol(s.h), gp = this._parseCol(s.g);
            zr += zp[0] * w; zg += zp[1] * w; zb += zp[2] * w;
            mr += mp[0] * w; mg += mp[1] * w; mb += mp[2] * w;
            hr2 += hp[0] * w; hg += hp[1] * w; hb += hp[2] * w;
            glr += gp[0] * w; glg += gp[1] * w; glb += gp[2] * w;
        }
        return {
            z: `rgb(${zr | 0},${zg | 0},${zb | 0})`,
            m: `rgb(${mr | 0},${mg | 0},${mb | 0})`,
            h: `rgb(${hr2 | 0},${hg | 0},${hb | 0})`,
            g: `rgb(${glr | 0},${glg | 0},${glb | 0})`
        };
    }
    _parseCol(c) {
        if (c.startsWith('rgb')) { const m = c.match(/\d+/g); return m ? m.map(Number) : [0, 0, 0]; }
        return [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
    }
    dayMod() {
        const t = this.dayTime;
        let base;
        // Moon phase affects night brightness: 0=new(dark), 4=full(bright)
        const moonPhase = this._dayCount % 8; // 0-7
        const moonBright = 1 - Math.abs(moonPhase - 4) / 4; // 0=new, 1=full
        const nightBase = 0.08 + moonBright * 0.15; // 0.08 (new moon) to 0.23 (full moon)
        if (t > 0.22 && t < 0.78) base = 1;
        else if (t < 0.10 || t > 0.90) base = nightBase;
        else if (t < 0.22) base = nightBase + (t - 0.10) / 0.12 * (1 - nightBase);
        else base = 1 - (t - 0.78) / 0.12 * (1 - nightBase);
        // Cloud cover dims light slightly during day
        const cd = this._weather ? this._weather.cloudDensity : 0;
        if (base > 0.5) base *= (1 - cd * 0.2);
        return base;
    }
    lc(a, b, t) {
        const ph = c => {
            if (c.startsWith('rgb')) { const m = c.match(/\d+/g); return m ? m.map(Number) : [0, 0, 0]; }
            return [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
        };
        const ca = ph(a), cb = ph(b);
        return `rgb(${ca[0] + (cb[0] - ca[0]) * t | 0},${ca[1] + (cb[1] - ca[1]) * t | 0},${ca[2] + (cb[2] - ca[2]) * t | 0})`;
    }

    renderMinimap() {
        const mx = this.mx, mw = this.mc.width, mh = this.mc.height;
        const img = mx.createImageData(mw, mh); const sc = WW / mw;
        for (let x = 0; x < mw; x++)for (let y = 0; y < mh; y++) {
            const wx = Math.floor(x * sc), wy = Math.floor(y * (WH / mh));
            const t = this.w.gt(wx, wy); const i = (y * mw + x) * 4; let r = 0, g = 0, b = 0;
            if (t === T.AIR) { if (wy < this.w.heightMap[wx]) { r = 100; g = 160; b = 220; } else { r = 20; g = 15; b = 10; } }
            else { const c = TC[t]; if (c && c[0] === '#' && c.length === 7) { r = parseInt(c.substr(1, 2), 16); g = parseInt(c.substr(3, 2), 16); b = parseInt(c.substr(5, 2), 16); } else { r = 100; g = 100; b = 100; } }
            img.data[i] = r; img.data[i + 1] = g; img.data[i + 2] = b; img.data[i + 3] = 255;
        }
        mx.putImageData(img, 0, 0);
    }

    updateMinimap() {
        this.renderMinimap(); const mx = this.mx, mw = this.mc.width, mh = this.mc.height;
        const px = (this.p.x / TS / WW) * mw, py = (this.p.y / TS / WH) * mh;
        // Player
        mx.fillStyle = '#ff0'; mx.fillRect(px - 2, py - 2, 4, 4);
        // Mobs
        for (const mob of this.mobs) {
            if (mob.def.isTrader) {
                // Trader icon - gold diamond
                mx.fillStyle = '#ffd700';
                const tmx = (mob.x / TS / WW) * mw, tmy = (mob.y / TS / WH) * mh;
                mx.fillRect(tmx - 2, tmy - 2, 4, 4);
                mx.fillStyle = '#ffee88'; mx.fillRect(tmx - 1, tmy - 1, 2, 2);
            } else if (mob.def.isVillager) {
                // Villager - small green dot
                mx.fillStyle = '#44aa44'; mx.fillRect((mob.x / TS / WW) * mw - 1, (mob.y / TS / WH) * mh - 1, 2, 2);
            } else {
                mx.fillStyle = '#f00'; mx.fillRect((mob.x / TS / WW) * mw - 1, (mob.y / TS / WH) * mh - 1, 2, 2);
            }
        }
        // Villages on minimap
        if (this.w.villageLocations) {
            mx.fillStyle = '#44ff44';
            for (const vcx of this.w.villageLocations) {
                const vmx = (vcx / WW) * mw;
                const vmy = (this.w.heightMap[Math.min(vcx, WW - 1)] / WH) * mh;
                mx.fillRect(vmx - 2, vmy - 3, 4, 2);
                mx.fillStyle = '#228822'; mx.fillRect(vmx - 1, vmy - 2, 2, 1);
                mx.fillStyle = '#44ff44';
            }
        }
        // World Tree indicator on minimap
        if (this._mutation && this._mutation.phase >= 3 && this._mutation.treeX) {
            const treePulse = Math.sin(performance.now() / 500) * 0.3 + 0.7;
            mx.globalAlpha = treePulse;
            mx.fillStyle = '#44ff88';
            const tmx2 = (this._mutation.treeX / WW) * mw;
            const tmy2 = (this._mutation.treeY / WH) * mh;
            mx.fillRect(tmx2 - 3, tmy2 - 6, 6, 8);
            mx.fillStyle = '#22aa44';
            mx.fillRect(tmx2 - 5, tmy2 - 8, 10, 4);
            mx.globalAlpha = 1;
        }
        // Boss indicator
        if (this.boss && this.boss.alive) { const isMl = this.boss instanceof MoonLord; mx.fillStyle = isMl ? '#44ffcc' : '#ff2266'; const bpx = isMl ? this.boss.cx : this.boss.x + this.boss.w / 2; const bpy = isMl ? this.boss.cy : this.boss.y + this.boss.h / 2; mx.fillRect((bpx / TS / WW) * mw - 3, (bpy / TS / WH) * mh - 3, 6, 6); }
        // Event indicator — pulsing border
        if (this._currentEvent && EVENTS[this._currentEvent]) {
            const evtCol = EVENTS[this._currentEvent].color;
            mx.strokeStyle = evtCol;
            mx.lineWidth = 2;
            const pulse = Math.sin(performance.now() / 200) * 0.3 + 0.7;
            mx.globalAlpha = pulse;
            mx.strokeRect(1, 1, mw - 2, mh - 2);
            mx.globalAlpha = 1;
        }
        const vw2 = (this.cv.width / this.zoom / TS / WW) * mw, vh2 = (this.cv.height / this.zoom / TS / WH) * mh;
        mx.strokeStyle = '#fff'; mx.lineWidth = 1; mx.strokeRect(px - vw2 / 2, py - vh2 / 2, vw2, vh2);
    }

    loop() {
        if (this.stopped) return;
        const now = performance.now(), dt = Math.min(now - this.lastT, 50); this.lastT = now;

        if (this.accumulator === undefined) this.accumulator = 0;
        this.accumulator += dt;
        const fixedDt = 1000 / 120; // 120 ticks per second (faster gameplay)

        while (this.accumulator >= fixedDt) {
            this.update(fixedDt);
            this.accumulator -= fixedDt;
        }

        this.render();
        if (Math.floor(now / 500) !== Math.floor((now - dt) / 500)) this.updateMinimap();
        requestAnimationFrame(() => this.loop());
    }
}